import { resolveAnchorLogoByAsset } from '../../utils/logoResolver';
import { resolveBusinessIdentityId } from '../../utils/identityRegistry';

// ═══════════════════════════════════════════════════════════════════════════════
// SECTOR MARKETING DATA — 42 Autonomous Intelligence Sub-Species
// Each is its own business entity under the Anchor umbrella
// Medical • Finance • Legal • Climate • Defence • Energy • Maritime • Space • Agriculture • Education • LEO • Wildlife
// ═══════════════════════════════════════════════════════════════════════════════

export interface SectorPricing {
  name: string;
  price: string;
  period: string;
  features: string[];
  cta: string;
  highlighted?: boolean;
  tierId?: string; // Maps to backend billing tier (e.g. 'STARTER', 'PRO', 'TEAM', 'BUSINESS', 'ENTERPRISE')
}

export interface SectorTestimonial {
  quote: string;
  author: string;
  role: string;
  avatar: string;
}

export interface SectorFeature {
  icon: string;
  title: string;
  description: string;
  worldFirst?: boolean;
}

export interface SectorDataSource {
  name: string;
  description: string;
}

export interface RevenueStream {
  name: string;
  description: string;
  icon: string;
  annualPotential: string;
}

export interface BusinessFunction {
  name: string;
  description: string;
  icon: string;
}

export interface TechnologyPartner {
  name: string;
  description: string;
  logo: string; // emoji or icon
  integration: string; // what the integration does
}

export interface APIProduct {
  name: string;
  description: string;
  endpoint: string;
  pricePerCall: string;
}

export interface SectorMarketingConfig {
  id: string;
  slug?: string; // URL path e.g. /anchor-medical
  brandName: string; // e.g. "Anchor Medical"
  tagline?: string;
  heroSubtitle: string;
  icon: string;
  logoUrl?: string; // sector logo image path
  colorFrom?: string; // gradient start
  colorTo?: string; // gradient end
  accentColor?: string; // e.g. "emerald"
  borderColor?: string;
  textColor?: string;
  bgGradient?: string;
  domain?: string; // e.g. "medical.anchoraiguard.com"
  targetMarket?: string[];
  problemStatement?: string;
  solutionStatement?: string;
  features: SectorFeature[];
  dataSources: SectorDataSource[];
  pricing?: SectorPricing[];
  testimonials?: SectorTestimonial[];
  stats?: { value: string; label: string }[];
  useCases: { title: string; description: string }[];
  // Full Business Structure
  revenueStreams?: RevenueStream[];
  businessFunctions?: BusinessFunction[];
  technologyPartners: TechnologyPartner[];
  apiProducts: APIProduct[];
  companyStructure: {
    entity: string; // e.g. "Anchor Medical Pty Ltd"
    division: string; // e.g. "Biomedical Intelligence Division"
    headcount: string; // e.g. "15-25 FTEs"
    hq: string;
    registrations: string[];
  };
  disclaimer?: string;
  ctaText: string;
  ctaSubtext: string;
  // Extended fields for newer sector entries
  heroTitle?: string;
  primaryColor?: string;
  secondaryColor?: string;
  logo?: string;
  pricingTiers?: { name: string; price: string; period: string; features: string[]; cta: string }[];
}

export const SECTOR_MARKETING_DATA: SectorMarketingConfig[] = [
  // ─── 1. ANCHOR MEDICAL ─────────────────────────────────────────────
  {
    id: 'medical',
    slug: '/anchor-medical',
    brandName: 'Anchor Medical',
    tagline: 'Autonomous Biomedical Intelligence',
    heroSubtitle:
      "The world's first self-evolving biomedical intelligence platform. Reads every paper, monitors every trial, detects every signal — before human teams know it exists.",
    icon: '🏥',
    logoUrl: resolveAnchorLogoByAsset(''),
    colorFrom: 'from-emerald-500',
    colorTo: 'to-teal-500',
    accentColor: 'emerald',
    borderColor: 'border-emerald-500',
    textColor: 'text-emerald-400',
    bgGradient: 'from-emerald-500/10 via-transparent to-emerald-900/5',
    domain: 'medical.anchoraiguard.com',
    targetMarket: [
      'Research Institutions',
      'Pharmaceutical Companies',
      'Health Policy Analysts',
      'Medical Researchers',
      'Biotech Startups',
      'Hospitals & Health Systems',
    ],
    problemStatement:
      'Over 3,000 biomedical papers are published daily. Drug interactions are missed. Adverse events go undetected. Emerging disease patterns are spotted weeks too late.',
    solutionStatement:
      'Anchor Medical reads every paper, monitors every trial, and detects every signal in real time — delivering actionable intelligence before human teams even know the data exists.',
    features: [
      {
        icon: '🧬',
        title: 'Biomedical Research Intelligence',
        description:
          'Continuously scans and synthesises global medical literature, surfacing breakthroughs the moment they appear. Over 35 million PubMed articles indexed.',
        worldFirst: true,
      },
      {
        icon: '💊',
        title: 'Drug Repurposing Discovery',
        description:
          'Identifies existing compounds with untapped therapeutic potential by cross-referencing molecular, clinical, and safety data across millions of records.',
        worldFirst: true,
      },
      {
        icon: '🔬',
        title: 'Clinical Trial Monitoring',
        description:
          'Tracks active trials worldwide, flagging enrollment changes, outcome signals, and competitive shifts in real time.',
      },
      {
        icon: '⚠️',
        title: 'Adverse Event Detection',
        description:
          'Monitors FDA FAERS and global pharmacovigilance databases, detecting safety signals before they become headlines.',
      },
      {
        icon: '🧠',
        title: 'AI Evolution Engine',
        description:
          'Self-evolving AI that continuously improves detection accuracy. Runs autonomously on a 15-second cycle — no human intervention required.',
        worldFirst: true,
      },
      {
        icon: '🌍',
        title: 'Disease Pattern Recognition',
        description:
          'Detects emerging disease trends from clinical, genomic, and epidemiological data weeks before mainstream awareness.',
      },
      {
        icon: '🔗',
        title: 'Cross-Sector Correlations',
        description:
          'Connects medical intelligence with climate, agriculture, and environmental data for hidden pattern discovery.',
        worldFirst: true,
      },
      {
        icon: '📊',
        title: 'Research Trend Visualisation',
        description:
          'Interactive dashboards showing how global research priorities shift, where funding converges, and which areas are underexplored.',
      },
    ],
    dataSources: [
      { name: 'PubMed / MEDLINE', description: '35M+ biomedical articles' },
      { name: 'ClinicalTrials.gov', description: 'Global clinical trial registry' },
      { name: 'FDA FAERS', description: 'Adverse event reporting system' },
      { name: 'WHO Disease Outbreak News', description: 'Global health alerts' },
      { name: 'NCBI Gene / UniProt', description: 'Genomic & protein data' },
      { name: 'DrugBank', description: 'Comprehensive drug database' },
    ],
    pricing: [
      {
        name: 'Researcher',
        price: '$2,990',
        period: '/mo',
        features: [
          '5 users',
          'Literature scanning',
          'Adverse event alerts',
          'Basic dashboards',
          'Email support',
        ],
        cta: 'Request demo',
      },
      {
        name: 'Institution',
        price: '$9,990',
        period: '/mo',
        features: [
          '25 users',
          'Everything in Researcher',
          'Drug repurposing engine',
          'Clinical trial monitoring',
          'API access',
          'Priority support',
        ],
        cta: 'Request demo',
        highlighted: true,
      },
      {
        name: 'Enterprise',
        price: '$29,990',
        period: '/mo',
        features: [
          'Unlimited users',
          'Everything in Institution',
          'Cross-sector correlations',
          'Custom AI models',
          'On-premise deployment',
          'Dedicated account manager',
          '99.9% SLA',
        ],
        cta: 'Contact Sales',
      },
    ],
    testimonials: [],
    stats: [
      { value: '35M+', label: 'Articles Indexed' },
      { value: '3,000+', label: 'Papers Scanned Daily' },
      { value: '15s', label: 'AI Evolution Cycle' },
      { value: '6 weeks', label: 'Earlier Signal Detection' },
    ],
    useCases: [
      {
        title: 'Pharmaceutical R&D',
        description:
          'Accelerate drug discovery by identifying repurposing candidates and monitoring competitor pipelines in real time.',
      },
      {
        title: 'Hospital Safety',
        description:
          'Detect adverse drug interactions across your patient population before they cause harm.',
      },
      {
        title: 'Public Health Surveillance',
        description:
          'Track emerging disease patterns and outbreak signals across global data sources.',
      },
      {
        title: 'Clinical Trial Strategy',
        description:
          'Monitor enrollment patterns, outcome signals, and competitive trials to optimise your clinical strategy.',
      },
    ],
    revenueStreams: [
      {
        name: 'SaaS Subscriptions',
        description:
          'Tiered platform access from individual researchers to enterprise institutions',
        icon: '💳',
        annualPotential: '$10M-$35M',
      },
      {
        name: 'Pharma Intelligence API',
        description:
          'Per-call API access for drug interaction checks, adverse event queries, and literature synthesis',
        icon: '🔌',
        annualPotential: '$5M-$15M',
      },
      {
        name: 'Data Licensing',
        description:
          'Licensed access to curated biomedical intelligence datasets for pharmaceutical R&D departments',
        icon: '📦',
        annualPotential: '$3M-$10M',
      },
      {
        name: 'Clinical Trial Matching Marketplace',
        description:
          'Connecting patients to trials and sponsors to sites — transaction-based revenue',
        icon: '🏥',
        annualPotential: '$2M-$8M',
      },
      {
        name: 'White-Label Intelligence',
        description:
          'Pharma companies embed Anchor Medical intelligence into their own platforms under their brand',
        icon: '🏷️',
        annualPotential: '$4M-$12M',
      },
      {
        name: 'Advisory & Consulting',
        description:
          'Expert biomedical intelligence advisory for drug development strategy and regulatory submissions',
        icon: '🎯',
        annualPotential: '$1M-$5M',
      },
    ],
    businessFunctions: [
      {
        name: 'Biomedical Research Operations',
        description:
          'Continuous scanning, indexing, and synthesis of 3,000+ daily medical publications',
        icon: '🔬',
      },
      {
        name: 'Drug Repurposing Engine',
        description: 'AI-driven discovery of existing compounds with new therapeutic potential',
        icon: '💊',
      },
      {
        name: 'Pharmacovigilance Intelligence',
        description:
          'Real-time adverse event detection and safety signal monitoring across global databases',
        icon: '⚠️',
      },
      {
        name: 'Clinical Trial Intelligence',
        description:
          'Worldwide trial monitoring, enrollment prediction, and competitive landscape analysis',
        icon: '📋',
      },
      {
        name: 'Disease Surveillance',
        description:
          'Early detection of emerging disease patterns from clinical, genomic, and epidemiological data',
        icon: '🌍',
      },
      {
        name: 'Cross-Sector Medical Correlations',
        description:
          'Connecting health data with climate, agriculture, and environmental datasets for novel insights',
        icon: '🔗',
      },
    ],
    technologyPartners: [
      {
        name: 'Neuralink',
        description: 'Brain-computer interface pioneer',
        logo: '🧠',
        integration:
          'Neural signal data integration for brain health monitoring, neurological disease pattern detection, and BCI-assisted rehabilitation intelligence',
      },
      {
        name: 'xAI (Grok)',
        description: 'Advanced AI reasoning engine',
        logo: '🤖',
        integration:
          'Grok-powered deep reasoning for complex drug interaction analysis, multi-paper synthesis, and biomedical hypothesis generation',
      },
      {
        name: 'X (Twitter)',
        description: 'Global real-time social platform',
        logo: '🐏',
        integration:
          'Health sentiment monitoring, disease outbreak early warning from social signals, and adverse event crowdsource detection',
      },
      {
        name: 'Starlink',
        description: 'Global satellite internet constellation',
        logo: '📡',
        integration:
          'Enabling real-time medical intelligence delivery to remote hospitals, field clinics, and disaster response teams worldwide',
      },
    ],
    apiProducts: [
      {
        name: 'Drug Interaction Check API',
        description:
          'Real-time drug-drug and drug-gene interaction analysis across 35M+ literature records',
        endpoint: '/api/v1/drug-interactions',
        pricePerCall: '$0.15',
      },
      {
        name: 'Adverse Event Signal API',
        description:
          'Query adverse event signals from FAERS and global pharmacovigilance databases',
        endpoint: '/api/v1/adverse-events',
        pricePerCall: '$0.25',
      },
      {
        name: 'Literature Synthesis API',
        description: 'AI-powered synthesis of research papers on any biomedical topic',
        endpoint: '/api/v1/literature-synthesis',
        pricePerCall: '$0.50',
      },
      {
        name: 'Clinical Trial Intelligence API',
        description: 'Real-time trial status, enrollment data, and competitive landscape queries',
        endpoint: '/api/v1/clinical-trials',
        pricePerCall: '$0.10',
      },
    ],
    companyStructure: {
      entity: 'Anchor AI Guard — Medical Division',
      division: 'Biomedical Intelligence Division',
      headcount: 'Founding team (scaling)',
      hq: 'Australia',
      registrations: [
        'Sovereign Architecture',
        'Designed for Compliance',
        'Australian-Built Platform',
      ],
    },
    disclaimer:
      'Anchor Medical is a research support tool only. It does not diagnose, treat, or prescribe. All outputs should be reviewed by qualified professionals before clinical application.',
    ctaText: 'Start Discovering What Others Miss',
    ctaSubtext: 'Guided demo — paid subscription for production • Full access to all features',
  },

  // ─── 2. ANCHOR FINANCE ─────────────────────────────────────────────
  {
    id: 'finance',
    slug: '/anchor-finance',
    brandName: 'Anchor Finance',
    tagline: 'Autonomous Financial Intelligence',
    heroSubtitle:
      "The world's first self-evolving financial intelligence platform. Cuts through market noise to deliver the signals that move decisions — before anyone else sees them.",
    icon: '💰',
    logoUrl: resolveAnchorLogoByAsset(''),
    colorFrom: 'from-blue-500',
    colorTo: 'to-indigo-500',
    accentColor: 'blue',
    borderColor: 'border-blue-500',
    textColor: 'text-blue-400',
    bgGradient: 'from-blue-500/10 via-transparent to-blue-900/5',
    domain: 'finance.anchoraiguard.com',
    targetMarket: [
      'Investment Banks',
      'Hedge Funds',
      'Central Banks',
      'Financial Analysts',
      'Compliance Teams',
      'Insurance Companies',
    ],
    problemStatement:
      "Markets move in milliseconds. Regulatory filings take days to parse. Cross-market correlations are invisible to siloed analysis. Your competitors are already trading on data you haven't seen.",
    solutionStatement:
      'Anchor Finance monitors every exchange, parses every filing, and correlates every signal in real time — turning raw data into alpha before your competitors know it exists.',
    features: [
      {
        icon: '📈',
        title: 'Real-Time Market Intelligence',
        description:
          'Tracks equities, bonds, commodities, forex, and crypto across global exchanges. AI highlights anomalies as they emerge.',
        worldFirst: true,
      },
      {
        icon: '📋',
        title: 'Regulatory Filing Analysis',
        description:
          'Parses SEC filings, FCA disclosures, and global regulatory documents in real time. Extracts material changes within minutes of publication.',
      },
      {
        icon: '🔮',
        title: 'Predictive Market Signals',
        description:
          'AI-powered forecasting that identifies momentum shifts, sector rotations, and liquidity patterns before consensus catches up.',
        worldFirst: true,
      },
      {
        icon: '💱',
        title: 'Forex & Macro Intelligence',
        description:
          'Monitors currency pairs, central bank signals, and macro-economic indicators. Correlates monetary policy with market movements.',
      },
      {
        icon: '⚡',
        title: 'Flash Event Detection',
        description:
          'Detect market-moving events — earnings surprises, geopolitical shifts, regulatory actions — within seconds of occurrence.',
      },
      {
        icon: '🔗',
        title: 'Cross-Asset Correlation',
        description:
          'Maps hidden relationships between asset classes, geographies, and economic indicators that siloed analysis consistently misses.',
        worldFirst: true,
      },
      {
        icon: '🧠',
        title: 'AI Evolution Engine',
        description:
          'Self-evolving AI that continuously improves signal detection. Runs autonomously on a 20-second cycle.',
        worldFirst: true,
      },
      {
        icon: '📊',
        title: 'Compliance Intelligence',
        description:
          'Automated compliance monitoring for SOX, MiFID II, Dodd-Frank, and Basel III. Real-time alerting on regulatory changes.',
      },
    ],
    dataSources: [
      { name: 'SEC EDGAR', description: 'US securities filings & disclosures' },
      { name: 'Federal Reserve FRED', description: 'Economic & financial data' },
      { name: 'Alpha Vantage', description: 'Global market data & technicals' },
      { name: 'European Central Bank', description: 'Eurozone monetary policy data' },
      { name: 'Bank of England', description: 'UK monetary & financial data' },
      { name: 'IMF/World Bank', description: 'Global economic indicators' },
    ],
    pricing: [
      {
        name: 'Analyst',
        price: '$4,990',
        period: '/mo',
        features: [
          '5 users',
          'Market monitoring',
          'Filing analysis',
          'Basic dashboards',
          'Email alerts',
        ],
        cta: 'Request demo',
      },
      {
        name: 'Professional',
        price: '$14,990',
        period: '/mo',
        features: [
          '25 users',
          'Everything in Analyst',
          'Predictive signals',
          'Cross-asset correlation',
          'API access',
          'Priority support',
        ],
        cta: 'Request demo',
        highlighted: true,
      },
      {
        name: 'Enterprise',
        price: '$49,990',
        period: '/mo',
        features: [
          'Unlimited users',
          'Everything in Professional',
          'Custom AI models',
          'Flash event detection',
          'On-premise deployment',
          'Dedicated analyst',
          '99.99% SLA',
        ],
        cta: 'Contact Sales',
      },
    ],
    testimonials: [],
    stats: [
      { value: '< 100ms', label: 'Signal Latency' },
      { value: '50+', label: 'Global Exchanges' },
      { value: '20s', label: 'AI Evolution Cycle' },
      { value: '40hrs/wk', label: 'Compliance Time Saved' },
    ],
    useCases: [
      {
        title: 'Alpha Generation',
        description:
          'Identify trading signals and market patterns before consensus. Turn data into edge.',
      },
      {
        title: 'Regulatory Compliance',
        description:
          'Automate filing analysis, compliance monitoring, and regulatory change management.',
      },
      {
        title: 'Risk Management',
        description:
          'Cross-asset correlation analysis to identify hidden portfolio risks and concentration exposure.',
      },
      {
        title: 'Central Bank Intelligence',
        description:
          'Track monetary policy signals across major central banks and predict rate trajectory.',
      },
    ],
    revenueStreams: [
      {
        name: 'SaaS Subscriptions',
        description: 'Tiered platform access from individual analysts to enterprise trading floors',
        icon: '💳',
        annualPotential: '$15M-$40M',
      },
      {
        name: 'Trading Signals API',
        description:
          'Real-time alpha signals, anomaly alerts, and predictive indicators via low-latency API',
        icon: '📡',
        annualPotential: '$10M-$30M',
      },
      {
        name: 'Data Licensing',
        description:
          'Licensed access to cross-asset correlation datasets, regulatory filing extracts, and macro indicators',
        icon: '📦',
        annualPotential: '$8M-$20M',
      },
      {
        name: 'Alpha-as-a-Service',
        description:
          'Managed signal generation for hedge funds and family offices — performance fee model',
        icon: '📈',
        annualPotential: '$5M-$25M',
      },
      {
        name: 'White-Label Financial Dashboards',
        description:
          'Banks and brokerages embed Anchor Finance intelligence into their client platforms',
        icon: '🏷️',
        annualPotential: '$6M-$18M',
      },
      {
        name: 'Compliance Automation',
        description:
          'Regulatory filing monitoring, compliance reporting, and audit-ready documentation',
        icon: '📋',
        annualPotential: '$4M-$12M',
      },
    ],
    businessFunctions: [
      {
        name: 'Market Intelligence Operations',
        description:
          'Real-time monitoring of 50+ global exchanges, parsing every trade, order flow shift, and anomaly',
        icon: '📈',
      },
      {
        name: 'Regulatory Filing Analysis',
        description:
          'Automated parsing of SEC, FCA, ASIC, and global regulatory filings within minutes of publication',
        icon: '📋',
      },
      {
        name: 'Predictive Signal Engine',
        description:
          'AI-powered forecasting generating alpha signals across equities, forex, commodities, and crypto',
        icon: '🔮',
      },
      {
        name: 'Cross-Asset Correlation Lab',
        description:
          'Discovery of hidden relationships between asset classes, geographies, and economic indicators',
        icon: '🔗',
      },
      {
        name: 'Flash Event Detection',
        description:
          'Sub-second detection of market-moving events — earnings surprises, geopolitical shifts, regulatory actions',
        icon: '⚡',
      },
      {
        name: 'Compliance Intelligence',
        description:
          'Automated regulatory compliance monitoring for SOX, MiFID II, Dodd-Frank, and Basel III',
        icon: '🛡️',
      },
    ],
    technologyPartners: [
      {
        name: 'xAI (Grok)',
        description: 'Advanced AI reasoning engine',
        logo: '🤖',
        integration:
          'Grok-powered deep financial reasoning for earnings analysis, regulatory impact assessment, and multi-variable market scenario modelling',
      },
      {
        name: 'X (Twitter)',
        description: 'Global real-time social platform',
        logo: '🐏',
        integration:
          'Real-time market sentiment analysis, social trading signal detection, executive communication monitoring, and breaking news alpha generation',
      },
      {
        name: 'Tesla',
        description: 'Electric vehicle and energy technology leader',
        logo: '🚗',
        integration:
          'EV market intelligence, Tesla supply chain tracking, energy storage market data, and automotive sector correlation analysis',
      },
      {
        name: 'Starlink',
        description: 'Global satellite internet constellation',
        logo: '📡',
        integration:
          'Ultra-low-latency financial data delivery to remote trading operations, disaster-resilient market access',
      },
    ],
    apiProducts: [
      {
        name: 'Market Anomaly Detection API',
        description:
          'Real-time anomaly detection across 50+ global exchanges with confidence scoring',
        endpoint: '/api/v1/market-anomalies',
        pricePerCall: '$0.50',
      },
      {
        name: 'Regulatory Filing Extract API',
        description: 'Structured data extraction from SEC, FCA, and global regulatory filings',
        endpoint: '/api/v1/filing-analysis',
        pricePerCall: '$1.00',
      },
      {
        name: 'Cross-Asset Correlation API',
        description:
          'Query hidden correlations between any combination of asset classes and indicators',
        endpoint: '/api/v1/correlations',
        pricePerCall: '$0.75',
      },
      {
        name: 'Predictive Signal API',
        description: 'AI-generated alpha signals with direction, magnitude, and confidence scoring',
        endpoint: '/api/v1/signals',
        pricePerCall: '$2.00',
      },
    ],
    companyStructure: {
      entity: 'Anchor AI Guard — Financial Division',
      division: 'Financial Markets Intelligence Division',
      headcount: 'Founding team (scaling)',
      hq: 'Australia',
      registrations: [
        'Sovereign Architecture',
        'Designed for Compliance',
        'Australian-Built Platform',
      ],
    },
    ctaText: 'See the Signals First',
    ctaSubtext: 'Guided demo — paid subscription for production • Full market access',
  },

  // ─── 3. ANCHOR LEGAL ────────────────────────────────────────────────
  {
    id: 'legal',
    slug: '/anchor-legal',
    brandName: 'Anchor Legal',
    tagline: 'Autonomous Legal Intelligence',
    heroSubtitle:
      "The world's first self-evolving legal intelligence platform. Tracks every case, monitors every regulation, and predicts every ruling — so you're never caught off guard.",
    icon: '⚖️',
    logoUrl: resolveAnchorLogoByAsset(''),
    colorFrom: 'from-amber-500',
    colorTo: 'to-yellow-500',
    accentColor: 'amber',
    borderColor: 'border-amber-500',
    textColor: 'text-amber-400',
    bgGradient: 'from-amber-500/10 via-transparent to-amber-900/5',
    domain: 'legal.anchoraiguard.com',
    targetMarket: [
      'Law Firms',
      'Corporate Legal Teams',
      'Regulatory Bodies',
      'Compliance Officers',
      'Government Agencies',
      'Insurance Companies',
    ],
    problemStatement:
      'Legal landscapes change daily. Regulatory shifts catch firms off guard. Case law precedents are buried in millions of documents. Compliance gaps cost billions.',
    solutionStatement:
      'Anchor Legal monitors every jurisdiction, analyses every filing, and predicts regulatory changes before they happen — turning legal risk into competitive advantage.',
    features: [
      {
        icon: '📜',
        title: 'Real-Time Regulatory Tracking',
        description:
          'Monitors legislative changes across 190+ jurisdictions. AI classifies impact severity and maps changes to your existing compliance framework.',
        worldFirst: true,
      },
      {
        icon: '⚖️',
        title: 'Case Law Intelligence',
        description:
          'Analyses judicial decisions across courts worldwide. Identifies precedent patterns and predicts ruling outcomes with confidence scoring.',
      },
      {
        icon: '🔮',
        title: 'Regulatory Impact Prediction',
        description:
          'Predicts how proposed regulations will affect your industry, operations, and compliance posture before they become law.',
        worldFirst: true,
      },
      {
        icon: '📋',
        title: 'Contract Risk Analysis',
        description:
          'AI-powered contract review that identifies hidden risks, unfavourable terms, and compliance gaps in seconds.',
      },
      {
        icon: '🌍',
        title: 'Cross-Jurisdiction Analysis',
        description:
          'Compares regulatory requirements across jurisdictions for multi-national operations. Highlights conflicts and harmonisation opportunities.',
      },
      {
        icon: '🧠',
        title: 'AI Evolution Engine',
        description:
          'Self-evolving AI that continuously improves legal analysis. Runs autonomously on a 25-second cycle.',
        worldFirst: true,
      },
    ],
    dataSources: [
      { name: 'Court Records (Global)', description: 'Judicial decisions worldwide' },
      { name: 'Legislative Databases', description: '190+ jurisdictions tracked' },
      { name: 'EU EUR-Lex', description: 'European legislation portal' },
      { name: 'UK legislation.gov.uk', description: 'UK statutory instruments' },
      { name: 'US Federal Register', description: 'US regulatory notices' },
    ],
    pricing: [
      {
        name: 'Practitioner',
        price: '$3,990',
        period: '/mo',
        features: [
          '5 users',
          'Regulatory tracking',
          'Case law search',
          'Basic alerts',
          'Email support',
        ],
        cta: 'Request demo',
      },
      {
        name: 'Firm',
        price: '$12,990',
        period: '/mo',
        features: [
          '25 users',
          'Everything in Practitioner',
          'Predictive analysis',
          'Contract review AI',
          'API access',
          'Priority support',
        ],
        cta: 'Request demo',
        highlighted: true,
      },
      {
        name: 'Enterprise',
        price: '$39,990',
        period: '/mo',
        features: [
          'Unlimited users',
          'Everything in Firm',
          'Cross-jurisdiction analysis',
          'Custom AI models',
          'On-premise',
          'Dedicated legal analyst',
        ],
        cta: 'Contact Sales',
      },
    ],
    testimonials: [],
    stats: [
      { value: '190+', label: 'Jurisdictions Tracked' },
      { value: '15min', label: 'Contract Review Time' },
      { value: '25s', label: 'AI Evolution Cycle' },
      { value: '8 weeks', label: 'Early Regulatory Warning' },
    ],
    useCases: [
      {
        title: 'Regulatory Compliance',
        description:
          'Track regulatory changes across all your jurisdictions and automatically map impact to your compliance framework.',
      },
      {
        title: 'Litigation Strategy',
        description:
          'Analyse case law precedents to build stronger arguments and predict judicial outcomes.',
      },
      {
        title: 'M&A Due Diligence',
        description:
          'Rapid contract analysis and regulatory risk assessment for mergers and acquisitions.',
      },
      {
        title: 'Policy Impact Assessment',
        description:
          'Predict how proposed legislation will affect your industry before it becomes law.',
      },
    ],
    revenueStreams: [
      {
        name: 'SaaS Subscriptions',
        description: 'Tiered platform access from solo practitioners to global law firms',
        icon: '💳',
        annualPotential: '$6M-$20M',
      },
      {
        name: 'Legal AI API',
        description:
          'Per-call access to regulatory tracking, case law analysis, and contract review capabilities',
        icon: '🔌',
        annualPotential: '$4M-$12M',
      },
      {
        name: 'Regulatory Alerts-as-a-Service',
        description:
          'Jurisdiction-specific regulatory change alerts with impact scoring — sold per jurisdiction',
        icon: '📣',
        annualPotential: '$3M-$10M',
      },
      {
        name: 'Contract Review Licensing',
        description:
          'Enterprise licensing of AI contract review engine for in-house legal departments',
        icon: '📋',
        annualPotential: '$5M-$15M',
      },
      {
        name: 'White-Label Compliance Tools',
        description:
          'Law firms and compliance consultancies embed Anchor Legal under their own brand',
        icon: '🏷️',
        annualPotential: '$3M-$10M',
      },
      {
        name: 'Expert Advisory Services',
        description:
          'Regulatory impact consulting, M&A due diligence support, and cross-jurisdiction compliance advisory',
        icon: '🎯',
        annualPotential: '$2M-$8M',
      },
    ],
    businessFunctions: [
      {
        name: 'Regulatory Intelligence Operations',
        description:
          'Continuous monitoring of legislative changes across 190+ jurisdictions with AI-powered impact classification',
        icon: '📜',
      },
      {
        name: 'Case Law Analytics',
        description:
          'Deep analysis of judicial decisions worldwide — precedent mapping, outcome prediction, and argument strength scoring',
        icon: '⚖️',
      },
      {
        name: 'Contract Intelligence Engine',
        description:
          'AI-powered contract review identifying hidden risks, unfavourable terms, and compliance gaps in seconds',
        icon: '📋',
      },
      {
        name: 'Cross-Jurisdiction Compliance',
        description:
          'Comparative regulatory analysis for multinational operations — conflict detection and harmonisation recommendations',
        icon: '🌍',
      },
      {
        name: 'Regulatory Prediction',
        description:
          'Forecasting how proposed regulations will affect industries and operations before they become law',
        icon: '🔮',
      },
    ],
    technologyPartners: [
      {
        name: 'xAI (Grok)',
        description: 'Advanced AI reasoning engine',
        logo: '🤖',
        integration:
          'Grok-powered legal document synthesis, contract clause analysis, and multi-jurisdiction regulatory reasoning with chain-of-thought explanation',
      },
      {
        name: 'X (Twitter)',
        description: 'Global real-time social platform',
        logo: '🐏',
        integration:
          'Public sentiment analysis on regulatory changes, political discourse monitoring for legislative prediction, and legal industry trend detection',
      },
      {
        name: 'SpaceX',
        description: 'Aerospace and space transportation',
        logo: '🚀',
        integration:
          'Space law and regulation intelligence — tracking the rapidly evolving regulatory frameworks for commercial space operations and orbital debris liability',
      },
    ],
    apiProducts: [
      {
        name: 'Regulatory Change API',
        description:
          'Real-time regulatory change monitoring across 190+ jurisdictions with impact scoring',
        endpoint: '/api/v1/regulatory-changes',
        pricePerCall: '$0.30',
      },
      {
        name: 'Contract Risk Analysis API',
        description:
          'AI-powered contract review returning risk scores, flagged clauses, and recommendations',
        endpoint: '/api/v1/contract-analysis',
        pricePerCall: '$2.00',
      },
      {
        name: 'Case Law Search API',
        description:
          'Semantic search across global case law with precedent mapping and outcome prediction',
        endpoint: '/api/v1/case-law',
        pricePerCall: '$0.50',
      },
      {
        name: 'Compliance Gap API',
        description:
          'Compare your compliance posture against regulatory requirements across jurisdictions',
        endpoint: '/api/v1/compliance-gap',
        pricePerCall: '$1.00',
      },
    ],
    companyStructure: {
      entity: 'Anchor AI Guard — Legal Division',
      division: 'Legal & Regulatory Intelligence Division',
      headcount: 'Founding team (scaling)',
      hq: 'Australia',
      registrations: [
        'Sovereign Architecture',
        'Designed for Compliance',
        'Australian-Built Platform',
      ],
    },
    ctaText: 'Stay Ahead of Every Regulation',
    ctaSubtext: 'Guided demo — paid subscription for production • 190+ jurisdictions',
  },

  // ─── 4. ANCHOR CLIMATE ──────────────────────────────────────────────
  {
    id: 'climate',
    slug: '/anchor-climate',
    brandName: 'Anchor Climate',
    tagline: 'Autonomous Climate Intelligence',
    heroSubtitle:
      "The world's first self-evolving climate intelligence platform. Monitors every sensor, models every scenario, and predicts every risk — so you can act before the impact arrives.",
    icon: '🌍',
    logoUrl: resolveAnchorLogoByAsset(''),
    colorFrom: 'from-green-500',
    colorTo: 'to-emerald-500',
    accentColor: 'green',
    borderColor: 'border-green-500',
    textColor: 'text-green-400',
    bgGradient: 'from-green-500/10 via-transparent to-green-900/5',
    domain: 'climate.anchoraiguard.com',
    targetMarket: [
      'Government Agencies',
      'Energy Companies',
      'Insurance Underwriters',
      'Agricultural Enterprises',
      'Infrastructure Planners',
      'ESG Analysts',
    ],
    problemStatement:
      'Climate data is fragmented across thousands of sources. Risk models are outdated within months. Carbon reporting is manual and error-prone. Physical climate risks are underestimated.',
    solutionStatement:
      'Anchor Climate unifies global climate data, models physical and transition risks in real time, and delivers actionable intelligence for every decision — from board-level ESG to operational resilience.',
    features: [
      {
        icon: '🌍¡️',
        title: 'Real-Time Climate Monitoring',
        description:
          'Aggregates satellite, ground sensor, and ocean buoy data from NASA, NOAA, ESA, and 50+ national meteorological agencies.',
        worldFirst: true,
      },
      {
        icon: '📊',
        title: 'Carbon Intelligence Dashboard',
        description:
          'Automated carbon accounting, emissions tracking, and Scope 1/2/3 reporting. Maps carbon risk to financial exposure.',
      },
      {
        icon: '🔮',
        title: 'Climate Risk Prediction',
        description:
          'AI-powered physical risk modelling for floods, wildfires, storms, and sea-level rise. Asset-level granularity.',
        worldFirst: true,
      },
      {
        icon: '🏭',
        title: 'Transition Risk Analysis',
        description:
          'Models the financial impact of regulatory, market, and technology transitions on your operations and portfolio.',
      },
      {
        icon: '🌊',
        title: 'Extreme Weather Early Warning',
        description:
          'Predicts extreme weather events with higher accuracy and longer lead times than traditional forecasting.',
      },
      {
        icon: '🧠',
        title: 'AI Evolution Engine',
        description:
          'Self-evolving AI that continuously improves climate prediction. Runs autonomously on a 30-second cycle.',
        worldFirst: true,
      },
    ],
    dataSources: [
      { name: 'NASA GISS / Earthdata', description: 'Global climate & satellite data' },
      { name: 'NOAA', description: 'US oceanic & atmospheric data' },
      { name: 'Copernicus / ESA', description: 'European climate monitoring' },
      { name: 'IPCC Reports', description: 'Climate science assessments' },
      { name: 'Carbon Disclosure Project', description: 'Corporate emissions data' },
    ],
    pricing: [
      {
        name: 'Analyst',
        price: '$3,490',
        period: '/mo',
        features: [
          '5 users',
          'Climate monitoring',
          'Carbon dashboard',
          'Basic reporting',
          'Email support',
        ],
        cta: 'Request demo',
      },
      {
        name: 'Professional',
        price: '$11,990',
        period: '/mo',
        features: [
          '25 users',
          'Everything in Analyst',
          'Risk prediction',
          'Transition analysis',
          'API access',
          'Priority support',
        ],
        cta: 'Request demo',
        highlighted: true,
      },
      {
        name: 'Enterprise',
        price: '$34,990',
        period: '/mo',
        features: [
          'Unlimited users',
          'Everything in Professional',
          'Asset-level risk models',
          'Custom AI models',
          'On-premise',
          'Dedicated analyst',
        ],
        cta: 'Contact Sales',
      },
    ],
    testimonials: [],
    stats: [
      { value: '50+', label: 'Data Agencies Connected' },
      { value: '3 weeks', label: 'Early Risk Detection' },
      { value: '30s', label: 'AI Evolution Cycle' },
      { value: '$12M+', label: 'Losses Prevented (Case Study)' },
    ],
    useCases: [
      {
        title: 'ESG Reporting',
        description:
          'Automate carbon accounting, emissions tracking, and regulatory ESG reporting across your global operations.',
      },
      {
        title: 'Physical Risk Assessment',
        description:
          'Model asset-level exposure to floods, wildfires, storms, and rising sea levels.',
      },
      {
        title: 'Insurance Underwriting',
        description:
          'Climate risk scoring for property, crop, and business interruption insurance portfolios.',
      },
      {
        title: 'Infrastructure Planning',
        description:
          'Climate-adjusted planning for construction, utilities, and transport infrastructure.',
      },
    ],
    revenueStreams: [
      {
        name: 'SaaS Subscriptions',
        description: 'Tiered platform access from ESG analysts to enterprise sustainability teams',
        icon: '💳',
        annualPotential: '$7M-$22M',
      },
      {
        name: 'Climate Risk API',
        description:
          'Per-call physical and transition risk scoring for assets, portfolios, and supply chains',
        icon: '🌍¡️',
        annualPotential: '$5M-$18M',
      },
      {
        name: 'Carbon Credit Marketplace',
        description:
          'Verified carbon credit trading platform with AI-powered pricing and quality scoring',
        icon: '🌿',
        annualPotential: '$10M-$50M',
      },
      {
        name: 'ESG Data Licensing',
        description:
          'Licensed access to climate risk scores, emissions data, and ESG metrics for financial institutions',
        icon: '📦',
        annualPotential: '$6M-$20M',
      },
      {
        name: 'Insurance Risk Scoring',
        description:
          'Climate-adjusted risk scores for property, crop, and business interruption insurance underwriting',
        icon: '🛡️',
        annualPotential: '$4M-$15M',
      },
      {
        name: 'Advisory & Consulting',
        description:
          'Climate risk assessment, transition planning, and regulatory compliance advisory for C-suite',
        icon: '🎯',
        annualPotential: '$2M-$8M',
      },
    ],
    businessFunctions: [
      {
        name: 'Climate Data Aggregation',
        description:
          'Unifying satellite, ground sensor, and ocean buoy data from 50+ national meteorological agencies',
        icon: '🌍¡️',
      },
      {
        name: 'Carbon Intelligence',
        description:
          'Automated Scope 1/2/3 carbon accounting, emissions tracking, and regulatory ESG reporting',
        icon: '📊',
      },
      {
        name: 'Physical Risk Modelling',
        description:
          'Asset-level climate risk modelling for floods, wildfires, storms, and sea-level rise scenarios',
        icon: '🌊',
      },
      {
        name: 'Transition Risk Analysis',
        description:
          'Financial modelling of regulatory, market, and technology transitions on operations and portfolios',
        icon: '🏭',
      },
      {
        name: 'Extreme Weather Forecasting',
        description:
          'AI-enhanced weather prediction with longer lead times and higher accuracy than traditional services',
        icon: '⛈️',
      },
    ],
    technologyPartners: [
      {
        name: 'Tesla',
        description: 'Electric vehicles, solar, and energy storage',
        logo: '⚡',
        integration:
          'Real-time Tesla Solar and Megapack deployment data, EV adoption metrics for transport emissions modelling, and Powerwall grid interaction intelligence',
      },
      {
        name: 'SpaceX',
        description: 'Aerospace and space transportation',
        logo: '🚀',
        integration:
          'Launch emissions tracking and space-based climate observation sensor data integration for enhanced Earth monitoring',
      },
      {
        name: 'The Boring Company',
        description: 'Infrastructure and tunnelling',
        logo: '🚧',
        integration:
          'Underground infrastructure climate resilience data, geothermal energy potential mapping, and urban heat island mitigation intelligence',
      },
      {
        name: 'xAI (Grok)',
        description: 'Advanced AI reasoning engine',
        logo: '🤖',
        integration:
          'Grok-powered climate scenario modelling, complex system dynamics analysis, and multi-variable environmental prediction',
      },
      {
        name: 'Starlink',
        description: 'Global satellite internet constellation',
        logo: '📡',
        integration:
          'Connectivity for remote climate sensors, weather stations, and environmental monitoring equipment in areas without terrestrial internet',
      },
    ],
    apiProducts: [
      {
        name: 'Physical Risk Score API',
        description:
          'Asset-level physical climate risk scoring for flood, fire, storm, and sea-level rise',
        endpoint: '/api/v1/physical-risk',
        pricePerCall: '$0.40',
      },
      {
        name: 'Carbon Footprint API',
        description:
          'Automated Scope 1/2/3 emissions calculation for organisations and supply chains',
        endpoint: '/api/v1/carbon-footprint',
        pricePerCall: '$0.60',
      },
      {
        name: 'Climate Forecast API',
        description: 'Hyper-local climate forecasting with seasonal and decadal projections',
        endpoint: '/api/v1/climate-forecast',
        pricePerCall: '$0.35',
      },
      {
        name: 'ESG Score API',
        description: 'Comprehensive ESG scoring for companies, portfolios, and sovereign entities',
        endpoint: '/api/v1/esg-score',
        pricePerCall: '$0.50',
      },
    ],
    companyStructure: {
      entity: 'Anchor AI Guard — Climate Division',
      division: 'Environmental & Climate Intelligence Division',
      headcount: 'Founding team (scaling)',
      hq: 'Australia',
      registrations: [
        'Sovereign Architecture',
        'Designed for Compliance',
        'Australian-Built Platform',
      ],
    },
    ctaText: 'Predict Climate Risk Before Impact',
    ctaSubtext: 'Guided demo — paid subscription for production • Real-time global data',
  },

  // ─── 5. ANCHOR DEFENCE ──────────────────────────────────────────────
  {
    id: 'defence',
    slug: '/anchor-defence',
    brandName: 'Anchor Defence',
    tagline: 'Autonomous Defence Intelligence',
    heroSubtitle:
      'Sovereign-grade defence intelligence for national security, military operations, and strategic threat assessment. Built for the highest classification environments.',
    icon: '🛡️',
    logoUrl: resolveAnchorLogoByAsset(''),
    colorFrom: 'from-red-500',
    colorTo: 'to-rose-500',
    accentColor: 'red',
    borderColor: 'border-red-500',
    textColor: 'text-red-400',
    bgGradient: 'from-red-500/10 via-transparent to-red-900/5',
    domain: 'defence.anchoraiguard.com',
    targetMarket: [
      'Defence Ministries',
      'Military Intelligence',
      'National Security Agencies',
      'Defence Contractors',
      'NATO/Allied Forces',
      'Critical Infrastructure Operators',
    ],
    problemStatement:
      "Threat landscapes evolve faster than human analysis. Intelligence silos prevent correlation. Adversaries exploit gaps between domains. Traditional OSINT tools can't keep pace.",
    solutionStatement:
      'Anchor Defence delivers autonomous, sovereign-grade intelligence that correlates across all threat domains — cyber, geopolitical, kinetic, and space — in real time, at national scale.',
    features: [
      {
        icon: '🎯',
        title: 'Multi-Domain Threat Correlation',
        description:
          'Correlates cyber, geopolitical, kinetic, and space-based threats into a unified operational picture. Detects compound threats invisible to siloed analysis.',
        worldFirst: true,
      },
      {
        icon: '🛰️',
        title: 'Satellite & Space Intelligence',
        description:
          'Integrates satellite imagery analysis with ground-truth data for real-time situational awareness across theatres of operation.',
      },
      {
        icon: '🔮',
        title: 'Adversary Prediction Engine',
        description:
          'AI-powered threat actor profiling and operation prediction. Models adversary TTPs and forecasts strategic moves.',
        worldFirst: true,
      },
      {
        icon: '🏛️',
        title: 'Geopolitical Risk Modelling',
        description:
          'Tracks political stability, economic sanctions, military postures, and diplomatic signals across 195 nations.',
      },
      {
        icon: '⚔️',
        title: 'Force Protection Intelligence',
        description:
          'Real-time threat alerting for deployed forces. Correlates HUMINT, SIGINT, and OSINT for force protection decisions.',
      },
      {
        icon: '🧠',
        title: 'AI Evolution Engine',
        description:
          'Self-evolving AI optimised for defence applications. Runs autonomously on a 35-second cycle. Air-gapped capable.',
        worldFirst: true,
      },
    ],
    dataSources: [
      { name: 'OSINT Feeds (Global)', description: 'Open-source intelligence aggregation' },
      { name: 'Geopolitical Risk APIs', description: 'Political stability & conflict data' },
      { name: 'MITRE ATT&CK', description: 'Adversary tactics, techniques & procedures' },
      { name: 'Satellite Imagery (via ESA, NASA)', description: 'Optical & SAR analysis' },
      { name: 'Sanctions & Export Control Databases', description: 'Global sanctions tracking' },
    ],
    pricing: [
      {
        name: 'Analyst',
        price: '$9,990',
        period: '/mo',
        features: [
          '5 users',
          'OSINT monitoring',
          'Geopolitical tracking',
          'Basic dashboards',
          'Secure support',
        ],
        cta: 'Request Access',
      },
      {
        name: 'Command',
        price: '$49,990',
        period: '/mo',
        features: [
          '50 users',
          'Everything in Analyst',
          'Multi-domain correlation',
          'Adversary prediction',
          'API access',
          'Priority support',
        ],
        cta: 'Request Access',
        highlighted: true,
      },
      {
        name: 'Sovereign',
        price: 'Custom',
        period: '',
        features: [
          'Unlimited users',
          'Everything in Command',
          'Air-gapped deployment',
          'Custom AI models',
          'On-premise / classified',
          'Dedicated intelligence team',
          'National-scale telemetry',
        ],
        cta: 'Contact Defence Sales',
      },
    ],
    testimonials: [],
    stats: [
      { value: '195', label: 'Nations Monitored' },
      { value: '72hrs', label: 'Adversary Prediction Lead' },
      { value: '35s', label: 'AI Evolution Cycle' },
      { value: '5', label: 'Intelligence Domains Fused' },
    ],
    useCases: [
      {
        title: 'National Threat Assessment',
        description:
          'Comprehensive multi-domain threat assessment correlating cyber, geopolitical, and kinetic intelligence.',
      },
      {
        title: 'Force Protection',
        description:
          'Real-time threat alerting for deployed forces, integrating HUMINT, SIGINT, and OSINT feeds.',
      },
      {
        title: 'Adversary Analysis',
        description:
          'Profile threat actors, predict operations, and map TTPs across Advanced Persistent Threat groups.',
      },
      {
        title: 'Strategic Planning',
        description:
          'Data-driven strategic intelligence for defence planning, resource allocation, and readiness assessment.',
      },
    ],
    revenueStreams: [
      {
        name: 'Sovereign Platform Licensing',
        description:
          'Air-gapped, classified-environment platform deployments for national defence agencies',
        icon: '🛡️',
        annualPotential: '$40M-$120M',
      },
      {
        name: 'Intelligence-as-a-Service',
        description:
          'Managed multi-domain intelligence briefings for allied nations and defence contractors',
        icon: '📰',
        annualPotential: '$18M-$42M',
      },
      {
        name: 'Training & Simulation',
        description:
          'AI-powered threat simulation, wargaming scenarios, and intelligence analyst training',
        icon: '🎯',
        annualPotential: '$10M-$35M',
      },
      {
        name: 'Threat Briefing Subscriptions',
        description:
          'Classified and unclassified threat intelligence briefings for government and C-suite',
        icon: '📊',
        annualPotential: '$10M-$35M',
      },
      {
        name: 'Defence Contractor Integration',
        description:
          'API and platform integration for Tier-1 defence contractors into their mission systems',
        icon: '🔌',
        annualPotential: '$15M-$38M',
      },
      {
        name: 'Cyber Range Services',
        description: 'Live-fire cyber defence exercises powered by autonomous adversary simulation',
        icon: '⚔️',
        annualPotential: '$4M-$15M',
      },
      {
        name: 'AUKUS Integration Licensing',
        description: 'Three-nation interoperability platform for AUKUS Pillar II',
        icon: '🤝',
        annualPotential: '$25M-$65M',
      },
      {
        name: 'Sensor-to-Shooter Platform',
        description:
          'F-35/BACN/Ground Commander kill-chain latency monitoring for allied militaries',
        icon: '⚡',
        annualPotential: '$12M-$35M',
      },
    ],
    businessFunctions: [
      {
        name: 'Multi-Domain Threat Fusion',
        description:
          'Correlating cyber, geopolitical, kinetic, and space-based threats into unified operational pictures',
        icon: '🎯',
      },
      {
        name: 'Adversary Prediction',
        description:
          'AI-powered threat actor profiling, TTP mapping, and operational prediction at strategic level',
        icon: '🔮',
      },
      {
        name: 'Geopolitical Risk Assessment',
        description:
          'Political stability tracking, sanctions monitoring, and diplomatic signal analysis across 195 nations',
        icon: '�️',
      },
      {
        name: 'Force Protection Intelligence',
        description:
          'Real-time threat alerting integrating HUMINT, SIGINT, and OSINT for deployed force protection',
        icon: '🛡️',
      },
      {
        name: 'Space & Satellite Defence',
        description:
          'Monitoring ASAT threats, orbital debris, and contested space domain operations',
        icon: '🛰️',
      },
      {
        name: 'Cyber Operations Intelligence',
        description:
          'APT tracking, zero-day intelligence, and nation-state cyber campaign analysis',
        icon: '🔒',
      },
    ],
    technologyPartners: [
      {
        name: 'SpaceX / Starshield',
        description: 'Defence-grade space services',
        logo: '🚀',
        integration:
          'Starshield secure communications, SpaceX launch intelligence for military payload tracking, and contested space domain awareness',
      },
      {
        name: 'Starlink',
        description: 'Global satellite internet constellation',
        logo: '📡',
        integration:
          'Battlefield communications resilience, field-deployed intelligence terminal connectivity, and denied-area communication backup',
      },
      {
        name: 'xAI (Grok)',
        description: 'Advanced AI reasoning engine',
        logo: '🤖',
        integration:
          'Grok-powered adversary behaviour modelling, complex scenario wargaming, and multi-source intelligence synthesis with reasoning chains',
      },
      {
        name: 'X (Twitter)',
        description: 'Global real-time social platform',
        logo: '🐏',
        integration:
          'OSINT collection from social media, public sentiment analysis for stability assessment, and disinformation campaign detection',
      },
      {
        name: 'Tesla',
        description: 'Autonomous vehicle and energy technology',
        logo: '🚗',
        integration:
          'Autonomous vehicle convoy intelligence, mobile power generation for field operations, and autonomous logistics support',
      },
    ],
    apiProducts: [
      {
        name: 'Threat Assessment API',
        description:
          'Multi-domain threat scoring for regions, actors, and scenarios with confidence levels',
        endpoint: '/api/v1/threat-assessment',
        pricePerCall: '$5.00',
      },
      {
        name: 'Adversary Profile API',
        description:
          'Detailed threat actor profiles with TTPs, capabilities, and predicted operations',
        endpoint: '/api/v1/adversary-profile',
        pricePerCall: '$10.00',
      },
      {
        name: 'Geopolitical Risk API',
        description: 'Real-time geopolitical risk scores for 195 nations with trend analysis',
        endpoint: '/api/v1/geopolitical-risk',
        pricePerCall: '$2.00',
      },
      {
        name: 'OSINT Aggregation API',
        description: 'Curated open-source intelligence feeds with AI-powered relevance scoring',
        endpoint: '/api/v1/osint',
        pricePerCall: '$1.00',
      },
      {
        name: 'Conflict Zone Feed API',
        description:
          'Real-time conflict zone status, sensor detections, and threat levels for 10 active theatres',
        endpoint: '/api/v1/conflict-zones',
        pricePerCall: '$8.00',
      },
      {
        name: 'Comms Link Health API',
        description: 'Link 16, BACN, and SATCOM health per conflict zone with EW jamming alerts',
        endpoint: '/api/v1/comms-health',
        pricePerCall: '$12.00',
      },
      {
        name: 'AUKUS Data Flow API',
        description:
          'Three-nation interoperability status — E-11A relay, RAAF sync, Electronic Noise Floor',
        endpoint: '/api/v1/aukus-dataflow',
        pricePerCall: '$15.00',
      },
      {
        name: 'Sensor-to-Shooter API',
        description: 'Kill chain latency and hop-by-hop status from F-35A MADL to ground commander',
        endpoint: '/api/v1/sensor-to-shooter',
        pricePerCall: '$20.00',
      },
    ],
    companyStructure: {
      entity: 'Anchor AI Guard — Defence Division',
      division: 'National Security & Defence Intelligence Division',
      headcount: 'Founding team (scaling)',
      hq: 'Australia',
      registrations: [
        'Sovereign Architecture',
        'Designed for Compliance',
        'Australian-Built Platform',
      ],
    },
    ctaText: 'Request Sovereign Access',
    ctaSubtext: 'Security-cleared briefing available • Air-gapped deployment • NATO-compatible',
  },

  // ─── 6. ANCHOR ENERGY ──────────────────────────────────────────────
  {
    id: 'energy',
    slug: '/anchor-energy',
    brandName: 'Anchor Energy',
    tagline: 'Autonomous Energy Intelligence',
    heroSubtitle:
      "The world's first self-evolving energy intelligence platform. Monitors every grid, tracks every commodity, and predicts every disruption — from solar farms to nuclear plants.",
    icon: '⚡',
    logoUrl: resolveAnchorLogoByAsset(''),
    colorFrom: 'from-orange-500',
    colorTo: 'to-amber-500',
    accentColor: 'orange',
    borderColor: 'border-orange-500',
    textColor: 'text-orange-400',
    bgGradient: 'from-orange-500/10 via-transparent to-orange-900/5',
    domain: 'energy.anchoraiguard.com',
    targetMarket: [
      'Energy Companies',
      'Grid Operators',
      'Utilities',
      'Renewable Energy Developers',
      'Energy Traders',
      'Government Energy Departments',
    ],
    problemStatement:
      'Energy markets are volatile. Grid stability is under threat from renewables integration. Commodity price swings are unpredictable. Regulatory landscapes shift rapidly across jurisdictions.',
    solutionStatement:
      'Anchor Energy unifies grid telemetry, commodity data, and regulatory intelligence into a single autonomous platform — delivering real-time operational and strategic energy intelligence.',
    features: [
      {
        icon: '⚡',
        title: 'Grid Intelligence & Stability',
        description:
          'Real-time monitoring of grid load, frequency, and stability across interconnected networks. Predicts congestion and outage risk.',
        worldFirst: true,
      },
      {
        icon: '🛢️',
        title: 'Commodity Market Intelligence',
        description:
          'Tracks oil, gas, LNG, coal, carbon credits, and renewable certificates across global exchanges. AI-powered price forecasting.',
      },
      {
        icon: '☀️',
        title: 'Renewable Integration Analysis',
        description:
          'Models the impact of solar, wind, and battery storage on grid stability and power pricing.',
      },
      {
        icon: '🔮',
        title: 'Supply Disruption Prediction',
        description:
          'Predicts energy supply disruptions from geopolitical events, weather, infrastructure failures, and market dynamics.',
        worldFirst: true,
      },
      {
        icon: '📋',
        title: 'Regulatory Intelligence',
        description:
          'Tracks energy regulations, carbon pricing schemes, and subsidy programs across 100+ jurisdictions.',
      },
      {
        icon: '🧠',
        title: 'AI Evolution Engine',
        description:
          'Self-evolving AI that continuously improves energy prediction. Runs autonomously on a 40-second cycle.',
        worldFirst: true,
      },
    ],
    dataSources: [
      { name: 'US EIA', description: 'Energy information & statistics' },
      { name: 'IEA', description: 'International energy data' },
      { name: 'Grid Operator APIs', description: 'Real-time grid telemetry' },
      { name: 'ICE / NYMEX', description: 'Commodity exchange data' },
      { name: 'EU ETS', description: 'Carbon emissions trading data' },
    ],
    pricing: [
      {
        name: 'Analyst',
        price: '$4,490',
        period: '/mo',
        features: [
          '5 users',
          'Market monitoring',
          'Price alerts',
          'Basic dashboards',
          'Email support',
        ],
        cta: 'Request demo',
      },
      {
        name: 'Operator',
        price: '$14,990',
        period: '/mo',
        features: [
          '25 users',
          'Everything in Analyst',
          'Grid intelligence',
          'Supply prediction',
          'API access',
          'Priority support',
        ],
        cta: 'Request demo',
        highlighted: true,
      },
      {
        name: 'Enterprise',
        price: '$39,990',
        period: '/mo',
        features: [
          'Unlimited users',
          'Everything in Operator',
          'Custom AI models',
          'On-premise deployment',
          'Dedicated analyst',
          '99.9% SLA',
        ],
        cta: 'Contact Sales',
      },
    ],
    testimonials: [],
    stats: [
      { value: '100+', label: 'Jurisdictions Tracked' },
      { value: '5 days', label: 'Early Disruption Warning' },
      { value: '40s', label: 'AI Evolution Cycle' },
      { value: '0', label: 'Load-Shedding Events (Case Study)' },
    ],
    useCases: [
      {
        title: 'Energy Trading',
        description:
          'Commodity price forecasting and supply/demand prediction for energy trading operations.',
      },
      {
        title: 'Grid Management',
        description: 'Real-time grid stability monitoring and renewable integration planning.',
      },
      {
        title: 'Regulatory Compliance',
        description:
          'Track energy regulations, carbon pricing, and subsidy programs across jurisdictions.',
      },
      {
        title: 'Supply Chain Risk',
        description:
          'Predict energy supply disruptions from geopolitical, weather, and infrastructure risks.',
      },
    ],
    revenueStreams: [
      {
        name: 'SaaS Subscriptions',
        description:
          'Tiered platform access from energy analysts to grid operators and utility enterprises',
        icon: '💳',
        annualPotential: '$10M-$30M',
      },
      {
        name: 'Grid Analytics API',
        description:
          'Real-time grid load, stability, and congestion intelligence for utilities and grid operators',
        icon: '⚡',
        annualPotential: '$6M-$20M',
      },
      {
        name: 'Energy Trading Signals',
        description:
          'AI-powered commodity price forecasting and supply/demand signals for energy traders',
        icon: '📈',
        annualPotential: '$10M-$35M',
      },
      {
        name: 'Carbon Market Intelligence',
        description: 'Carbon credit pricing, ETS tracking, and emissions compliance intelligence',
        icon: '🌿',
        annualPotential: '$4M-$15M',
      },
      {
        name: 'Smart Grid Licensing',
        description:
          'White-label grid intelligence for utilities to embed in their SCADA and EMS systems',
        icon: '🏷️',
        annualPotential: '$5M-$18M',
      },
      {
        name: 'Advisory & Consulting',
        description:
          'Energy transition planning, regulatory compliance, and grid modernisation advisory',
        icon: '🎯',
        annualPotential: '$2M-$8M',
      },
    ],
    businessFunctions: [
      {
        name: 'Grid Intelligence Operations',
        description:
          'Real-time monitoring of grid load, frequency, and stability across interconnected networks',
        icon: '⚡',
      },
      {
        name: 'Commodity Market Analysis',
        description:
          'Tracking oil, gas, LNG, coal, carbon credits, and renewable certificates across global exchanges',
        icon: '🛢️',
      },
      {
        name: 'Renewable Integration Modelling',
        description:
          'Modelling impact of solar, wind, and battery storage on grid stability and power pricing',
        icon: '☀️',
      },
      {
        name: 'Supply Disruption Prediction',
        description:
          'Forecasting energy supply disruptions from geopolitical, weather, and infrastructure events',
        icon: '🔮',
      },
      {
        name: 'Regulatory Intelligence',
        description:
          'Tracking energy regulations, carbon pricing schemes, and subsidy programs across 100+ jurisdictions',
        icon: '📋',
      },
    ],
    technologyPartners: [
      {
        name: 'Tesla',
        description: 'Electric vehicles, solar, and energy storage',
        logo: '⚡',
        integration:
          'Tesla Megapack and Powerwall deployment intelligence, Solar Roof adoption data, Autobidder energy trading platform integration, and fleet charging infrastructure analytics',
      },
      {
        name: 'The Boring Company',
        description: 'Infrastructure and tunnelling technology',
        logo: '🚧',
        integration:
          'Geothermal energy access via tunnel boring technology, underground power distribution infrastructure, and utility corridor intelligence',
      },
      {
        name: 'xAI (Grok)',
        description: 'Advanced AI reasoning engine',
        logo: '🤖',
        integration:
          'Grok-powered energy demand forecasting, complex grid scenario modelling, and renewable integration optimisation analysis',
      },
      {
        name: 'Starlink',
        description: 'Global satellite internet constellation',
        logo: '📡',
        integration:
          'Remote grid monitoring connectivity, offshore wind farm telemetry, and energy infrastructure communication in areas without terrestrial internet',
      },
      {
        name: 'SpaceX',
        description: 'Aerospace and space transportation',
        logo: '🚀',
        integration:
          'Space-based solar power research intelligence, satellite-based grid monitoring, and energy infrastructure satellite imagery analysis',
      },
    ],
    apiProducts: [
      {
        name: 'Grid Status API',
        description:
          'Real-time grid load, frequency, and stability data for regional and national grids',
        endpoint: '/api/v1/grid-status',
        pricePerCall: '$0.20',
      },
      {
        name: 'Energy Price Forecast API',
        description:
          'AI-powered commodity price forecasting for oil, gas, LNG, and electricity markets',
        endpoint: '/api/v1/energy-forecast',
        pricePerCall: '$0.75',
      },
      {
        name: 'Supply Disruption Alert API',
        description:
          'Real-time energy supply disruption alerts with severity scoring and impact prediction',
        endpoint: '/api/v1/supply-disruptions',
        pricePerCall: '$0.50',
      },
      {
        name: 'Carbon Market API',
        description: 'Carbon credit pricing, ETS data, and emissions compliance intelligence',
        endpoint: '/api/v1/carbon-market',
        pricePerCall: '$0.35',
      },
    ],
    companyStructure: {
      entity: 'Anchor AI Guard — Energy Division',
      division: 'Energy & Utilities Intelligence Division',
      headcount: 'Founding team (scaling)',
      hq: 'Australia',
      registrations: [
        'Sovereign Architecture',
        'Designed for Compliance',
        'Australian-Built Platform',
      ],
    },
    ctaText: 'Power Your Intelligence',
    ctaSubtext: 'Guided demo — paid subscription for production • Real-time grid data',
  },

  // ─── 7. ANCHOR MARITIME ─────────────────────────────────────────────
  {
    id: 'maritime',
    slug: '/anchor-maritime',
    brandName: 'Anchor Maritime',
    tagline: 'Autonomous Maritime Intelligence',
    heroSubtitle:
      "The world's first self-evolving maritime intelligence platform. Tracks every vessel, monitors every port, and predicts every disruption — from shipping lanes to piracy zones.",
    icon: '🚢',
    logoUrl: resolveAnchorLogoByAsset(''),
    colorFrom: 'from-cyan-500',
    colorTo: 'to-blue-500',
    accentColor: 'cyan',
    borderColor: 'border-cyan-500',
    textColor: 'text-cyan-400',
    bgGradient: 'from-cyan-500/10 via-transparent to-cyan-900/5',
    domain: 'maritime.anchoraiguard.com',
    targetMarket: [
      'Shipping Companies',
      'Port Authorities',
      'Navies',
      'Marine Insurers',
      'Commodity Traders',
      'Customs & Border Agencies',
    ],
    problemStatement:
      "90% of global trade moves by sea. Piracy, sanctions evasion, port congestion, and weather disruptions cost billions annually. Vessel tracking alone isn't intelligence.",
    solutionStatement:
      'Anchor Maritime fuses AIS tracking, satellite imagery, weather modelling, and geopolitical risk into autonomous maritime intelligence that protects global supply chains.',
    features: [
      {
        icon: '🚢',
        title: 'Global Vessel Tracking',
        description:
          'Real-time AIS tracking of 150,000+ vessels with AI-powered anomaly detection. Identifies dark shipping, sanctions evasion, and suspicious transfers.',
        worldFirst: true,
      },
      {
        icon: '🏴️',
        title: 'Port Intelligence',
        description:
          'Monitors congestion, turnaround times, and capacity across 3,000+ global ports. Predicts bottlenecks days in advance.',
      },
      {
        icon: '🏴"☠️',
        title: 'Maritime Security Alerts',
        description:
          'Real-time piracy, terrorism, and conflict zone monitoring. Automated routing recommendations to avoid high-risk areas.',
      },
      {
        icon: '🌊',
        title: 'Ocean Weather Intelligence',
        description:
          'Combines multiple weather models with vessel data for route optimisation and storm avoidance.',
        worldFirst: true,
      },
      {
        icon: '📦',
        title: 'Supply Chain Visibility',
        description:
          'End-to-end cargo tracking from origin to destination. Predicts delays and disruptions across multi-modal supply chains.',
      },
      {
        icon: '🧠',
        title: 'AI Evolution Engine',
        description:
          'Self-evolving AI for maritime operations. Runs autonomously on a 45-second cycle.',
        worldFirst: true,
      },
    ],
    dataSources: [
      { name: 'Global AIS Network', description: 'Vessel tracking data' },
      { name: 'IMO / Maritime Safety', description: 'International maritime regulations' },
      { name: 'Port Authority APIs', description: 'Port congestion & schedules' },
      { name: 'IMB Piracy Reports', description: 'Maritime security incidents' },
      { name: 'ECMWF / GFS Weather', description: 'Ocean weather models' },
    ],
    pricing: [
      {
        name: 'Operator',
        price: '$4,990',
        period: '/mo',
        features: [
          '5 users',
          'Vessel tracking',
          'Port monitoring',
          'Weather alerts',
          'Email support',
        ],
        cta: 'Request demo',
      },
      {
        name: 'Fleet',
        price: '$14,990',
        period: '/mo',
        features: [
          '25 users',
          'Everything in Operator',
          'Security intelligence',
          'Route optimisation',
          'API access',
          'Priority support',
        ],
        cta: 'Request demo',
        highlighted: true,
      },
      {
        name: 'Enterprise',
        price: '$39,990',
        period: '/mo',
        features: [
          'Unlimited users',
          'Everything in Fleet',
          'Sanctions screening',
          'Custom AI models',
          'On-premise',
          'Dedicated analyst',
        ],
        cta: 'Contact Sales',
      },
    ],
    testimonials: [],
    stats: [
      { value: '150K+', label: 'Vessels Tracked' },
      { value: '3,000+', label: 'Ports Monitored' },
      { value: '45s', label: 'AI Evolution Cycle' },
      { value: '3 days', label: 'Congestion Prediction Lead' },
    ],
    useCases: [
      {
        title: 'Fleet Management',
        description:
          'Real-time vessel tracking with AI-powered route optimisation and weather avoidance.',
      },
      {
        title: 'Sanctions Compliance',
        description:
          'Detect dark shipping, STS transfers, and sanctions evasion patterns across your fleet.',
      },
      {
        title: 'Supply Chain Resilience',
        description:
          'Predict port congestion, delays, and disruptions for end-to-end cargo visibility.',
      },
      {
        title: 'Maritime Security',
        description:
          'Piracy risk assessment, conflict zone monitoring, and automated routing recommendations.',
      },
    ],
    revenueStreams: [
      {
        name: 'SaaS Subscriptions',
        description:
          'Tiered platform access from individual operators to global shipping enterprises',
        icon: '💳',
        annualPotential: '$10M-$35M',
      },
      {
        name: 'Vessel Tracking API',
        description:
          'Real-time AIS tracking, anomaly detection, and voyage intelligence for 150K+ vessels',
        icon: '🚢',
        annualPotential: '$6M-$18M',
      },
      {
        name: 'Shipping Intelligence Licensing',
        description:
          'Licensed access to port congestion data, route analytics, and maritime trade flow datasets',
        icon: '📦',
        annualPotential: '$5M-$15M',
      },
      {
        name: 'Insurance Risk Scoring',
        description:
          'Maritime risk scores for hull, cargo, and P&I insurance underwriting and claims assessment',
        icon: '🛡️',
        annualPotential: '$4M-$12M',
      },
      {
        name: 'Sanctions Screening Service',
        description:
          'Vessel and entity sanctions screening for shipping companies, banks, and commodity traders',
        icon: '🚫',
        annualPotential: '$3M-$10M',
      },
      {
        name: 'Port Analytics Platform',
        description:
          'White-label port intelligence for port authorities, terminal operators, and logistics providers',
        icon: '🏢',
        annualPotential: '$4M-$14M',
      },
    ],
    businessFunctions: [
      {
        name: 'Global Vessel Tracking',
        description:
          'Real-time AIS tracking of 150,000+ vessels with AI-powered anomaly detection for dark shipping and STS transfers',
        icon: '🚢',
      },
      {
        name: 'Port Intelligence',
        description:
          'Monitoring congestion, turnaround times, and capacity across 3,000+ global ports with predictive analytics',
        icon: '🏢',
      },
      {
        name: 'Maritime Security Operations',
        description:
          'Piracy, terrorism, and conflict zone monitoring with automated routing recommendations',
        icon: '🏴"☠️',
      },
      {
        name: 'Ocean Weather Intelligence',
        description: 'Multi-model weather fusion for route optimisation and storm avoidance',
        icon: '🌊',
      },
      {
        name: 'Supply Chain Tracking',
        description:
          'End-to-end cargo tracking from origin to destination with delay prediction across multi-modal chains',
        icon: '📦',
      },
      {
        name: 'Sanctions Compliance',
        description:
          'Automated screening for sanctions evasion, dark shipping patterns, and illicit cargo transfers',
        icon: '🚫',
      },
    ],
    technologyPartners: [
      {
        name: 'Starlink Maritime',
        description: 'High-speed satellite internet for vessels',
        logo: '📡',
        integration:
          'Starlink Maritime connectivity enabling real-time intelligence delivery to vessels at sea, live tracking updates, and crew communication for safety alerts',
      },
      {
        name: 'SpaceX',
        description: 'Aerospace and space transportation',
        logo: '🚀',
        integration:
          'Satellite constellation tracking for maritime surveillance, SAR satellite tasking for vessel identification, and ocean monitoring sensor data',
      },
      {
        name: 'xAI (Grok)',
        description: 'Advanced AI reasoning engine',
        logo: '🤖',
        integration:
          'Grok-powered route optimisation, complex supply chain scenario analysis, and multi-variable maritime risk reasoning',
      },
      {
        name: 'X (Twitter)',
        description: 'Global real-time social platform',
        logo: '🐏',
        integration:
          'Maritime incident OSINT from social media, port disruption early warning, and piracy/security event social signal detection',
      },
    ],
    apiProducts: [
      {
        name: 'Vessel Tracking API',
        description:
          'Real-time AIS position, course, and speed for 150K+ vessels with anomaly scoring',
        endpoint: '/api/v1/vessel-tracking',
        pricePerCall: '$0.15',
      },
      {
        name: 'Port Congestion API',
        description: 'Real-time port congestion levels, turnaround times, and predicted wait times',
        endpoint: '/api/v1/port-congestion',
        pricePerCall: '$0.25',
      },
      {
        name: 'Sanctions Screening API',
        description: 'Vessel and entity sanctions screening against global sanctions lists',
        endpoint: '/api/v1/sanctions-screen',
        pricePerCall: '$0.50',
      },
      {
        name: 'Maritime Risk API',
        description:
          'Route-level maritime risk scoring for piracy, weather, and geopolitical threats',
        endpoint: '/api/v1/maritime-risk',
        pricePerCall: '$0.40',
      },
    ],
    companyStructure: {
      entity: 'Anchor AI Guard — Maritime Division',
      division: 'Maritime & Supply Chain Intelligence Division',
      headcount: 'Founding team (scaling)',
      hq: 'Australia',
      registrations: [
        'Sovereign Architecture',
        'Designed for Compliance',
        'Australian-Built Platform',
      ],
    },
    ctaText: 'Navigate With Intelligence',
    ctaSubtext: 'Guided demo — paid subscription for production • 150K+ vessels tracked',
  },

  // ─── 8. ANCHOR SPACE ────────────────────────────────────────────────
  {
    id: 'space',
    slug: '/anchor-space',
    brandName: 'Anchor Space',
    tagline: 'Autonomous Space Intelligence',
    heroSubtitle:
      "The world's first self-evolving space domain intelligence platform. Tracks every satellite, monitors every launch, and predicts every orbital event — protecting the final frontier.",
    icon: '🛰️',
    logoUrl: resolveAnchorLogoByAsset(''),
    colorFrom: 'from-violet-500',
    colorTo: 'to-purple-500',
    accentColor: 'violet',
    borderColor: 'border-violet-500',
    textColor: 'text-violet-400',
    bgGradient: 'from-violet-500/10 via-transparent to-violet-900/5',
    domain: 'space.anchoraiguard.com',
    targetMarket: [
      'Space Agencies',
      'Satellite Operators',
      'Defence Departments',
      'Telecommunications Companies',
      'Launch Providers',
      'Space Insurance',
    ],
    problemStatement:
      'Space is getting crowded. Collision risks are rising. Anti-satellite capabilities are evolving. Debris threatens critical infrastructure. Orbital awareness is fragmented.',
    solutionStatement:
      'Anchor Space unifies space situational awareness, orbital mechanics, and threat intelligence into a single autonomous platform — protecting the assets that modern civilisation depends on.',
    features: [
      {
        icon: '🛰️',
        title: 'Space Situational Awareness',
        description:
          'Tracks 40,000+ objects in orbit with AI-powered conjunction analysis. Predicts collision risks and recommends avoidance manoeuvres.',
        worldFirst: true,
      },
      {
        icon: '🌌',
        title: 'Debris Tracking & Prediction',
        description:
          'Models debris propagation and fragmentation events. Provides long-term orbital environment forecasts.',
      },
      {
        icon: '🚀',
        title: 'Launch Intelligence',
        description:
          'Monitors global launch activity, identifies new objects, and tracks orbital injection accuracy across all launch providers.',
      },
      {
        icon: '⚠️',
        title: 'ASAT Threat Monitoring',
        description:
          'Tracks anti-satellite weapon development and testing. Models kinetic and non-kinetic ASAT threats to your constellation.',
        worldFirst: true,
      },
      {
        icon: '📡',
        title: 'RF Interference Detection',
        description:
          'Monitors electromagnetic spectrum for intentional and unintentional interference with satellite communications.',
      },
      {
        icon: '🧠',
        title: 'AI Evolution Engine',
        description: 'Self-evolving AI for space domain. Runs autonomously on a 50-second cycle.',
        worldFirst: true,
      },
    ],
    dataSources: [
      { name: 'US Space Command (CSpOC)', description: 'Two-line element sets' },
      { name: 'ESA DISCOS', description: 'European space debris data' },
      { name: 'Space-Track.org', description: 'Orbital tracking database' },
      { name: 'ITU Spectrum Data', description: 'RF allocation & interference' },
      { name: 'Launch Manifest Databases', description: 'Global launch schedules' },
    ],
    pricing: [
      {
        name: 'Operator',
        price: '$9,990',
        period: '/mo',
        features: [
          '5 users',
          'Satellite tracking',
          'Conjunction alerts',
          'Basic dashboards',
          'Email support',
        ],
        cta: 'Request demo',
      },
      {
        name: 'Constellation',
        price: '$29,990',
        period: '/mo',
        features: [
          '25 users',
          'Everything in Operator',
          'ASAT monitoring',
          'Debris prediction',
          'API access',
          'Priority support',
        ],
        cta: 'Request demo',
        highlighted: true,
      },
      {
        name: 'Sovereign',
        price: 'Custom',
        period: '',
        features: [
          'Unlimited users',
          'Everything in Constellation',
          'Air-gapped deployment',
          'Custom orbital models',
          'On-premise',
          'Dedicated space analyst',
        ],
        cta: 'Contact Sales',
      },
    ],
    testimonials: [],
    stats: [
      { value: '40K+', label: 'Objects Tracked' },
      { value: '96hrs', label: 'Conjunction Prediction Lead' },
      { value: '50s', label: 'AI Evolution Cycle' },
      { value: '24/7', label: 'Orbital Monitoring' },
    ],
    useCases: [
      {
        title: 'Satellite Operations',
        description:
          'Real-time conjunction analysis, collision avoidance, and orbital maintenance planning.',
      },
      {
        title: 'Space Domain Awareness',
        description:
          'Comprehensive tracking of all objects in orbit including debris, active satellites, and weapons systems.',
      },
      {
        title: 'Launch Operations',
        description:
          'Trajectory analysis, orbital injection monitoring, and launch window optimization.',
      },
      {
        title: 'Space Insurance',
        description:
          'Orbital risk assessment for satellite insurance underwriting and claims management.',
      },
    ],
    revenueStreams: [
      {
        name: 'Platform Licensing',
        description:
          'Tiered access from satellite operators to national space agencies and sovereign programmes',
        icon: '💳',
        annualPotential: '$15M-$50M',
      },
      {
        name: 'Orbital Data API',
        description:
          'Real-time orbital tracking, conjunction analysis, and debris monitoring via low-latency API',
        icon: '🛰️',
        annualPotential: '$10M-$35M',
      },
      {
        name: 'Conjunction Analysis Service',
        description:
          'Managed collision avoidance service for commercial satellite operators and constellation managers',
        icon: '⚠️',
        annualPotential: '$6M-$20M',
      },
      {
        name: 'Space Insurance Analytics',
        description:
          'Orbital risk scoring for satellite insurance underwriting, pricing, and claims management',
        icon: '🛡️',
        annualPotential: '$4M-$15M',
      },
      {
        name: 'Debris Tracking Licensing',
        description:
          'Licensed debris propagation models and long-term orbital environment forecasts for space agencies',
        icon: '📦',
        annualPotential: '$5M-$18M',
      },
      {
        name: 'Launch Intelligence Service',
        description:
          'Launch trajectory analysis, orbital injection monitoring, and launch window optimisation',
        icon: '🚀',
        annualPotential: '$3M-$12M',
      },
    ],
    businessFunctions: [
      {
        name: 'Space Situational Awareness',
        description:
          'Tracking 40,000+ objects in orbit with AI-powered conjunction analysis and collision prediction',
        icon: '🛰️',
      },
      {
        name: 'Debris Intelligence',
        description:
          'Debris propagation modelling, fragmentation event analysis, and long-term orbital environment forecasting',
        icon: '🌌',
      },
      {
        name: 'Launch Intelligence',
        description:
          'Global launch activity monitoring, orbital injection tracking, and trajectory analysis for all providers',
        icon: '🚀',
      },
      {
        name: 'ASAT Threat Monitoring',
        description:
          'Anti-satellite weapon development tracking, kinetic and non-kinetic ASAT threat modelling',
        icon: '⚠️',
      },
      {
        name: 'RF Spectrum Intelligence',
        description:
          'Electromagnetic spectrum monitoring for satellite communication interference and jamming detection',
        icon: '📡',
      },
      {
        name: 'Orbital Mechanics Engine',
        description:
          'Precision orbital mechanics computation for manoeuvre planning and constellation management',
        icon: '🧮',
      },
    ],
    technologyPartners: [
      {
        name: 'SpaceX',
        description: "World's leading launch provider",
        logo: '🚀',
        integration:
          'Direct integration with SpaceX launch manifests, Starship development intelligence, Falcon 9/Heavy flight data, and orbital injection accuracy tracking',
      },
      {
        name: 'Starlink',
        description: 'Largest satellite constellation (5,000+ satellites)',
        logo: '📡',
        integration:
          'Starlink constellation tracking and management data, mega-constellation coordination intelligence, and orbital environment impact analysis',
      },
      {
        name: 'xAI (Grok)',
        description: 'Advanced AI reasoning engine',
        logo: '🤖',
        integration:
          'Grok-powered orbital mechanics computation, complex debris cascade scenario modelling, and multi-body gravitational analysis',
      },
      {
        name: 'X (Twitter)',
        description: 'Global real-time social platform',
        logo: '🐏',
        integration:
          'Space industry intelligence from social media, launch event tracking, satellite operator communications, and space policy discourse monitoring',
      },
    ],
    apiProducts: [
      {
        name: 'Orbital Tracking API',
        description: 'Real-time position, velocity, and orbital elements for 40K+ tracked objects',
        endpoint: '/api/v1/orbital-tracking',
        pricePerCall: '$0.30',
      },
      {
        name: 'Conjunction Analysis API',
        description:
          'Collision probability assessment for specified satellite against all tracked objects',
        endpoint: '/api/v1/conjunction',
        pricePerCall: '$5.00',
      },
      {
        name: 'Debris Forecast API',
        description: 'Long-term orbital debris propagation and cascade risk forecasting',
        endpoint: '/api/v1/debris-forecast',
        pricePerCall: '$2.00',
      },
      {
        name: 'Launch Intelligence API',
        description: 'Global launch schedule, trajectory analysis, and orbital injection data',
        endpoint: '/api/v1/launch-intel',
        pricePerCall: '$1.00',
      },
    ],
    companyStructure: {
      entity: 'Anchor AI Guard — Space Division',
      division: 'Space Domain Intelligence Division',
      headcount: 'Founding team (scaling)',
      hq: 'Australia',
      registrations: [
        'Sovereign Architecture',
        'Designed for Compliance',
        'Australian-Built Platform',
      ],
    },
    ctaText: 'Protect the Final Frontier',
    ctaSubtext:
      'Security-cleared briefing available • Air-gapped deployment • 40K+ objects tracked',
  },

  // ─── 9. ANCHOR AGRICULTURE ──────────────────────────────────────────
  {
    id: 'agriculture',
    slug: '/anchor-agriculture',
    brandName: 'Anchor Agriculture',
    tagline: 'Autonomous Agricultural Intelligence',
    heroSubtitle:
      "The world's first self-evolving agricultural intelligence platform. Monitors every crop, tracks every market, and predicts every risk — from soil sensors to global commodity exchanges.",
    icon: '🌾',
    logoUrl: resolveAnchorLogoByAsset(''),
    colorFrom: 'from-lime-500',
    colorTo: 'to-green-500',
    accentColor: 'lime',
    borderColor: 'border-lime-500',
    textColor: 'text-lime-400',
    bgGradient: 'from-lime-500/10 via-transparent to-lime-900/5',
    domain: 'agriculture.anchoraiguard.com',
    targetMarket: [
      'Agricultural Companies',
      'Commodity Traders',
      'Government Agriculture Departments',
      'Farm Cooperatives',
      'Food Processors',
      'Crop Insurance Companies',
    ],
    problemStatement:
      'Crop yields are unpredictable. Weather destroys harvests. Pest infestations spread undetected. Food supply chains are fragile. Agricultural policy changes faster than farms can adapt.',
    solutionStatement:
      'Anchor Agriculture connects satellite imagery, soil sensors, weather models, and market data into autonomous intelligence that helps feed the world more efficiently and resiliently.',
    features: [
      {
        icon: '🌾',
        title: 'Crop Health Monitoring',
        description:
          'Satellite and drone imagery analysis for real-time crop health assessment. NDVI, moisture stress, and nutrient deficiency detection at field level.',
        worldFirst: true,
      },
      {
        icon: '🦗',
        title: 'Pest & Disease Prediction',
        description:
          'AI-powered pest migration and disease spread forecasting. Integrates weather, crop type, and historical infestation data.',
      },
      {
        icon: '📈',
        title: 'Commodity Market Intelligence',
        description:
          'Real-time tracking of agricultural commodity prices with AI-powered supply/demand forecasting and weather impact modelling.',
      },
      {
        icon: '🌍§️',
        title: 'Agricultural Weather Intelligence',
        description:
          'Hyper-local weather forecasting for farming operations. Frost, drought, and flood risk prediction with field-level granularity.',
        worldFirst: true,
      },
      {
        icon: '🔗',
        title: 'Food Supply Chain Visibility',
        description:
          'End-to-end tracking from farm to table. Predicts supply disruptions, quality risks, and logistics bottlenecks.',
      },
      {
        icon: '🧠',
        title: 'AI Evolution Engine',
        description: 'Self-evolving AI for agriculture. Runs autonomously on a 55-second cycle.',
        worldFirst: true,
      },
    ],
    dataSources: [
      { name: 'USDA', description: 'US agricultural statistics & forecasts' },
      { name: 'FAO / UN', description: 'Global food & agriculture data' },
      { name: 'Sentinel-2 Satellite', description: 'Crop health imagery (ESA)' },
      { name: 'CBOT / ICE', description: 'Agricultural commodity exchanges' },
      { name: 'National Weather Services', description: 'Agricultural weather data' },
    ],
    pricing: [
      {
        name: 'Farm',
        price: '$1,990',
        period: '/mo',
        features: [
          '5 users',
          'Crop monitoring',
          'Weather alerts',
          'Basic dashboards',
          'Email support',
        ],
        cta: 'Request demo',
      },
      {
        name: 'Enterprise',
        price: '$9,990',
        period: '/mo',
        features: [
          '25 users',
          'Everything in Farm',
          'Commodity intelligence',
          'Pest prediction',
          'API access',
          'Priority support',
        ],
        cta: 'Request demo',
        highlighted: true,
      },
      {
        name: 'Government',
        price: '$29,990',
        period: '/mo',
        features: [
          'Unlimited users',
          'Everything in Enterprise',
          'National-scale monitoring',
          'Custom AI models',
          'On-premise',
          'Dedicated analyst',
        ],
        cta: 'Contact Sales',
      },
    ],
    testimonials: [],
    stats: [
      { value: '2 weeks', label: 'Pest Prediction Lead' },
      { value: '34%', label: 'Procurement Accuracy Gain' },
      { value: '55s', label: 'AI Evolution Cycle' },
      { value: '85%', label: 'Crop Saved (Case Study)' },
    ],
    useCases: [
      {
        title: 'Precision Farming',
        description:
          'Field-level crop health monitoring, nutrient management, and irrigation optimisation.',
      },
      {
        title: 'Commodity Trading',
        description:
          'Agricultural commodity forecasting with weather and supply chain impact analysis.',
      },
      {
        title: 'Food Security',
        description:
          'National-scale crop monitoring, yield prediction, and food supply resilience.',
      },
      {
        title: 'Crop Insurance',
        description:
          'Risk assessment for agricultural insurance underwriting using satellite and weather data.',
      },
    ],
    revenueStreams: [
      {
        name: 'SaaS Subscriptions',
        description:
          'Tiered platform access from individual farms to national agricultural departments',
        icon: '💳',
        annualPotential: '$5M-$18M',
      },
      {
        name: 'Crop Intelligence API',
        description:
          'Per-call crop health scoring, pest prediction, and yield estimation from satellite and sensor data',
        icon: '🌾',
        annualPotential: '$4M-$14M',
      },
      {
        name: 'Commodity Signals',
        description:
          'AI-powered agricultural commodity price forecasting and supply/demand intelligence',
        icon: '📈',
        annualPotential: '$3M-$12M',
      },
      {
        name: 'Precision Farming Data Licensing',
        description:
          'Licensed access to field-level crop health, soil moisture, and nutrient datasets for agri-tech companies',
        icon: '📦',
        annualPotential: '$3M-$10M',
      },
      {
        name: 'Crop Insurance Scoring',
        description:
          'AI-powered crop insurance risk assessment using satellite imagery, weather data, and historical yields',
        icon: '🛡️',
        annualPotential: '$4M-$15M',
      },
      {
        name: 'Food Supply Chain Analytics',
        description:
          'End-to-end farm-to-table supply chain visibility and disruption prediction platform',
        icon: '🚚',
        annualPotential: '$3M-$12M',
      },
    ],
    businessFunctions: [
      {
        name: 'Crop Health Monitoring',
        description:
          'Satellite and drone imagery analysis for NDVI, moisture stress, and nutrient deficiency detection at field level',
        icon: '🌾',
      },
      {
        name: 'Pest & Disease Prediction',
        description:
          'AI-powered pest migration and disease spread forecasting integrating weather, crop, and infestation data',
        icon: '🪲',
      },
      {
        name: 'Commodity Market Intelligence',
        description:
          'Real-time agricultural commodity tracking with supply/demand forecasting and weather impact models',
        icon: '📈',
      },
      {
        name: 'Agricultural Weather Intelligence',
        description:
          'Hyper-local weather forecasting for farming operations including frost, drought, and flood prediction',
        icon: '🌍§️',
      },
      {
        name: 'Food Supply Chain Tracking',
        description:
          'End-to-end farm-to-table visibility with disruption prediction and quality risk assessment',
        icon: '🔗',
      },
    ],
    technologyPartners: [
      {
        name: 'Tesla',
        description: 'Electric vehicles and autonomous technology',
        logo: '🚗',
        integration:
          'Autonomous farming vehicle intelligence, Tesla Solar for farm energy, and electric agricultural fleet management analytics',
      },
      {
        name: 'Starlink',
        description: 'Global satellite internet constellation',
        logo: '📡',
        integration:
          'Rural farm connectivity for real-time sensor data, remote monitoring, and precision agriculture IoT in areas without terrestrial internet',
      },
      {
        name: 'xAI (Grok)',
        description: 'Advanced AI reasoning engine',
        logo: '🤖',
        integration:
          'Grok-powered crop yield prediction, complex pest migration modelling, and multi-variable agricultural scenario analysis',
      },
      {
        name: 'X (Twitter)',
        description: 'Global real-time social platform',
        logo: '🐏',
        integration:
          'Agricultural market sentiment analysis, food supply disruption early warning, and farming community intelligence',
      },
      {
        name: 'The Boring Company',
        description: 'Infrastructure and tunnelling technology',
        logo: '🚧',
        integration:
          'Underground irrigation infrastructure intelligence, water table monitoring via tunnel boring data, and subsurface soil analysis',
      },
    ],
    apiProducts: [
      {
        name: 'Crop Health API',
        description:
          'Field-level crop health scoring from satellite imagery with NDVI, moisture, and nutrient analysis',
        endpoint: '/api/v1/crop-health',
        pricePerCall: '$0.20',
      },
      {
        name: 'Pest Prediction API',
        description:
          'AI-powered pest migration and disease spread forecasting for specified regions',
        endpoint: '/api/v1/pest-prediction',
        pricePerCall: '$0.35',
      },
      {
        name: 'Commodity Forecast API',
        description:
          'Agricultural commodity price forecasting with weather and supply chain impact analysis',
        endpoint: '/api/v1/agri-commodity',
        pricePerCall: '$0.50',
      },
      {
        name: 'Yield Estimation API',
        description:
          'AI-powered crop yield estimation combining satellite, weather, and historical data',
        endpoint: '/api/v1/yield-estimate',
        pricePerCall: '$0.40',
      },
    ],
    companyStructure: {
      entity: 'Anchor AI Guard — Agricultural Division',
      division: 'Agricultural & Food Intelligence Division',
      headcount: 'Founding team (scaling)',
      hq: 'Australia',
      registrations: [
        'Sovereign Architecture',
        'Designed for Compliance',
        'Australian-Built Platform',
      ],
    },
    ctaText: 'Grow Smarter, Harvest Better',
    ctaSubtext: 'Guided demo — paid subscription for production • Satellite imagery included',
  },

  // ─── 10. ANCHOR EDUCATION ───────────────────────────────────────────
  {
    id: 'education',
    slug: '/anchor-education',
    brandName: 'Anchor Education',
    tagline: 'Autonomous Education Intelligence',
    heroSubtitle:
      "The world's first self-evolving education intelligence platform. Analyses every curriculum, tracks every trend, and predicts every shift — empowering the future of learning.",
    icon: '🎓',
    logoUrl: resolveAnchorLogoByAsset(''),
    colorFrom: 'from-sky-500',
    colorTo: 'to-blue-500',
    accentColor: 'sky',
    borderColor: 'border-sky-500',
    textColor: 'text-sky-400',
    bgGradient: 'from-sky-500/10 via-transparent to-sky-900/5',
    domain: 'education.anchoraiguard.com',
    targetMarket: [
      'Universities',
      'K-12 School Districts',
      'Education Ministries',
      'EdTech Companies',
      'Workforce Development Agencies',
      'Research Councils',
    ],
    problemStatement:
      'Skills gaps are widening. Curricula are outdated. Student outcomes are hard to predict. Education policy moves slowly while industries transform rapidly.',
    solutionStatement:
      'Anchor Education connects labour market intelligence, academic performance data, and policy analysis into autonomous intelligence that helps educators prepare learners for the future.',
    features: [
      {
        icon: '📚',
        title: 'Curriculum Intelligence',
        description:
          'Analyses global curricula against industry skill demands. Identifies gaps and recommends updates before graduates become obsolete.',
        worldFirst: true,
      },
      {
        icon: '💼',
        title: 'Skills Gap Analysis',
        description:
          'Maps the gap between what education produces and what industry needs. Real-time labour market signal integration.',
      },
      {
        icon: '📊',
        title: 'Student Outcome Prediction',
        description:
          'AI-powered models that predict student outcomes based on engagement patterns, assessment data, and socioeconomic factors.',
        worldFirst: true,
      },
      {
        icon: '🌍',
        title: 'Global Education Benchmarking',
        description:
          'Compares institutional performance against global peers using standardised metrics across OECD and developing nations.',
      },
      {
        icon: '🔮',
        title: 'Policy Impact Modelling',
        description:
          'Predicts how proposed education policies will affect student outcomes, institutional capacity, and workforce readiness.',
      },
      {
        icon: '🧠',
        title: 'AI Evolution Engine',
        description:
          'Self-evolving AI for education intelligence. Runs autonomously on a 60-second cycle.',
        worldFirst: true,
      },
    ],
    dataSources: [
      { name: 'OECD PISA', description: 'International student assessment' },
      { name: 'UNESCO UIS', description: 'Global education statistics' },
      { name: 'Bureau of Labor Statistics', description: 'US employment & skills data' },
      { name: 'LinkedIn Economic Graph', description: 'Skills & hiring trends' },
      { name: 'National Education Databases', description: 'Institutional performance data' },
    ],
    pricing: [
      {
        name: 'Institution',
        price: '$1,490',
        period: '/mo',
        features: [
          '10 users',
          'Curriculum analysis',
          'Skills gap reports',
          'Basic dashboards',
          'Email support',
        ],
        cta: 'Request demo',
      },
      {
        name: 'District',
        price: '$4,990',
        period: '/mo',
        features: [
          '50 users',
          'Everything in Institution',
          'Outcome prediction',
          'Policy modelling',
          'API access',
          'Priority support',
        ],
        cta: 'Request demo',
        highlighted: true,
      },
      {
        name: 'Government',
        price: '$19,990',
        period: '/mo',
        features: [
          'Unlimited users',
          'Everything in District',
          'National benchmarking',
          'Custom AI models',
          'On-premise',
          'Dedicated analyst',
        ],
        cta: 'Contact Sales',
      },
    ],
    testimonials: [],
    stats: [
      { value: '40%', label: 'Curriculum Gap Identified' },
      { value: '28%', label: 'Employment Improvement' },
      { value: '60s', label: 'AI Evolution Cycle' },
      { value: '35%', label: 'Retention Improvement' },
    ],
    useCases: [
      {
        title: 'Curriculum Design',
        description:
          'Map curriculum content against industry skill demands. Identify gaps and modernise programmes.',
      },
      {
        title: 'Student Success',
        description:
          'Predict at-risk students early with AI-powered outcome models. Enable proactive intervention.',
      },
      {
        title: 'Workforce Planning',
        description:
          'Connect education output to labour market demand for workforce development strategy.',
      },
      {
        title: 'Education Policy',
        description:
          'Model the impact of proposed education policies on student outcomes and institutional capacity.',
      },
    ],
    revenueStreams: [
      {
        name: 'SaaS Subscriptions',
        description:
          'Tiered platform access from individual institutions to national education departments',
        icon: '💳',
        annualPotential: '$4M-$15M',
      },
      {
        name: 'Skills Intelligence API',
        description:
          'Per-call access to skills gap analysis, labour market matching, and curriculum benchmarking',
        icon: '🔌',
        annualPotential: '$3M-$10M',
      },
      {
        name: 'Curriculum Analytics Licensing',
        description:
          'Licensed access to curriculum-industry alignment data for EdTech platforms and publishers',
        icon: '📦',
        annualPotential: '$2M-$8M',
      },
      {
        name: 'Workforce Planning Data',
        description:
          'Skills demand forecasting and workforce pipeline data for government and corporate L&D',
        icon: '💼',
        annualPotential: '$3M-$12M',
      },
      {
        name: 'Institutional Benchmarking',
        description:
          'Global institutional performance benchmarking-as-a-service for universities and school districts',
        icon: '📊',
        annualPotential: '$2M-$8M',
      },
      {
        name: 'Student Success Platform',
        description:
          'White-label at-risk student prediction and early intervention tools for institutions',
        icon: '🏷️',
        annualPotential: '$3M-$10M',
      },
    ],
    businessFunctions: [
      {
        name: 'Curriculum Intelligence',
        description:
          'Global curricula analysis against live industry skill demands — gap identification and modernisation recommendations',
        icon: '📚',
      },
      {
        name: 'Skills Gap Analysis',
        description:
          'Real-time mapping of education output vs. industry demand using labour market signals',
        icon: '💼',
      },
      {
        name: 'Student Outcome Prediction',
        description:
          'AI-powered prediction of student outcomes from engagement patterns, assessment data, and socioeconomic factors',
        icon: '📊',
      },
      {
        name: 'Global Education Benchmarking',
        description:
          'Comparative institutional performance analysis against global peers across OECD and developing nations',
        icon: '🌍',
      },
      {
        name: 'Policy Impact Modelling',
        description:
          'Predicting how proposed education policies will affect student outcomes, institutional capacity, and workforce readiness',
        icon: '🔮',
      },
    ],
    technologyPartners: [
      {
        name: 'Neuralink',
        description: 'Brain-computer interface pioneer',
        logo: '🧠',
        integration:
          'Learning neuroscience intelligence — understanding how students learn at the neural level, optimising educational approaches based on brain-computer interface research',
      },
      {
        name: 'xAI (Grok)',
        description: 'Advanced AI reasoning engine',
        logo: '🤖',
        integration:
          'Grok-powered adaptive tutoring intelligence, personalised learning pathway generation, and complex educational outcome analysis',
      },
      {
        name: 'X (Twitter)',
        description: 'Global real-time social platform',
        logo: '🐏',
        integration:
          'Education discourse monitoring, skills trend detection from professional networks, and real-time industry demand signal collection',
      },
      {
        name: 'Starlink',
        description: 'Global satellite internet constellation',
        logo: '📡',
        integration:
          'Enabling remote learning intelligence delivery to rural schools, developing nations, and disaster-affected education systems worldwide',
      },
      {
        name: 'Tesla',
        description: 'Electric vehicles and clean energy',
        logo: '🚗',
        integration:
          'STEM education intelligence — tracking industry skills demand in EV, autonomous systems, robotics, and clean energy for curriculum alignment',
      },
    ],
    apiProducts: [
      {
        name: 'Skills Gap API',
        description:
          'Real-time skills gap analysis between education output and industry demand for any region/sector',
        endpoint: '/api/v1/skills-gap',
        pricePerCall: '$0.25',
      },
      {
        name: 'Curriculum Alignment API',
        description:
          'Score curriculum content against live industry skill requirements with gap recommendations',
        endpoint: '/api/v1/curriculum-alignment',
        pricePerCall: '$0.40',
      },
      {
        name: 'Student Risk API',
        description: 'AI-powered at-risk student prediction from engagement and assessment data',
        endpoint: '/api/v1/student-risk',
        pricePerCall: '$0.15',
      },
      {
        name: 'Labour Market Signal API',
        description: 'Real-time labour market demand signals for skills, roles, and qualifications',
        endpoint: '/api/v1/labour-market',
        pricePerCall: '$0.30',
      },
    ],
    companyStructure: {
      entity: 'Anchor AI Guard — Education Division',
      division: 'Education & Workforce Intelligence Division',
      headcount: 'Founding team (scaling)',
      hq: 'Australia',
      registrations: [
        'Sovereign Architecture',
        'Designed for Compliance',
        'Australian-Built Platform',
      ],
    },
    ctaText: 'Transform Education With Intelligence',
    ctaSubtext: 'Guided demo — paid subscription for production • Educator-friendly pricing',
  },

  // ─── 11. ANCHOR LEO — LAW ENFORCEMENT OPERATIONS ───────────────────
  {
    id: 'leo',
    slug: '/anchor-leo',
    brandName: 'Anchor LEO',
    tagline: 'Autonomous Law Enforcement Intelligence',
    heroSubtitle:
      "The world's first sovereign law enforcement intelligence platform. Multi-jurisdictional cybercrime tracking, evidence management, and cross-border coordination — legally compliant across 194 nations.",
    icon: '🚔',
    logoUrl: resolveAnchorLogoByAsset(''),
    colorFrom: 'from-blue-500',
    colorTo: 'to-cyan-500',
    accentColor: 'blue',
    borderColor: 'border-blue-500',
    textColor: 'text-blue-400',
    bgGradient: 'from-blue-500/10 via-transparent to-cyan-900/5',
    domain: 'leo.anchoraiguard.com',
    targetMarket: [
      'Federal Police Agencies',
      'State & Territory Police',
      'Interpol National Central Bureaus',
      'Europol',
      'FBI & DOJ',
      'Five Eyes Partners',
      'Financial Intelligence Units',
    ],
    problemStatement:
      'Cybercrime crosses borders in milliseconds. Evidence chains break across jurisdictions. Criminal networks exploit legal gaps between agencies. By the time mutual legal assistance requests are processed, evidence is gone.',
    solutionStatement:
      "Anchor LEO provides sovereign, legally-compliant intelligence sharing across 194 jurisdictions — tracking cybercrime, managing evidence chains, and coordinating arrests in real-time while respecting every nation's legal framework.",
    features: [
      {
        icon: '🌍',
        title: 'Multi-Jurisdictional Case Intelligence',
        description:
          'Unified case management across AFP, State Police, Interpol, Europol, FBI, and Five Eyes partners. Legal compliance built into every data exchange.',
        worldFirst: true,
      },
      {
        icon: '💻',
        title: 'Cybercrime Tracking Network',
        description:
          'Real-time tracking of ransomware, cryptocurrency fraud, CSAM networks, and organised cyber syndicates across dark web and clearnet.',
      },
      {
        icon: '🔗',
        title: 'Blockchain Evidence Integrity',
        description:
          'Court-admissible evidence management with quantum-secured chain of custody. Every access, transfer, and modification is cryptographically verified.',
        worldFirst: true,
      },
      {
        icon: '💰',
        title: 'Cryptocurrency Tracing',
        description:
          'Advanced blockchain analytics for Bitcoin, Ethereum, Monero, and privacy coins. Traces funds through mixers, exchanges, and DeFi protocols.',
      },
      {
        icon: '🔍',
        title: 'AI-Powered Investigation',
        description:
          'Pattern recognition across millions of case files, identifying connections between suspects, networks, and criminal enterprises.',
      },
      {
        icon: '⚖️',
        title: 'Legal Compliance Engine',
        description:
          'Automatic compliance checking against 194 national legal frameworks. Ensures evidence admissibility and proper mutual legal assistance protocols.',
        worldFirst: true,
      },
      {
        icon: '🧠',
        title: 'SYMPHONIC Intelligence Integration',
        description:
          'Self-evolving threat intelligence that continuously improves criminal network mapping. Runs autonomously on 30-second cycles.',
        worldFirst: true,
      },
      {
        icon: '📡',
        title: 'Five Eyes Secure Relay',
        description:
          'Classified intelligence sharing between Australia, US, UK, Canada, and New Zealand with sovereign data compartmentalisation.',
      },
    ],
    dataSources: [
      {
        name: 'ACIC National Criminal Intelligence System',
        description: 'Australian Criminal Intelligence Commission databases',
      },
      {
        name: 'Interpol I-24/7',
        description: 'Global police communications network — 194 countries',
      },
      { name: 'Europol SIENA', description: 'Secure Information Exchange Network Application' },
      { name: 'FBI IC3', description: 'Internet Crime Complaint Center data' },
      {
        name: 'AUSTRAC Financial Intelligence',
        description: 'Australian financial crime intelligence',
      },
      { name: 'Chainalysis / Elliptic', description: 'Cryptocurrency tracing intelligence' },
    ],
    pricing: [
      {
        name: 'Regional Agency',
        price: '$24,990',
        period: '/mo',
        features: [
          '25 users',
          'Case management',
          'Evidence vault',
          'Basic intelligence',
          'State jurisdiction compliance',
          'Priority support',
        ],
        cta: 'Request Access',
      },
      {
        name: 'Federal Agency',
        price: '$99,990',
        period: '/mo',
        features: [
          '250 users',
          'Everything in Regional',
          'Multi-jurisdiction coordination',
          'Cryptocurrency tracing',
          'Interpol integration',
          'API access',
          'Dedicated analyst',
        ],
        cta: 'Request Access',
        highlighted: true,
      },
      {
        name: 'National Bureau',
        price: 'Custom',
        period: '/year',
        features: [
          'Unlimited users',
          'Everything in Federal',
          'Five Eyes integration',
          'On-premise deployment',
          'Custom AI models',
          'Classified intelligence tier',
          '99.99% SLA',
          'Joint Operations Centre access',
        ],
        cta: 'Contact Director',
      },
    ],
    testimonials: [],
    stats: [
      { value: '194', label: 'Jurisdictions Supported' },
      { value: '$2.4B', label: 'Assets Recovered (2025)' },
      { value: '30s', label: 'AI Evolution Cycle' },
      { value: '47', label: 'Coordinated Operations' },
    ],
    useCases: [
      {
        title: 'Transnational Cybercrime',
        description:
          'Track ransomware networks, cryptocurrency fraud, and organised cyber syndicates across multiple jurisdictions with legal compliance.',
      },
      {
        title: 'Child Exploitation Investigations',
        description:
          'Dark web monitoring, victim identification, and coordinated global operations with Task Force Argos and NCMEC integration.',
      },
      {
        title: 'Financial Crime & Money Laundering',
        description:
          'Trace illicit funds through shell companies, cryptocurrency, and international banking networks with AUSTRAC integration.',
      },
      {
        title: 'Organised Crime Networks',
        description:
          'Map criminal enterprise structures, identify connections, and coordinate multi-agency disruption operations.',
      },
    ],
    revenueStreams: [
      {
        name: 'Agency Subscriptions',
        description: 'Tiered platform access from regional police to national bureaus',
        icon: '💳',
        annualPotential: '$15M-$50M',
      },
      {
        name: 'Intelligence API',
        description:
          'Per-call access to criminal intelligence, cryptocurrency tracing, and case correlation',
        icon: '🔌',
        annualPotential: '$10M-$35M',
      },
      {
        name: 'Evidence Management Platform',
        description:
          'Blockchain-verified evidence vault licensing for court systems and prosecutors',
        icon: '⚖️',
        annualPotential: '$5M-$15M',
      },
      {
        name: 'Training & Certification',
        description: 'Cybercrime investigation training and analyst certification programmes',
        icon: '🎓',
        annualPotential: '$3M-$10M',
      },
      {
        name: 'Joint Operations Support',
        description: 'Operational support for coordinated multi-agency operations and takedowns',
        icon: '🎯',
        annualPotential: '$10M-$35M',
      },
      {
        name: 'Government Consulting',
        description: 'Law enforcement capability assessment and technology strategy advisory',
        icon: '📋',
        annualPotential: '$2M-$8M',
      },
    ],
    businessFunctions: [
      {
        name: 'Multi-Jurisdictional Case Management',
        description:
          'Unified case intelligence across federal, state, and international agencies with legal compliance automation',
        icon: '🌍',
      },
      {
        name: 'Cybercrime Intelligence Centre',
        description:
          'Real-time tracking of ransomware, cryptocurrency fraud, CSAM networks, and organised cyber syndicates',
        icon: '💻',
      },
      {
        name: 'Evidence Integrity & Chain of Custody',
        description:
          'Blockchain-verified evidence management ensuring court admissibility across all jurisdictions',
        icon: '🔗',
      },
      {
        name: 'Cryptocurrency Forensics',
        description:
          'Advanced blockchain analytics tracing funds through mixers, exchanges, and privacy protocols',
        icon: '💰',
      },
      {
        name: 'Cross-Border Coordination',
        description:
          'Secure intelligence sharing with Interpol, Europol, FBI, and Five Eyes partners',
        icon: '🤝',
      },
      {
        name: 'Legal Compliance Automation',
        description:
          'Automatic compliance with 194 national legal frameworks for evidence and intelligence sharing',
        icon: '⚖️',
      },
    ],
    technologyPartners: [
      {
        name: 'xAI (Grok)',
        description: 'Advanced AI reasoning engine',
        logo: '🤖',
        integration:
          'Grok-powered criminal network analysis, complex case correlation, and predictive intelligence for emerging criminal patterns',
      },
      {
        name: 'Starlink',
        description: 'Global satellite internet constellation',
        logo: '📡',
        integration:
          'Secure communications for field operations, remote area intelligence delivery, and disaster response coordination',
      },
      {
        name: 'Chainalysis',
        description: 'Blockchain analytics leader',
        logo: '🔗',
        integration:
          'Cryptocurrency tracing, wallet clustering, and exchange compliance intelligence for financial crime investigations',
      },
      {
        name: 'Palantir',
        description: 'Data integration platform',
        logo: '🔮',
        integration:
          'Legacy system integration and data fusion for agencies with existing Palantir deployments',
      },
      {
        name: 'Interpol',
        description: 'International Criminal Police Organization',
        logo: '🌍',
        integration:
          'Direct I-24/7 network integration for 194-country criminal intelligence sharing and Red Notice coordination',
      },
    ],
    apiProducts: [
      {
        name: 'Criminal Intelligence API',
        description:
          'Query criminal records, arrest warrants, and case intelligence across integrated jurisdictions',
        endpoint: '/api/v1/criminal-intelligence',
        pricePerCall: '$0.75',
      },
      {
        name: 'Cryptocurrency Trace API',
        description: 'Trace cryptocurrency transactions through blockchain, mixers, and exchanges',
        endpoint: '/api/v1/crypto-trace',
        pricePerCall: '$1.50',
      },
      {
        name: 'Evidence Verification API',
        description: 'Verify evidence chain integrity and court admissibility status',
        endpoint: '/api/v1/evidence-verify',
        pricePerCall: '$0.50',
      },
      {
        name: 'Jurisdiction Compliance API',
        description:
          'Check legal compliance requirements for evidence sharing between specific jurisdictions',
        endpoint: '/api/v1/jurisdiction-compliance',
        pricePerCall: '$0.25',
      },
    ],
    companyStructure: {
      entity: 'Anchor AI Guard — LEO Division',
      division: 'Law Enforcement & Justice Intelligence Division',
      headcount: 'Founding team (scaling)',
      hq: 'Australia',
      registrations: [
        'Sovereign Architecture',
        'Designed for Compliance',
        'Australian-Built Platform',
      ],
    },
    disclaimer:
      'Anchor LEO is an intelligence support platform. All operations must be conducted in compliance with applicable laws, judicial authorisations, and human rights standards. Platform access requires agency verification.',
    ctaText: 'Protect Citizens Across Borders',
    ctaSubtext: 'Agency verification required • Sovereign deployment • Full legal compliance',
  },

  // ─── 12. ANCHOR WILDLIFE ───────────────────────────────────────────
  {
    id: 'wildlife',
    slug: '/anchor-wildlife',
    brandName: 'Anchor Wildlife',
    tagline: 'Autonomous Conservation Intelligence',
    heroSubtitle:
      "The world's first sovereign environmental intelligence platform. Detects poaching, tracks species, coordinates rangers, and disrupts trafficking — protecting wildlife before threats reach the herd.",
    icon: '🦁',
    logoUrl: resolveAnchorLogoByAsset(''),
    colorFrom: 'from-green-500',
    colorTo: 'to-emerald-500',
    accentColor: 'green',
    borderColor: 'border-green-500',
    textColor: 'text-green-400',
    bgGradient: 'from-green-500/10 via-transparent to-emerald-900/5',
    domain: 'wildlife.anchoraiguard.com',
    targetMarket: [
      'National Parks & Reserves',
      'Wildlife Conservation NGOs',
      'Anti-Poaching Units',
      'CITES Authorities',
      'INTERPOL Environmental Security',
      'Marine Conservation Bodies',
      'Government Wildlife Agencies',
    ],
    problemStatement:
      'Every 26 minutes an elephant is poached. Rangers patrol vast territories blind. Trafficking networks operate across dark web markets and international borders. Conservation agencies lack real-time intelligence to protect endangered species.',
    solutionStatement:
      'Anchor Wildlife provides sovereign-grade environmental intelligence — detecting threats before they reach wildlife, coordinating ranger response in under 30 seconds, and mapping trafficking networks across 194 countries.',
    features: [
      {
        icon: '🎯',
        title: 'AI-Powered Poacher Detection',
        description:
          'Thermal cameras, acoustic sensors, and drone patrols detect intruders in real-time. Alerts dispatched to rangers within 30 seconds of threat detection.',
        worldFirst: true,
      },
      {
        icon: '😀',
        title: 'Species Population Intelligence',
        description:
          'GPS collar monitoring, migration prediction, stress detection, and population analytics for endangered species across 194+ global reserves.',
      },
      {
        icon: '🚁',
        title: 'Ranger Dispatch & Coordination',
        description:
          'Real-time field coordination with GPS tracking, helicopter deployment, K9 unit management, and tactical response planning.',
      },
      {
        icon: '🕸️',
        title: 'Trafficking Network Analysis',
        description:
          'Dark web surveillance, cryptocurrency tracing, customs X-ray integration, and CITES enforcement support for combating illegal wildlife trade.',
        worldFirst: true,
      },
      {
        icon: '🛰️',
        title: 'Satellite Deforestation Monitoring',
        description:
          'Real-time satellite imagery analysis detecting habitat encroachment, illegal mining camps, and airstrip construction in protected areas.',
      },
      {
        icon: '🔗',
        title: 'Cross-Sector Intelligence',
        description:
          'Integrates with Maritime (illegal fishing), LEO (INTERPOL), Climate (habitat threats), Space (satellite), and Agriculture (human-wildlife conflict).',
        worldFirst: true,
      },
      {
        icon: '🧠',
        title: 'Predictive Threat Modelling',
        description:
          'Machine learning combines moon phase, historical patterns, market prices, and patrol gaps to predict high-risk zones 72 hours ahead.',
        worldFirst: true,
      },
      {
        icon: '⚔',
        title: 'Marine Conservation Intelligence',
        description:
          'AIS anomaly detection for illegal fishing, marine reserve monitoring, and coast guard coordination for ocean wildlife protection.',
      },
    ],
    dataSources: [
      {
        name: 'CITES Trade Database',
        description: 'International wildlife trade permits & seizures',
      },
      { name: 'TRAFFIC Network', description: 'WWF/IUCN trade monitoring intelligence' },
      { name: 'INTERPOL I-24/7', description: 'International wildlife crime alerts' },
      { name: 'Satellite Imagery', description: 'Commercial & government earth observation' },
      { name: 'GPS Collar Networks', description: 'Species tracking telemetry data' },
      { name: 'Dark Web Crawlers', description: 'Wildlife trafficking marketplace monitoring' },
    ],
    pricing: [
      {
        name: 'Reserve',
        price: '$4,990',
        period: '/mo',
        features: [
          '1 reserve',
          'Poaching alerts',
          'Species tracking',
          'Basic ranger dispatch',
          'Email support',
        ],
        cta: 'Request demo',
      },
      {
        name: 'Conservation',
        price: '$14,990',
        period: '/mo',
        features: [
          '5 reserves',
          'Everything in Reserve',
          'Trafficking intel',
          'Cross-sector integration',
          'API access',
          'Priority support',
        ],
        cta: 'Request demo',
        highlighted: true,
      },
      {
        name: 'Sovereign',
        price: '$49,990',
        period: '/mo',
        features: [
          'Unlimited reserves',
          'Everything in Conservation',
          'Satellite monitoring',
          'INTERPOL integration',
          'Custom AI models',
          'On-premise deployment',
          '99.9% SLA',
        ],
        cta: 'Contact Sales',
      },
    ],
    testimonials: [],
    stats: [
      { value: '< 30s', label: 'Alert Response Time' },
      { value: '194+', label: 'Reserves Connected' },
      { value: '42,847', label: 'Species Tracked' },
      { value: '97%', label: 'Detection Accuracy' },
    ],
    useCases: [
      {
        title: 'Anti-Poaching Operations',
        description:
          'Real-time threat detection with thermal, acoustic, and drone surveillance. Ranger dispatch within 30 seconds of alert.',
      },
      {
        title: 'Species Population Monitoring',
        description:
          'GPS collar integration, migration tracking, stress detection, and population analytics for conservation planning.',
      },
      {
        title: 'Wildlife Trafficking Disruption',
        description:
          'Dark web monitoring, cryptocurrency tracing, and customs coordination for CITES enforcement.',
      },
      {
        title: 'Marine Reserve Protection',
        description:
          'Illegal fishing detection, AIS anomaly analysis, and coast guard coordination for ocean conservation.',
      },
    ],
    revenueStreams: [
      {
        name: 'Reserve Subscriptions',
        description: 'Tiered platform access from single reserves to national park networks',
        icon: '💳',
        annualPotential: '$15M-$45M',
      },
      {
        name: 'Conservation Intelligence API',
        description:
          'Per-call access to species tracking, threat detection, and trafficking analysis',
        icon: '🔌',
        annualPotential: '$10M-$35M',
      },
      {
        name: 'Government Contracts',
        description:
          'Sovereign deployment for national wildlife agencies and environmental ministries',
        icon: '🏛️',
        annualPotential: '$20M-$60M',
      },
      {
        name: 'NGO Partnerships',
        description:
          'WWF, IUCN, Wildlife Conservation Society, and conservation foundation integrations',
        icon: '🌍',
        annualPotential: '$5M-$15M',
      },
      {
        name: 'Trafficking Intelligence',
        description: 'INTERPOL, CITES, and customs agency intelligence licensing',
        icon: '🔍',
        annualPotential: '$10M-$30M',
      },
      {
        name: 'Carbon Credit Integration',
        description: 'Conservation verification for carbon offset markets and ESG compliance',
        icon: '🌱',
        annualPotential: '$10M-$35M',
      },
    ],
    businessFunctions: [
      {
        name: 'Anti-Poaching Surveillance',
        description:
          'AI-powered thermal, acoustic, and drone detection with <30 second ranger dispatch',
        icon: '🎯',
      },
      {
        name: 'Species Intelligence Centre',
        description:
          'GPS collar integration, population analytics, migration prediction, and stress monitoring',
        icon: '😀',
      },
      {
        name: 'Ranger Operations Command',
        description:
          'Real-time field coordination, helicopter dispatch, K9 deployment, and tactical planning',
        icon: '🚁',
      },
      {
        name: 'Trafficking Network Analysis',
        description:
          'Dark web surveillance, cryptocurrency tracing, and international crime network mapping',
        icon: '🕸️',
      },
      {
        name: 'Satellite Monitoring Operations',
        description:
          'Deforestation detection, habitat encroachment alerts, and illegal camp identification',
        icon: '🛰️',
      },
      {
        name: 'Marine Conservation Intelligence',
        description: 'AIS anomaly detection, illegal fishing alerts, and coast guard coordination',
        icon: '⚔',
      },
    ],
    technologyPartners: [
      {
        name: 'xAI (Grok)',
        description: 'Advanced AI reasoning engine',
        logo: '🤖',
        integration:
          'Grok-powered threat analysis, poaching pattern prediction, and trafficking network mapping',
      },
      {
        name: 'Starlink',
        description: 'Global satellite internet constellation',
        logo: '📡',
        integration:
          'Real-time connectivity for remote reserves, ranger comms, and sensor data relay in areas with no terrestrial infrastructure',
      },
      {
        name: 'SpaceX',
        description: 'Space technology company',
        logo: '🚀',
        integration:
          'Satellite imagery partnerships for habitat monitoring and deforestation detection',
      },
      {
        name: 'INTERPOL',
        description: 'International Criminal Police Organization',
        logo: '🌍',
        integration:
          'I-24/7 network integration for wildlife crime alerts, Purple Notices, and coordinated operations',
      },
      {
        name: 'TRAFFIC',
        description: 'Wildlife trade monitoring network',
        logo: '🦁',
        integration:
          'Trade intelligence sharing, seizure coordination, and trafficking route analysis',
      },
    ],
    apiProducts: [
      {
        name: 'Poaching Alert API',
        description:
          'Real-time threat detection alerts with GPS coordinates, threat classification, and confidence scoring',
        endpoint: '/api/v1/poaching-alerts',
        pricePerCall: '$0.50',
      },
      {
        name: 'Species Tracking API',
        description: 'GPS collar data, population analytics, and migration pattern queries',
        endpoint: '/api/v1/species-tracking',
        pricePerCall: '$0.25',
      },
      {
        name: 'Trafficking Intelligence API',
        description:
          'Dark web listing monitoring, cryptocurrency wallet tracing, and network analysis',
        endpoint: '/api/v1/trafficking-intel',
        pricePerCall: '$1.00',
      },
      {
        name: 'Satellite Analysis API',
        description:
          'Deforestation alerts, habitat change detection, and illegal activity identification',
        endpoint: '/api/v1/satellite-analysis',
        pricePerCall: '$0.75',
      },
    ],
    companyStructure: {
      entity: 'Anchor AI Guard — Wildlife Division',
      division: 'Conservation & Environmental Intelligence Division',
      headcount: 'Founding team (scaling)',
      hq: 'Australia',
      registrations: [
        'Sovereign Architecture',
        'Designed for Compliance',
        'Australian-Built Platform',
      ],
    },
    disclaimer:
      'Anchor Wildlife is a sovereign environmental intelligence platform providing analytics, alerts, and decision support to authorised conservation agencies. It does not perform law enforcement actions, identify civilians, or conduct surveillance outside protected areas. All operations must comply with applicable conservation laws, privacy regulations, and human rights standards.',
    ctaText: 'Protect Wildlife Before Threats Arrive',
    ctaSubtext: 'Conservation verification required • Sovereign deployment • Full legal compliance',
  },

  // ─── 13. ANCHOR CASINO & HOSPITALITY ───────────────────────────────
  {
    id: 'casino',
    slug: '/anchor-casino',
    brandName: 'Anchor Casino',
    tagline: 'Autonomous Casino & Hospitality Intelligence',
    heroSubtitle:
      "The world's first sovereign hospitality intelligence platform. Detects fraud, monitors surveillance, protects high-value assets, and safeguards guests — securing every floor, table, and transaction in real time.",
    icon: '🎰',
    logoUrl: resolveAnchorLogoByAsset(''),
    colorFrom: 'from-yellow-500',
    colorTo: 'to-amber-500',
    accentColor: 'amber',
    borderColor: 'border-amber-500',
    textColor: 'text-amber-400',
    bgGradient: 'from-yellow-500/10 via-transparent to-amber-900/5',
    domain: 'casino.anchoraiguard.com',
    targetMarket: [
      'Casino Operators & Resorts',
      'Integrated Resort Companies',
      'Hotel Chains & Hospitality Groups',
      'Gaming Commissions & Regulators',
      'Convention & Exhibition Centres',
      'Luxury Hospitality Brands',
      'Tribal Gaming Operations',
    ],
    problemStatement:
      'Casinos and hotels are among the most targeted private-sector environments in the world. Fraud rings, organised crime, money laundering, insider threats, and guest safety incidents cost the global hospitality industry billions annually.',
    solutionStatement:
      'Anchor Casino provides sovereign-grade hospitality intelligence — detecting fraud before chips hit the table, identifying threat actors across properties, and safeguarding every guest, asset, and transaction in real time.',
    features: [
      {
        icon: '🎯',
        title: 'AI-Powered Fraud Detection',
        description:
          'Real-time chip tracking, card counting detection, collusion analysis, and transaction anomaly monitoring across every gaming table and slot floor.',
        worldFirst: true,
      },
      {
        icon: '📹',
        title: 'Sovereign Surveillance Intelligence',
        description:
          'AI-enhanced camera analytics with facial recognition, behaviour analysis, crowd density monitoring, and incident prediction across entire properties.',
      },
      {
        icon: '💰',
        title: 'Anti-Money Laundering Engine',
        description:
          'Automated AML/CTF compliance monitoring, suspicious activity reports, cash transaction tracking, and regulatory filing assistance.',
        worldFirst: true,
      },
      {
        icon: '🛡️',
        title: 'Guest Safety & Safeguarding',
        description:
          'Vulnerable person detection, problem gambling identification, self-exclusion enforcement, and mandatory safeguarding dispatch under 5 seconds.',
      },
      {
        icon: '🏨',
        title: 'Property-Wide Threat Intelligence',
        description:
          'Access control, key card anomaly detection, staff movement analytics, VIP protection coordination, and emergency response orchestration.',
      },
      {
        icon: '🔗',
        title: 'Cross-Property Network Analysis',
        description:
          'Multi-venue intelligence sharing, known offender databases, syndicate pattern recognition, and industry-wide threat correlation.',
        worldFirst: true,
      },
      {
        icon: '🧠',
        title: 'Predictive Risk Modelling',
        description:
          'Machine learning combines event schedules, historical incidents, guest patterns, and external threats to predict high-risk periods 48 hours ahead.',
        worldFirst: true,
      },
      {
        icon: '⚖️',
        title: 'Regulatory Compliance Automation',
        description:
          'Automated compliance with gaming commissions, AUSTRAC, FinCEN, and international gaming regulations across all jurisdictions.',
      },
    ],
    dataSources: [
      {
        name: 'Gaming Commission Databases',
        description: 'Exclusion lists, licence records & compliance data',
      },
      { name: 'AUSTRAC / FinCEN', description: 'Anti-money laundering transaction reporting' },
      {
        name: 'Property Management Systems',
        description: 'Hotel, F&B, and gaming floor management data',
      },
      { name: 'Surveillance Camera Networks', description: 'AI-enhanced video analytics feeds' },
      { name: 'Table Game Management', description: 'Chip tracking, bet patterns & outcome data' },
      {
        name: 'Industry Threat Databases',
        description: 'Casino industry shared threat intelligence',
      },
    ],
    pricing: [
      {
        name: 'Property',
        price: '$9,990',
        period: '/mo',
        features: [
          '1 property',
          'Fraud detection',
          'Surveillance AI',
          'AML monitoring',
          'Email support',
        ],
        cta: 'Request demo',
      },
      {
        name: 'Resort',
        price: '$29,990',
        period: '/mo',
        features: [
          '5 properties',
          'Everything in Property',
          'Cross-property intel',
          'Regulatory automation',
          'API access',
          'Priority support',
        ],
        cta: 'Request demo',
        highlighted: true,
      },
      {
        name: 'Sovereign',
        price: '$89,990',
        period: '/mo',
        features: [
          'Unlimited properties',
          'Everything in Resort',
          'Predictive modelling',
          'Custom AI models',
          'On-premise deployment',
          'Dedicated analyst team',
          '99.9% SLA',
        ],
        cta: 'Contact Sales',
      },
    ],
    testimonials: [],
    stats: [
      { value: '< 5s', label: 'Threat Response Time' },
      { value: '847+', label: 'Properties Protected' },
      { value: '$2.1B', label: 'Fraud Prevented' },
      { value: '99.7%', label: 'Detection Accuracy' },
    ],
    useCases: [
      {
        title: 'Casino Floor Security',
        description:
          'Real-time fraud detection, chip tracking, card counting identification, and collusion analysis across all gaming tables and machines.',
      },
      {
        title: 'Anti-Money Laundering',
        description:
          'Automated AML/CTF monitoring with suspicious activity reporting, cash tracking, and regulatory compliance automation.',
      },
      {
        title: 'Guest Safety & Safeguarding',
        description:
          'Vulnerable person detection, problem gambling identification, self-exclusion enforcement, and immediate welfare dispatch.',
      },
      {
        title: 'Property Security Operations',
        description:
          'Integrated surveillance, access control, VIP protection, and emergency response coordination across entire resort properties.',
      },
    ],
    revenueStreams: [
      {
        name: 'Property Subscriptions',
        description: 'Tiered platform access from single venues to global resort chains',
        icon: '💳',
        annualPotential: '$25M-$75M',
      },
      {
        name: 'Gaming Intelligence API',
        description: 'Per-call access to fraud detection, AML scoring, and threat analysis',
        icon: '🔌',
        annualPotential: '$15M-$45M',
      },
      {
        name: 'Regulatory Compliance SaaS',
        description: 'Automated filing, audit preparation, and commission reporting tools',
        icon: '⚖️',
        annualPotential: '$10M-$30M',
      },
      {
        name: 'Surveillance AI Licensing',
        description: 'AI-enhanced camera analytics, facial recognition, and behaviour analysis',
        icon: '📹',
        annualPotential: '$20M-$60M',
      },
      {
        name: 'Cross-Property Intelligence',
        description: 'Multi-venue threat sharing, industry databases, and syndicate tracking',
        icon: '🔗',
        annualPotential: '$12M-$35M',
      },
      {
        name: 'Consulting & Implementation',
        description: 'On-site deployment, custom model training, and security architecture review',
        icon: '🎯',
        annualPotential: '$8M-$20M',
      },
    ],
    businessFunctions: [
      {
        name: 'Gaming Floor Intelligence',
        description:
          'Real-time fraud detection, chip tracking, and collusion analysis across all tables and machines',
        icon: '🎰',
      },
      {
        name: 'Surveillance Command Centre',
        description:
          'AI-enhanced camera analytics, facial recognition, and crowd behaviour monitoring',
        icon: '📹',
      },
      {
        name: 'AML/CTF Compliance Engine',
        description:
          'Automated anti-money laundering monitoring, suspicious activity reporting, and regulatory filing',
        icon: '💰',
      },
      {
        name: 'Guest Safeguarding Operations',
        description:
          'Vulnerable person detection, problem gambling alerts, and welfare dispatch coordination',
        icon: '🛡️',
      },
      {
        name: 'Property Security Management',
        description: 'Access control, key card analytics, staff monitoring, and emergency response',
        icon: '🏨',
      },
      {
        name: 'Cross-Property Threat Network',
        description:
          'Multi-venue intelligence sharing, known offender databases, and syndicate tracking',
        icon: '🔗',
      },
    ],
    technologyPartners: [
      {
        name: 'xAI (Grok)',
        description: 'Advanced AI reasoning engine',
        logo: '🤖',
        integration:
          'Grok-powered fraud pattern analysis, behaviour prediction, and AML intelligence',
      },
      {
        name: 'Starlink',
        description: 'Global satellite internet constellation',
        logo: '📡',
        integration: 'Redundant connectivity for remote resort properties and cruise ship casinos',
      },
      {
        name: 'AUSTRAC',
        description: 'Australian Transaction Reports and Analysis Centre',
        logo: '🇦🇺',
        integration: 'Automated suspicious matter reporting and compliance data exchange',
      },
      {
        name: 'Gaming Commissions',
        description: 'State and national gaming regulators',
        logo: '⚖️',
        integration:
          'Real-time compliance monitoring, exclusion list sync, and licence verification',
      },
      {
        name: 'Interpol',
        description: 'International Criminal Police Organization',
        logo: '🌍',
        integration: 'Organised crime intelligence sharing and international warrant coordination',
      },
    ],
    apiProducts: [
      {
        name: 'Fraud Detection API',
        description:
          'Real-time gaming fraud scoring, chip anomaly detection, and collusion analysis',
        endpoint: '/api/v1/casino-fraud',
        pricePerCall: '$0.75',
      },
      {
        name: 'AML Intelligence API',
        description:
          'Transaction risk scoring, suspicious activity detection, and regulatory compliance checks',
        endpoint: '/api/v1/casino-aml',
        pricePerCall: '$1.00',
      },
      {
        name: 'Surveillance Analytics API',
        description: 'Camera feed analysis, facial matching, and behaviour anomaly detection',
        endpoint: '/api/v1/casino-surveillance',
        pricePerCall: '$0.50',
      },
      {
        name: 'Guest Safety API',
        description:
          'Vulnerable person scoring, problem gambling indicators, and welfare alert triggers',
        endpoint: '/api/v1/casino-safety',
        pricePerCall: '$0.60',
      },
    ],
    companyStructure: {
      entity: 'Anchor AI Guard — Casino Division',
      division: 'Hospitality & Gaming Intelligence Division',
      headcount: 'Founding team (scaling)',
      hq: 'Australia',
      registrations: [
        'Sovereign Architecture',
        'Designed for Compliance',
        'Australian-Built Platform',
      ],
    },
    disclaimer:
      'Anchor Casino is a sovereign hospitality intelligence platform providing analytics, fraud detection, and compliance support to authorised gaming and hospitality operators. It does not perform law enforcement actions, make gaming decisions, or identify individuals outside of casino security operations. All operations must comply with applicable gaming regulations, privacy laws, and responsible gambling requirements.',
    ctaText: 'Secure Every Table, Floor & Guest',
    ctaSubtext:
      'Gaming commission verification required • Sovereign deployment • Full regulatory compliance',
  },

  // ─── 14. ANCHOR INTERPOL INTELLIGENCE ──────────────────────────────
  {
    id: 'interpol',
    slug: '/anchor-interpol',
    brandName: 'Anchor Interpol Intelligence',
    tagline: 'Autonomous International Law Enforcement Intelligence',
    heroSubtitle:
      "The world's first sovereign international criminal intelligence platform. Tracks fugitives, disrupts cybercrime networks, combats terrorism and trafficking — coordinating 194 member nations in real time.",
    icon: '🌍',
    logoUrl: resolveAnchorLogoByAsset(''),
    colorFrom: 'from-rose-500',
    colorTo: 'to-red-500',
    accentColor: 'rose',
    borderColor: 'border-rose-500',
    textColor: 'text-rose-400',
    bgGradient: 'from-rose-500/10 via-transparent to-red-900/5',
    domain: 'interpol.anchoraiguard.com',
    targetMarket: [
      'National Police Forces',
      'Interpol NCBs (194 Countries)',
      'Counter-Terrorism Agencies',
      'Cybercrime Units',
      'Border Control & Immigration',
      'Financial Intelligence Units',
      'Anti-Trafficking Task Forces',
    ],
    problemStatement:
      'International crime operates across borders while law enforcement remains siloed by jurisdiction. Fugitives flee, cybercrime networks span continents, terrorism cells coordinate globally, and trafficking routes shift faster than traditional cooperation can respond.',
    solutionStatement:
      'Anchor Interpol Intelligence provides sovereign-grade international criminal intelligence — tracking fugitives across 194 nations, disrupting cybercrime networks in real time, and coordinating cross-border operations with AI-powered precision.',
    features: [
      {
        icon: '🔴',
        title: 'Red Notice Intelligence',
        description:
          'AI-enhanced fugitive tracking, movement prediction, identity correlation across global databases, and automated alert distribution to 194 member nations.',
        worldFirst: true,
      },
      {
        icon: '💻',
        title: 'Cybercrime Operations Centre',
        description:
          'Dark web monitoring, ransomware network mapping, cryptocurrency tracing, and coordinated international takedown operations.',
      },
      {
        icon: '🎯',
        title: 'Fugitive Movement Prediction',
        description:
          'Machine learning models analyse travel patterns, financial transactions, communications metadata, and open-source intelligence to predict fugitive locations.',
        worldFirst: true,
      },
      {
        icon: '🛡️',
        title: 'Counter-Terrorism Intelligence',
        description:
          'Foreign fighter tracking, radicalisation network mapping, weapons trafficking monitoring, and coordinated cross-border threat neutralisation.',
      },
      {
        icon: '🚨',
        title: 'Human Trafficking Disruption',
        description:
          'Victim identification, route prediction, financial network exposure, and multi-agency rescue operation coordination.',
        worldFirst: true,
      },
      {
        icon: '💰',
        title: 'Financial Crime & AML',
        description:
          'International money laundering detection, sanctions evasion tracking, terrorist financing disruption, and cross-border asset freezing coordination.',
      },
      {
        icon: '📡',
        title: 'Cross-Border Data Relay',
        description:
          'Sovereign encrypted intelligence sharing between 194 NCBs with zero-knowledge proof verification and diplomatic-grade security protocols.',
        worldFirst: true,
      },
      {
        icon: '🕸️',
        title: 'Dark Web OSINT',
        description:
          'Autonomous crawling of dark web forums, marketplaces, and encrypted channels to identify criminal operations and threat actors.',
      },
    ],
    dataSources: [
      {
        name: 'Interpol I-24/7 Network',
        description: 'Secure global police communications network',
      },
      {
        name: 'Interpol Notices Database',
        description: 'Red, Blue, Yellow, Green, Orange, Purple, Black notices',
      },
      { name: 'FATF / Egmont Group', description: 'Financial intelligence unit cooperation data' },
      { name: 'Europol SIENA', description: 'European secure information exchange network' },
      { name: 'Five Eyes Intelligence', description: 'Signals and human intelligence cooperation' },
      {
        name: 'Dark Web Monitoring',
        description: 'Autonomous deep/dark web intelligence gathering',
      },
    ],
    pricing: [
      {
        name: 'Regional Bureau',
        price: '$74,990',
        period: '/mo',
        features: [
          '1 regional bureau',
          'Red Notice intelligence',
          'Cybercrime monitoring',
          'Fugitive tracking',
          'Priority support',
        ],
        cta: 'Request Access',
      },
      {
        name: 'Continental Command',
        price: '$249,990',
        period: '/mo',
        features: [
          'Continental coverage',
          'Everything in Regional',
          'Counter-terrorism',
          'Trafficking disruption',
          'Dark web OSINT',
          'API access',
        ],
        cta: 'Request Access',
        highlighted: true,
      },
      {
        name: 'Sovereign Global',
        price: 'Custom',
        period: '',
        features: [
          '194-nation coverage',
          'Everything in Continental',
          'Predictive models',
          'Custom AI training',
          'Sovereign deployment',
          'Dedicated intelligence team',
          '99.99% SLA',
        ],
        cta: 'Contact Command',
      },
    ],
    testimonials: [],
    stats: [
      { value: '194', label: 'Nations Connected' },
      { value: '12,400+', label: 'Fugitives Tracked' },
      { value: '< 3s', label: 'Cross-Border Alert Time' },
      { value: '99.97%', label: 'Intelligence Accuracy' },
    ],
    useCases: [
      {
        title: 'International Fugitive Tracking',
        description:
          'AI-powered Red Notice intelligence with movement prediction, identity correlation, and coordinated international apprehension operations.',
      },
      {
        title: 'Cybercrime Network Disruption',
        description:
          'Multi-nation coordinated takedowns of ransomware groups, dark web markets, and organised cybercrime syndicates.',
      },
      {
        title: 'Counter-Terrorism Coordination',
        description:
          'Foreign fighter tracking, radicalisation network mapping, and cross-border threat intelligence sharing with real-time alerts.',
      },
      {
        title: 'Human Trafficking Disruption',
        description:
          'Victim identification, route prediction, financial exposure, and multi-agency rescue coordination across borders.',
      },
    ],
    revenueStreams: [
      {
        name: 'Bureau Subscriptions',
        description: 'Tiered access from regional bureaus to sovereign global intelligence',
        icon: '🌍',
        annualPotential: '$50M-$150M',
      },
      {
        name: 'Criminal Intelligence API',
        description:
          'Per-call access to fugitive tracking, cybercrime analysis, and threat intelligence',
        icon: '🔌',
        annualPotential: '$30M-$80M',
      },
      {
        name: 'Counter-Terrorism SaaS',
        description:
          'Dedicated CT intelligence platform with predictive modelling and alert distribution',
        icon: '🛡️',
        annualPotential: '$25M-$70M',
      },
      {
        name: 'Trafficking Disruption Platform',
        description:
          'End-to-end anti-trafficking intelligence with victim identification and route prediction',
        icon: '🚨',
        annualPotential: '$15M-$45M',
      },
      {
        name: 'Dark Web Intelligence',
        description:
          'Autonomous deep/dark web monitoring, marketplace infiltration, and actor identification',
        icon: '🕸️',
        annualPotential: '$20M-$55M',
      },
      {
        name: 'Consulting & Deployment',
        description:
          'On-site sovereign deployment, custom AI training, and intelligence architecture',
        icon: '🎯',
        annualPotential: '$10M-$30M',
      },
    ],
    businessFunctions: [
      {
        name: 'Red Notice Command',
        description:
          'AI-enhanced international fugitive tracking with movement prediction and identity correlation',
        icon: '🔴',
      },
      {
        name: 'Cybercrime Operations',
        description:
          'Dark web monitoring, ransomware network mapping, cryptocurrency tracing, and coordinated takedowns',
        icon: '💻',
      },
      {
        name: 'Counter-Terrorism Hub',
        description:
          'Foreign fighter tracking, radicalisation mapping, weapons trafficking, and threat neutralisation',
        icon: '🛡️',
      },
      {
        name: 'Anti-Trafficking Intelligence',
        description:
          'Victim identification, route prediction, financial exposure, and rescue coordination',
        icon: '🚨',
      },
      {
        name: 'Financial Crime Unit',
        description:
          'International money laundering, sanctions evasion, terrorist financing, and asset freezing',
        icon: '💰',
      },
      {
        name: 'Cross-Border Data Relay',
        description:
          'Sovereign encrypted intelligence sharing with zero-knowledge proof verification',
        icon: '📡',
      },
    ],
    technologyPartners: [
      {
        name: 'xAI (Grok)',
        description: 'Advanced AI reasoning engine',
        logo: '🤖',
        integration:
          'Grok-powered fugitive prediction, network analysis, and pattern recognition across 194 nations',
      },
      {
        name: 'Starlink',
        description: 'Global satellite internet constellation',
        logo: '📡',
        integration:
          'Redundant sovereign connectivity for remote field operations and crisis zones',
      },
      {
        name: 'Interpol I-24/7',
        description: 'Secure global police communications',
        logo: '🌍',
        integration: 'Direct integration with Interpol secure communications and databases',
      },
      {
        name: 'FATF',
        description: 'Financial Action Task Force',
        logo: '💰',
        integration: 'International AML/CTF standards compliance and intelligence cooperation',
      },
      {
        name: 'Five Eyes Alliance',
        description: 'Intelligence-sharing alliance',
        logo: '🔒',
        integration:
          'Signals intelligence fusion for counter-terrorism and organised crime operations',
      },
    ],
    apiProducts: [
      {
        name: 'Fugitive Intelligence API',
        description:
          'Real-time fugitive tracking, movement prediction, and identity correlation across 194 nations',
        endpoint: '/api/v1/interpol-fugitive',
        pricePerCall: '$2.50',
      },
      {
        name: 'Cybercrime Intelligence API',
        description:
          'Dark web monitoring, ransomware tracking, and coordinated takedown intelligence',
        endpoint: '/api/v1/interpol-cyber',
        pricePerCall: '$1.50',
      },
      {
        name: 'Counter-Terrorism API',
        description: 'Threat actor tracking, network analysis, and cross-border alert distribution',
        endpoint: '/api/v1/interpol-ct',
        pricePerCall: '$3.00',
      },
      {
        name: 'Trafficking Intelligence API',
        description:
          'Route prediction, victim identification, financial network exposure, and rescue coordination',
        endpoint: '/api/v1/interpol-trafficking',
        pricePerCall: '$2.00',
      },
    ],
    companyStructure: {
      entity: 'Anchor AI Guard — Interpol Division',
      division: 'International Criminal Intelligence Division',
      headcount: 'Founding team (scaling)',
      hq: 'Australia',
      registrations: [
        'Sovereign Architecture',
        'Designed for Compliance',
        'Australian-Built Platform',
      ],
    },
    disclaimer:
      'Anchor Interpol Intelligence is a sovereign international criminal intelligence platform providing analytics, tracking, and coordination support to authorised law enforcement agencies. It does not perform arrests, conduct interrogations, or operate outside lawful authority. All operations must comply with applicable international law, human rights standards, and data protection regulations across all 194 member nations.',
    ctaText: 'Unite 194 Nations Against Crime',
    ctaSubtext:
      'Law enforcement verification required • Sovereign deployment • Full international compliance',
  },

  // ─── 15. ANCHOR AVIATION ───────────────────────────────────────────
  {
    id: 'aviation',
    slug: '/anchor-aviation',
    brandName: 'Anchor Aviation',
    tagline: 'Autonomous Aviation Intelligence',
    heroSubtitle:
      "The world's first self-evolving aviation intelligence platform. Monitors every flight, every maintenance signal, every airspace anomaly — before incidents become accidents.",
    icon: '✈️',
    logoUrl: resolveAnchorLogoByAsset(''),
    colorFrom: 'from-sky-500',
    colorTo: 'to-cyan-500',
    accentColor: 'sky',
    borderColor: 'border-sky-500',
    textColor: 'text-sky-400',
    bgGradient: 'from-sky-500/10 via-transparent to-sky-900/5',
    domain: 'aviation.anchoraiguard.com',
    targetMarket: [
      'Airlines',
      'Airport Authorities',
      'Aviation Regulators',
      'MRO Providers',
      'Air Traffic Control',
      'Defence Aviation',
    ],
    problemStatement:
      'Aviation safety depends on detecting micro-signals across millions of flights, maintenance logs, and weather patterns. Manual reporting misses critical correlations. Near-misses go unreported.',
    solutionStatement:
      'Anchor Aviation processes real-time flight telemetry, maintenance records, and ATC data to predict failures, optimise routes, and detect safety threats before they materialise.',
    features: [
      {
        icon: '🛫',
        title: 'Flight Safety Intelligence',
        description:
          'Continuous monitoring of global flight operations, detecting anomalous patterns across altitude, speed, and route deviations in real time.',
        worldFirst: true,
      },
      {
        icon: '🔧',
        title: 'Predictive Maintenance',
        description:
          'AI-driven analysis of maintenance logs and sensor data to predict component failures weeks before they occur.',
      },
      {
        icon: '🌍',
        title: 'Airspace Threat Detection',
        description:
          'Real-time monitoring of airspace incursions, drone threats, and unauthorised flight activity.',
        worldFirst: true,
      },
      {
        icon: '🌍¦️',
        title: 'Weather Impact Analysis',
        description:
          'Correlates weather patterns with flight safety data to generate predictive risk assessments for routes and airports.',
      },
      {
        icon: '🧠',
        title: 'AI Evolution Engine',
        description:
          'Self-evolving AI that continuously improves detection accuracy on a 15-second cycle.',
        worldFirst: true,
      },
      {
        icon: '📡',
        title: 'ADS-B Intelligence',
        description:
          'Processes ADS-B transponder data from millions of flights for comprehensive situational awareness.',
      },
      {
        icon: '🔗',
        title: 'Cross-Sector Correlations',
        description:
          'Links aviation data with defence, maritime, and climate intelligence for holistic threat assessment.',
        worldFirst: true,
      },
      {
        icon: '📊',
        title: 'Operational Dashboards',
        description:
          'Real-time fleet performance, fuel efficiency, and safety score dashboards for airline operations centres.',
      },
    ],
    dataSources: [
      { name: 'ADS-B Exchange', description: 'Global flight tracking data' },
      { name: 'FAA / EASA / CASA', description: 'Regulatory and incident databases' },
      { name: 'NOTAM Systems', description: 'Notices to air missions worldwide' },
      { name: 'METAR / TAF', description: 'Aviation weather observations and forecasts' },
      { name: 'ICAO Safety Reports', description: 'International aviation safety data' },
      { name: 'Maintenance Log Systems', description: 'Aircraft maintenance and component data' },
    ],
    pricing: [
      {
        name: 'Regional',
        price: '$4,990',
        period: '/mo',
        features: [
          '5 users',
          'Flight monitoring',
          'Weather integration',
          'Basic dashboards',
          'Email support',
        ],
        cta: 'Request demo',
      },
      {
        name: 'Airline',
        price: '$14,990',
        period: '/mo',
        features: [
          '50 users',
          'Everything in Regional',
          'Predictive maintenance',
          'Route optimisation',
          'API access',
          'Priority support',
        ],
        cta: 'Request demo',
        highlighted: true,
      },
      {
        name: 'Enterprise',
        price: '$39,990',
        period: '/mo',
        features: [
          'Unlimited users',
          'Everything in Airline',
          'Airspace threat detection',
          'Custom AI models',
          'On-premise deployment',
          'Dedicated account manager',
          '99.9% SLA',
        ],
        cta: 'Contact Sales',
      },
    ],
    testimonials: [],
    stats: [
      { value: '2M+', label: 'Flights Monitored Daily' },
      { value: '23 days', label: 'Failure Prediction Lead' },
      { value: '15s', label: 'AI Evolution Cycle' },
      { value: '34%', label: 'Fewer Diversions' },
    ],
    useCases: [
      {
        title: 'Airline Safety Operations',
        description:
          'Monitor fleet-wide safety signals and predict maintenance needs before they cause delays or incidents.',
      },
      {
        title: 'Airport Security',
        description:
          'Detect airspace incursions, drone threats, and anomalous flight patterns around airports in real time.',
      },
      {
        title: 'Regulatory Compliance',
        description:
          'Automated compliance monitoring against ICAO, FAA, EASA, and CASA requirements with audit-ready reporting.',
      },
      {
        title: 'Defence Aviation',
        description:
          'Track military and civilian airspace interactions, detect surveillance flights, and monitor sovereign airspace.',
      },
    ],
    revenueStreams: [
      {
        name: 'SaaS Subscriptions',
        description: 'Tiered platform access from regional operators to global airlines',
        icon: '💳',
        annualPotential: '$10M-$30M',
      },
      {
        name: 'Flight Intelligence API',
        description: 'Per-call API for flight tracking, safety scoring, and maintenance prediction',
        icon: '🔌',
        annualPotential: '$5M-$15M',
      },
      {
        name: 'Data Licensing',
        description: 'Curated aviation intelligence datasets for manufacturers and insurers',
        icon: '📦',
        annualPotential: '$3M-$10M',
      },
      {
        name: 'Predictive MRO Platform',
        description: 'Maintenance prediction marketplace connecting airlines with parts suppliers',
        icon: '🔧',
        annualPotential: '$4M-$12M',
      },
      {
        name: 'White-Label Aviation Intel',
        description: 'Airlines embed Anchor intelligence into their own operations systems',
        icon: '🏷️',
        annualPotential: '$6M-$18M',
      },
      {
        name: 'Advisory & Consulting',
        description: 'Expert aviation safety advisory for airlines, airports, and regulators',
        icon: '🎯',
        annualPotential: '$2M-$6M',
      },
    ],
    businessFunctions: [
      {
        name: 'Flight Operations Intelligence',
        description: 'Real-time monitoring and analysis of global flight operations',
        icon: '🛫',
      },
      {
        name: 'Predictive Maintenance Engine',
        description: 'AI-driven component failure prediction from sensor and maintenance data',
        icon: '🔧',
      },
      {
        name: 'Airspace Security Operations',
        description:
          'Detection and tracking of airspace threats including drones and unauthorised flights',
        icon: '🌍',
      },
      {
        name: 'Weather Intelligence',
        description: 'Aviation-specific weather analysis and predictive route risk assessment',
        icon: '🌍¦️',
      },
      {
        name: 'Regulatory Compliance',
        description: 'Automated compliance tracking against global aviation standards',
        icon: '📋',
      },
      {
        name: 'Cross-Sector Aviation Correlations',
        description: 'Connecting aviation data with defence, maritime, and climate intelligence',
        icon: '🔗',
      },
    ],
    technologyPartners: [
      {
        name: 'Starlink',
        description: 'Global satellite internet constellation',
        logo: '📡',
        integration:
          'In-flight connectivity for real-time telemetry streaming and cockpit intelligence delivery',
      },
      {
        name: 'xAI (Grok)',
        description: 'Advanced AI reasoning engine',
        logo: '🤖',
        integration:
          'Deep reasoning for complex failure mode analysis and multi-system diagnostic correlation',
      },
      {
        name: 'X (Twitter)',
        description: 'Global real-time social platform',
        logo: '🐏',
        integration:
          'Aviation incident early warning from social signals and passenger safety reporting',
      },
      {
        name: 'SpaceX',
        description: 'Space launch and transport',
        logo: '🚀',
        integration: 'Orbital debris tracking and suborbital airspace coordination',
      },
    ],
    apiProducts: [
      {
        name: 'Flight Safety Score API',
        description:
          'Real-time safety scoring for individual flights based on telemetry, weather, and historical data',
        endpoint: '/api/v1/flight-safety',
        pricePerCall: '$0.20',
      },
      {
        name: 'Predictive Maintenance API',
        description: 'Component failure probability assessment from maintenance and sensor data',
        endpoint: '/api/v1/maintenance-predict',
        pricePerCall: '$0.35',
      },
      {
        name: 'Airspace Threat API',
        description: 'Real-time airspace threat detection and drone incursion alerts',
        endpoint: '/api/v1/airspace-threats',
        pricePerCall: '$0.30',
      },
      {
        name: 'Route Optimisation API',
        description: 'AI-optimised routing considering weather, traffic, and fuel efficiency',
        endpoint: '/api/v1/route-optimise',
        pricePerCall: '$0.25',
      },
    ],
    companyStructure: {
      entity: 'Anchor AI Guard — Aviation Division',
      division: 'Aviation Safety Intelligence Division',
      headcount: 'Founding team (scaling)',
      hq: 'Australia',
      registrations: [
        'Sovereign Architecture',
        'Designed for Compliance',
        'Australian-Built Platform',
      ],
    },
    disclaimer:
      'Anchor Aviation is an intelligence and analytics platform. It does not control aircraft, issue air traffic instructions, or replace certified aviation systems. All outputs must be validated by qualified aviation professionals.',
    ctaText: 'Predict What Radar Cannot See',
    ctaSubtext:
      'Guided demo — paid subscription • Aviation authority verification available • Full regulatory compliance',
  },

  // ─── 16. ANCHOR CHARITY ────────────────────────────────────────────
  {
    id: 'charity',
    slug: '/anchor-charity',
    brandName: 'Anchor Charity',
    tagline: 'Autonomous Humanitarian Intelligence',
    heroSubtitle:
      "The world's first self-evolving humanitarian intelligence platform. Tracks every dollar, every programme, every outcome — ensuring aid reaches those who need it most.",
    icon: '🤝',
    logoUrl: resolveAnchorLogoByAsset(''),
    colorFrom: 'from-pink-500',
    colorTo: 'to-rose-500',
    accentColor: 'pink',
    borderColor: 'border-pink-500',
    textColor: 'text-pink-400',
    bgGradient: 'from-pink-500/10 via-transparent to-pink-900/5',
    domain: 'charity.anchoraiguard.com',
    targetMarket: [
      'International NGOs',
      'Government Aid Agencies',
      'Philanthropic Foundations',
      'Charity Regulators',
      'Donors & Impact Investors',
      'Disaster Relief Organisations',
    ],
    problemStatement:
      "Billions in aid are lost to fraud, mismanagement, and opacity every year. Donors can't verify impact. Duplicate programmes waste resources. Corruption siphons funds from the most vulnerable.",
    solutionStatement:
      'Anchor Charity provides sovereign-grade transparency intelligence — tracking every dollar from donation to delivery, detecting fraud in real time, and measuring genuine impact with AI precision.',
    features: [
      {
        icon: '💸',
        title: 'Fund Flow Traceability',
        description:
          'End-to-end tracking of every donated dollar from source to beneficiary, with AI-verified delivery confirmation.',
        worldFirst: true,
      },
      {
        icon: '🔍',
        title: 'Fraud & Misuse Detection',
        description:
          'Real-time detection of fund diversion, ghost beneficiaries, and procurement fraud across charity operations.',
      },
      {
        icon: '📊',
        title: 'Impact Measurement',
        description:
          'AI-powered measurement of actual programme outcomes vs. reported outcomes, detecting inflated claims.',
        worldFirst: true,
      },
      {
        icon: '🗺️',
        title: 'Disaster Response Coordination',
        description:
          'Real-time coordination of multi-agency disaster relief, preventing duplication and identifying coverage gaps.',
      },
      {
        icon: '🧠',
        title: 'AI Evolution Engine',
        description:
          'Self-evolving AI continuously improving fraud detection and impact measurement on a 15-second cycle.',
        worldFirst: true,
      },
      {
        icon: '🌍',
        title: 'Global Programme Mapping',
        description:
          'Maps every active humanitarian programme worldwide, identifying overlaps, gaps, and unserved populations.',
      },
      {
        icon: '🔗',
        title: 'Cross-Sector Correlations',
        description:
          'Links charity data with climate, health, and economic intelligence for predictive needs assessment.',
        worldFirst: true,
      },
      {
        icon: '📋',
        title: 'Regulatory Compliance',
        description:
          'Automated ACNC, Charity Commission, and IRS compliance monitoring with audit-ready reporting.',
      },
    ],
    dataSources: [
      { name: 'ACNC Register', description: 'Australian Charities & Not-for-profits Commission' },
      { name: 'UN OCHA ReliefWeb', description: 'Global humanitarian data' },
      { name: 'IATI Registry', description: 'International aid transparency data' },
      { name: 'Charity Commission (UK)', description: 'UK charity register and accounts' },
      { name: 'GuideStar / Candid', description: 'US nonprofit data' },
      { name: 'World Bank Open Data', description: 'Development indicators and aid flows' },
    ],
    pricing: [
      {
        name: 'Foundation',
        price: '$1,990',
        period: '/mo',
        features: [
          '5 users',
          'Fund tracking',
          'Basic impact reports',
          'Compliance alerts',
          'Email support',
        ],
        cta: 'Request demo',
      },
      {
        name: 'Organisation',
        price: '$6,990',
        period: '/mo',
        features: [
          '25 users',
          'Everything in Foundation',
          'Fraud detection',
          'Multi-programme tracking',
          'API access',
          'Priority support',
        ],
        cta: 'Request demo',
        highlighted: true,
      },
      {
        name: 'Global',
        price: '$19,990',
        period: '/mo',
        features: [
          'Unlimited users',
          'Everything in Organisation',
          'Disaster coordination',
          'Custom AI models',
          'White-label reporting',
          'Dedicated account manager',
          '99.9% SLA',
        ],
        cta: 'Contact Sales',
      },
    ],
    testimonials: [],
    stats: [
      { value: '$4.2B', label: 'Aid Tracked Annually' },
      { value: '$340M', label: 'Fraud Prevented' },
      { value: '15s', label: 'AI Evolution Cycle' },
      { value: '194', label: 'Countries Monitored' },
    ],
    useCases: [
      {
        title: 'Donor Transparency',
        description:
          'Give donors real-time visibility into how their funds are used and what outcomes they achieve.',
      },
      {
        title: 'Fraud Prevention',
        description:
          'Detect fund diversion, ghost beneficiaries, and procurement fraud before losses accumulate.',
      },
      {
        title: 'Disaster Relief Coordination',
        description:
          'Coordinate multi-agency response to prevent duplication and ensure coverage of affected populations.',
      },
      {
        title: 'Regulatory Compliance',
        description: 'Automated compliance with charity regulations across multiple jurisdictions.',
      },
    ],
    revenueStreams: [
      {
        name: 'SaaS Subscriptions',
        description: 'Tiered platform access from small charities to global NGOs',
        icon: '💳',
        annualPotential: '$5M-$15M',
      },
      {
        name: 'Impact Verification API',
        description: 'Per-call API for verifying programme outcomes and fund disbursement',
        icon: '🔌',
        annualPotential: '$2M-$8M',
      },
      {
        name: 'Donor Intelligence Platform',
        description: 'White-label donor portals showing real-time fund tracking and impact',
        icon: '📦',
        annualPotential: '$3M-$10M',
      },
      {
        name: 'Government Aid Monitoring',
        description: 'Sovereign aid tracking for government international development agencies',
        icon: '🏛️',
        annualPotential: '$4M-$12M',
      },
      {
        name: 'Compliance-as-a-Service',
        description: 'Automated charity compliance monitoring and audit preparation',
        icon: '📋',
        annualPotential: '$2M-$6M',
      },
      {
        name: 'Advisory & Consulting',
        description: 'Expert humanitarian intelligence advisory for NGOs and donors',
        icon: '🎯',
        annualPotential: '$1M-$4M',
      },
    ],
    businessFunctions: [
      {
        name: 'Fund Traceability Operations',
        description: 'End-to-end tracking of aid flows from donation to delivery',
        icon: '💸',
      },
      {
        name: 'Fraud Detection Engine',
        description: 'AI-driven identification of misuse, diversion, and ghost beneficiary schemes',
        icon: '🔍',
      },
      {
        name: 'Impact Measurement',
        description: 'Quantitative measurement and verification of programme outcomes',
        icon: '📊',
      },
      {
        name: 'Disaster Coordination',
        description: 'Real-time multi-agency relief coordination and resource allocation',
        icon: '🗺️',
      },
      {
        name: 'Regulatory Intelligence',
        description: 'Global charity regulation monitoring and compliance automation',
        icon: '📋',
      },
      {
        name: 'Cross-Sector Humanitarian Intel',
        description: 'Connecting aid data with climate, health, and economic intelligence',
        icon: '🔗',
      },
    ],
    technologyPartners: [
      {
        name: 'Starlink',
        description: 'Global satellite internet constellation',
        logo: '📡',
        integration:
          'Enabling real-time aid tracking and programme monitoring in remote and disaster-affected regions',
      },
      {
        name: 'xAI (Grok)',
        description: 'Advanced AI reasoning engine',
        logo: '🤖',
        integration:
          'Deep reasoning for complex fund flow analysis and fraud pattern detection across global operations',
      },
      {
        name: 'X (Twitter)',
        description: 'Global real-time social platform',
        logo: '🐏',
        integration:
          'Disaster early warning from social signals and humanitarian needs assessment from ground reports',
      },
      {
        name: 'Neuralink',
        description: 'Brain-computer interface pioneer',
        logo: '🧠',
        integration:
          'Accessibility technology integration for disability-focused humanitarian programmes',
      },
    ],
    apiProducts: [
      {
        name: 'Fund Trace API',
        description: 'Track individual donations from source to beneficiary with full audit trail',
        endpoint: '/api/v1/fund-trace',
        pricePerCall: '$0.10',
      },
      {
        name: 'Fraud Detection API',
        description: 'Real-time fraud risk scoring for charity transactions and disbursements',
        endpoint: '/api/v1/charity-fraud',
        pricePerCall: '$0.20',
      },
      {
        name: 'Impact Score API',
        description: 'Programme impact verification and outcome measurement',
        endpoint: '/api/v1/impact-score',
        pricePerCall: '$0.15',
      },
      {
        name: 'Needs Assessment API',
        description: 'AI-powered humanitarian needs assessment for target populations',
        endpoint: '/api/v1/needs-assess',
        pricePerCall: '$0.25',
      },
    ],
    companyStructure: {
      entity: 'Anchor AI Guard — Charity Division',
      division: 'Humanitarian Intelligence Division',
      headcount: 'Founding team (scaling)',
      hq: 'Australia',
      registrations: [
        'Sovereign Architecture',
        'Designed for Compliance',
        'Australian-Built Platform',
      ],
    },
    disclaimer:
      'Anchor Charity is an intelligence and transparency platform. It does not distribute aid, manage humanitarian programmes, or make funding decisions. All outputs should be validated by qualified professionals.',
    ctaText: 'Make Every Dollar Count',
    ctaSubtext:
      'Guided demo — paid subscription • Special NGO pricing available • Full regulatory compliance',
  },

  // ─── 17. ANCHOR CRYPTO ─────────────────────────────────────────────
  {
    id: 'crypto',
    slug: '/anchor-crypto',
    brandName: 'Anchor Crypto',
    tagline: 'Autonomous Blockchain Intelligence',
    heroSubtitle:
      "The world's first self-evolving blockchain intelligence platform. Traces every wallet, every transaction, every smart contract — exposing what the blockchain tries to hide.",
    icon: '₿',
    logoUrl: resolveAnchorLogoByAsset(''),
    colorFrom: 'from-orange-500',
    colorTo: 'to-amber-500',
    accentColor: 'orange',
    borderColor: 'border-orange-500',
    textColor: 'text-orange-400',
    bgGradient: 'from-orange-500/10 via-transparent to-orange-900/5',
    domain: 'crypto.anchoraiguard.com',
    targetMarket: [
      'Crypto Exchanges',
      'DeFi Protocols',
      'Law Enforcement',
      'Financial Regulators',
      'Institutional Investors',
      'Compliance Teams',
    ],
    problemStatement:
      "Over $14B in crypto was stolen in 2023 alone. Rug pulls, money laundering, and sanctions evasion exploit blockchain pseudonymity. Traditional compliance tools can't keep pace with DeFi complexity.",
    solutionStatement:
      'Anchor Crypto provides sovereign-grade blockchain intelligence — tracing funds across 50+ chains, de-anonymising wallets, and detecting illicit activity before enforcement agencies issue alerts.',
    features: [
      {
        icon: '🔗',
        title: 'Multi-Chain Tracing',
        description:
          'Traces transactions across 50+ blockchains simultaneously, including cross-chain bridges and mixer services.',
        worldFirst: true,
      },
      {
        icon: '👤',
        title: 'Wallet De-Anonymisation',
        description:
          'AI-powered identity clustering that links pseudonymous wallets to real-world entities using on-chain and off-chain signals.',
      },
      {
        icon: '🚨',
        title: 'Rug Pull Detection',
        description:
          'Predicts rug pulls and exit scams by analysing smart contract code, liquidity patterns, and developer behaviour.',
        worldFirst: true,
      },
      {
        icon: '🏦',
        title: 'AML/KYC Intelligence',
        description:
          'Real-time transaction screening against sanctions lists, PEP databases, and known illicit wallet clusters.',
      },
      {
        icon: '🧠',
        title: 'AI Evolution Engine',
        description:
          'Self-evolving AI that continuously improves detection of novel laundering techniques on a 15-second cycle.',
        worldFirst: true,
      },
      {
        icon: '📈',
        title: 'DeFi Risk Intelligence',
        description:
          'Smart contract vulnerability scanning, liquidity risk assessment, and protocol health monitoring.',
      },
      {
        icon: '🔗',
        title: 'Cross-Sector Correlations',
        description:
          'Links blockchain data with financial, law enforcement, and defence intelligence for comprehensive threat mapping.',
        worldFirst: true,
      },
      {
        icon: '📊',
        title: 'Compliance Dashboards',
        description:
          'Real-time AUSTRAC, FinCEN, and MiCA compliance monitoring with automated suspicious activity reporting.',
      },
    ],
    dataSources: [
      {
        name: 'Bitcoin / Ethereum / 50+ Chains',
        description: 'Full node and indexed blockchain data',
      },
      { name: 'AUSTRAC / FinCEN / FATF', description: 'AML regulatory databases' },
      { name: 'OFAC Sanctions List', description: 'US Treasury sanctioned wallets' },
      { name: 'DeFi Protocol Data', description: 'Smart contract state and liquidity data' },
      { name: 'Dark Web Intelligence', description: 'Marketplace and forum monitoring' },
      { name: 'Exchange KYC Data', description: 'Partnership-based identity resolution' },
    ],
    pricing: [
      {
        name: 'Analyst',
        price: '$3,990',
        period: '/mo',
        features: [
          '5 users',
          'Multi-chain tracing',
          'Wallet screening',
          'Basic dashboards',
          'Email support',
        ],
        cta: 'Request demo',
      },
      {
        name: 'Exchange',
        price: '$14,990',
        period: '/mo',
        features: [
          '50 users',
          'Everything in Analyst',
          'AML automation',
          'Rug pull alerts',
          'API access',
          'Priority support',
        ],
        cta: 'Request demo',
        highlighted: true,
      },
      {
        name: 'Enterprise',
        price: '$49,990',
        period: '/mo',
        features: [
          'Unlimited users',
          'Everything in Exchange',
          'De-anonymisation engine',
          'Custom AI models',
          'On-premise deployment',
          'Dedicated account manager',
          '99.9% SLA',
        ],
        cta: 'Contact Sales',
      },
    ],
    testimonials: [],
    stats: [
      { value: '50+', label: 'Chains Monitored' },
      { value: '$2.1B', label: 'Illicit Funds Traced' },
      { value: '15s', label: 'AI Evolution Cycle' },
      { value: '94%', label: 'Rug Pull Detection Rate' },
    ],
    useCases: [
      {
        title: 'Exchange Compliance',
        description:
          'Automate AML/KYC screening and suspicious activity reporting for crypto exchanges.',
      },
      {
        title: 'Law Enforcement',
        description:
          'Trace illicit funds across chains, de-anonymise wallets, and build prosecution-ready evidence packages.',
      },
      {
        title: 'DeFi Risk Management',
        description:
          'Assess smart contract risks, monitor protocol health, and detect rug pull signals before they execute.',
      },
      {
        title: 'Sanctions Enforcement',
        description:
          'Real-time screening of all transactions against global sanctions lists and sanctioned wallet clusters.',
      },
    ],
    revenueStreams: [
      {
        name: 'SaaS Subscriptions',
        description: 'Tiered platform access from individual analysts to global exchanges',
        icon: '💳',
        annualPotential: '$15M-$40M',
      },
      {
        name: 'Blockchain Tracing API',
        description: 'Per-call API for transaction tracing, wallet scoring, and AML screening',
        icon: '🔌',
        annualPotential: '$10M-$35M',
      },
      {
        name: 'Data Licensing',
        description:
          'Curated blockchain intelligence datasets for financial institutions and regulators',
        icon: '📦',
        annualPotential: '$5M-$15M',
      },
      {
        name: 'Law Enforcement Contracts',
        description:
          'Sovereign blockchain forensics for national and international law enforcement agencies',
        icon: '🏛️',
        annualPotential: '$6M-$18M',
      },
      {
        name: 'White-Label Compliance',
        description: 'Exchanges embed Anchor compliance intelligence into their platforms',
        icon: '🏷️',
        annualPotential: '$4M-$12M',
      },
      {
        name: 'Advisory & Forensics',
        description: 'Expert blockchain forensic investigation and advisory services',
        icon: '🎯',
        annualPotential: '$3M-$8M',
      },
    ],
    businessFunctions: [
      {
        name: 'Multi-Chain Analytics',
        description: 'Transaction tracing and analysis across 50+ blockchain networks',
        icon: '🔗',
      },
      {
        name: 'Identity Intelligence',
        description: 'Wallet de-anonymisation and entity resolution using AI clustering',
        icon: '👤',
      },
      {
        name: 'AML Operations',
        description: 'Automated anti-money laundering screening and suspicious activity detection',
        icon: '🏦',
      },
      {
        name: 'Smart Contract Security',
        description: 'Vulnerability scanning and rug pull prediction for DeFi protocols',
        icon: '🔒',
      },
      {
        name: 'Sanctions Compliance',
        description: 'Real-time screening against global sanctions lists and PEP databases',
        icon: '🚨',
      },
      {
        name: 'Cross-Sector Crypto Intel',
        description:
          'Connecting blockchain data with financial, law enforcement, and defence intelligence',
        icon: '🔗',
      },
    ],
    technologyPartners: [
      {
        name: 'xAI (Grok)',
        description: 'Advanced AI reasoning engine',
        logo: '🤖',
        integration:
          'Deep reasoning for complex transaction graph analysis and novel laundering pattern detection',
      },
      {
        name: 'X (Twitter)',
        description: 'Global real-time social platform',
        logo: '🐏',
        integration:
          'Crypto sentiment monitoring, scam early warning from social signals, and influencer wallet tracking',
      },
      {
        name: 'Starlink',
        description: 'Global satellite internet constellation',
        logo: '📡',
        integration:
          'Resilient connectivity for blockchain node operation and real-time intelligence delivery worldwide',
      },
      {
        name: 'Neuralink',
        description: 'Brain-computer interface pioneer',
        logo: '🧠',
        integration:
          'Future: Direct neural interface for high-speed blockchain forensic analysis and pattern recognition',
      },
    ],
    apiProducts: [
      {
        name: 'Transaction Trace API',
        description: 'Trace any transaction across 50+ chains with full hop-by-hop analysis',
        endpoint: '/api/v1/tx-trace',
        pricePerCall: '$0.30',
      },
      {
        name: 'Wallet Risk Score API',
        description: 'Real-time risk scoring for any wallet address across all supported chains',
        endpoint: '/api/v1/wallet-risk',
        pricePerCall: '$0.15',
      },
      {
        name: 'AML Screen API',
        description: 'Screen addresses against sanctions, PEP, and known illicit wallet databases',
        endpoint: '/api/v1/aml-screen',
        pricePerCall: '$0.10',
      },
      {
        name: 'Rug Pull Prediction API',
        description: 'Probability scoring for rug pull risk on DeFi tokens and protocols',
        endpoint: '/api/v1/rugpull-predict',
        pricePerCall: '$0.50',
      },
    ],
    companyStructure: {
      entity: 'Anchor AI Guard — Crypto Division',
      division: 'Blockchain Forensics Division',
      headcount: 'Founding team (scaling)',
      hq: 'Australia',
      registrations: [
        'Sovereign Architecture',
        'Designed for Compliance',
        'Australian-Built Platform',
      ],
    },
    disclaimer:
      'Anchor Crypto is an intelligence and analytics platform. It does not custody digital assets, execute trades, or provide financial advice. All outputs should be validated by qualified compliance professionals.',
    ctaText: 'See Through the Blockchain',
    ctaSubtext:
      'Guided demo — paid subscription • Law enforcement pricing available • Full regulatory compliance',
  },

  // ─── 18. ANCHOR ENTERTAINMENT ──────────────────────────────────────
  {
    id: 'entertainment',
    slug: '/anchor-entertainment',
    brandName: 'Anchor Entertainment',
    tagline: 'Autonomous Entertainment Intelligence',
    heroSubtitle:
      "The world's first self-evolving entertainment intelligence platform. Reads every stream, every ticket sale, every social signal — predicting what audiences want before they know it.",
    icon: '🎬',
    logoUrl: resolveAnchorLogoByAsset(''),
    colorFrom: 'from-fuchsia-500',
    colorTo: 'to-purple-500',
    accentColor: 'fuchsia',
    borderColor: 'border-fuchsia-500',
    textColor: 'text-fuchsia-400',
    bgGradient: 'from-fuchsia-500/10 via-transparent to-fuchsia-900/5',
    domain: 'entertainment.anchoraiguard.com',
    targetMarket: [
      'Streaming Platforms',
      'Film Studios',
      'Music Labels',
      'Event Promoters',
      'Gaming Companies',
      'Talent Agencies',
    ],
    problemStatement:
      'Entertainment is a $2.7T industry driven by guesswork. Studios greenlight films on gut feel. Tour promoters miss market shifts. Piracy costs the industry $80B annually.',
    solutionStatement:
      'Anchor Entertainment provides AI-driven audience intelligence — predicting content performance, detecting piracy in real time, and optimising revenue across streaming, live events, and gaming.',
    features: [
      {
        icon: '🎯',
        title: 'Audience Prediction Engine',
        description:
          'Predicts content success probability across demographics, regions, and platforms before release.',
        worldFirst: true,
      },
      {
        icon: '🏴"☠️',
        title: 'Anti-Piracy Intelligence',
        description:
          'Detects and maps piracy networks in real time across torrent, streaming, and social platforms.',
      },
      {
        icon: '🎵',
        title: 'Music Intelligence',
        description:
          'Analyses streaming patterns, playlist dynamics, and social sentiment to predict breakout artists.',
        worldFirst: true,
      },
      {
        icon: '🎮',
        title: 'Gaming Analytics',
        description:
          'Player behaviour analysis, churn prediction, and monetisation optimisation for gaming studios.',
      },
      {
        icon: '🧠',
        title: 'AI Evolution Engine',
        description:
          'Self-evolving AI that continuously improves audience prediction on a 15-second cycle.',
        worldFirst: true,
      },
      {
        icon: '📺',
        title: 'Streaming Intelligence',
        description:
          'Cross-platform viewing analysis, content gap identification, and licensing valuation.',
      },
      {
        icon: '🔗',
        title: 'Cross-Sector Correlations',
        description:
          'Links entertainment data with social, economic, and demographic intelligence.',
        worldFirst: true,
      },
      {
        icon: '🎪',
        title: 'Live Event Intelligence',
        description: 'Ticket demand prediction, venue optimisation, and tour routing analytics.',
      },
    ],
    dataSources: [
      {
        name: 'Streaming APIs',
        description: 'Spotify, Apple Music, YouTube, Netflix viewing data',
      },
      { name: 'Box Office Mojo / The Numbers', description: 'Film performance data' },
      { name: 'Social Media Platforms', description: 'Sentiment and engagement analytics' },
      { name: 'Ticket Platforms', description: 'Event sales and demand data' },
      { name: 'DMCA / Anti-Piracy Networks', description: 'Piracy detection and takedown data' },
      { name: 'Gaming Analytics Platforms', description: 'Player behaviour and market data' },
    ],
    pricing: [
      {
        name: 'Creator',
        price: '$2,990',
        period: '/mo',
        features: [
          '5 users',
          'Audience analytics',
          'Content performance',
          'Basic dashboards',
          'Email support',
        ],
        cta: 'Request demo',
      },
      {
        name: 'Studio',
        price: '$12,990',
        period: '/mo',
        features: [
          '50 users',
          'Everything in Creator',
          'Prediction engine',
          'Anti-piracy monitoring',
          'API access',
          'Priority support',
        ],
        cta: 'Request demo',
        highlighted: true,
      },
      {
        name: 'Enterprise',
        price: '$39,990',
        period: '/mo',
        features: [
          'Unlimited users',
          'Everything in Studio',
          'Cross-platform intelligence',
          'Custom AI models',
          'White-label analytics',
          'Dedicated account manager',
          '99.9% SLA',
        ],
        cta: 'Contact Sales',
      },
    ],
    testimonials: [],
    stats: [
      { value: '2.1B+', label: 'Streams Analysed Daily' },
      { value: '$80M', label: 'Piracy Revenue Protected' },
      { value: '15s', label: 'AI Evolution Cycle' },
      { value: '87%', label: 'Prediction Accuracy' },
    ],
    useCases: [
      {
        title: 'Content Greenlight Decisions',
        description:
          'Predict audience reception and revenue potential before investing in production.',
      },
      {
        title: 'Anti-Piracy Operations',
        description:
          'Detect and map piracy networks in real time, automating takedown requests at scale.',
      },
      {
        title: 'Tour & Event Planning',
        description: 'Optimise tour routes, predict ticket demand, and maximise venue revenue.',
      },
      {
        title: 'Streaming Optimisation',
        description:
          'Identify content gaps, optimise release timing, and maximise platform engagement.',
      },
    ],
    revenueStreams: [
      {
        name: 'SaaS Subscriptions',
        description: 'Tiered access from independent creators to global studios',
        icon: '💳',
        annualPotential: '$12M-$35M',
      },
      {
        name: 'Audience Intelligence API',
        description:
          'Per-call API for audience prediction, sentiment analysis, and content scoring',
        icon: '🔌',
        annualPotential: '$6M-$18M',
      },
      {
        name: 'Anti-Piracy Service',
        description: 'Managed piracy detection and automated takedown for content owners',
        icon: '🏴"☠️',
        annualPotential: '$10M-$35M',
      },
      {
        name: 'Data Licensing',
        description: 'Entertainment intelligence datasets for studios, agencies, and advertisers',
        icon: '📦',
        annualPotential: '$4M-$12M',
      },
      {
        name: 'White-Label Analytics',
        description: 'Streaming platforms embed Anchor intelligence in their dashboards',
        icon: '🏷️',
        annualPotential: '$5M-$15M',
      },
      {
        name: 'Advisory & Consulting',
        description:
          'Expert entertainment intelligence advisory for content and distribution strategy',
        icon: '🎯',
        annualPotential: '$2M-$6M',
      },
    ],
    businessFunctions: [
      {
        name: 'Audience Intelligence',
        description: 'Predictive analytics for content performance and audience behaviour',
        icon: '🎯',
      },
      {
        name: 'Anti-Piracy Operations',
        description: 'Real-time piracy detection, network mapping, and automated takedowns',
        icon: '🏴"☠️',
      },
      {
        name: 'Music Analytics',
        description: 'Streaming pattern analysis and breakout artist prediction',
        icon: '🎵',
      },
      {
        name: 'Gaming Intelligence',
        description: 'Player behaviour analysis, churn prediction, and monetisation optimisation',
        icon: '🎮',
      },
      {
        name: 'Live Event Analytics',
        description: 'Ticket demand prediction, venue optimisation, and tour routing',
        icon: '🎪',
      },
      {
        name: 'Cross-Platform Intelligence',
        description: 'Unified analytics across streaming, social, live, and gaming platforms',
        icon: '🔗',
      },
    ],
    technologyPartners: [
      {
        name: 'X (Twitter)',
        description: 'Global real-time social platform',
        logo: '🐏',
        integration:
          'Real-time audience sentiment monitoring, trend detection, and viral content prediction',
      },
      {
        name: 'xAI (Grok)',
        description: 'Advanced AI reasoning engine',
        logo: '🤖',
        integration:
          'Deep reasoning for content success prediction and complex audience behaviour analysis',
      },
      {
        name: 'Starlink',
        description: 'Global satellite internet constellation',
        logo: '📡',
        integration:
          'Enabling live event streaming intelligence and global content delivery analytics',
      },
      {
        name: 'Neuralink',
        description: 'Brain-computer interface pioneer',
        logo: '🧠',
        integration:
          'Future: Neural engagement measurement for immersive entertainment and gaming experiences',
      },
    ],
    apiProducts: [
      {
        name: 'Content Prediction API',
        description: 'Predict audience reception and revenue potential for any content concept',
        endpoint: '/api/v1/content-predict',
        pricePerCall: '$0.40',
      },
      {
        name: 'Piracy Detection API',
        description: 'Search for pirated content across torrent, streaming, and social platforms',
        endpoint: '/api/v1/piracy-detect',
        pricePerCall: '$0.20',
      },
      {
        name: 'Audience Sentiment API',
        description: 'Real-time audience sentiment analysis for content, artists, and events',
        endpoint: '/api/v1/audience-sentiment',
        pricePerCall: '$0.15',
      },
      {
        name: 'Trend Prediction API',
        description:
          'Predict emerging entertainment trends across music, film, gaming, and live events',
        endpoint: '/api/v1/trend-predict',
        pricePerCall: '$0.30',
      },
    ],
    companyStructure: {
      entity: 'Anchor AI Guard — Entertainment Division',
      division: 'Entertainment Intelligence Division',
      headcount: 'Founding team (scaling)',
      hq: 'Australia',
      registrations: [
        'Sovereign Architecture',
        'Designed for Compliance',
        'Australian-Built Platform',
      ],
    },
    disclaimer:
      'Anchor Entertainment is an intelligence and analytics platform. It does not produce content, manage artists, or sell tickets. All outputs should be validated by qualified entertainment professionals.',
    ctaText: 'Predict What Audiences Crave',
    ctaSubtext:
      'Guided demo — paid subscription • Creator tier available • Full content compliance',
  },

  // ─── 19. ANCHOR INSURANCE ─────────────────────────────────────────
  {
    id: 'insurance',
    slug: '/anchor-insurance',
    brandName: 'Anchor Insurance',
    tagline: 'Autonomous Insurance Intelligence',
    heroSubtitle:
      "The world's first self-evolving insurance intelligence platform. Detects every fraudulent claim, prices every risk, and predicts every catastrophe — before underwriters see the data.",
    icon: '🛡️',
    logoUrl: resolveAnchorLogoByAsset(''),
    colorFrom: 'from-teal-500',
    colorTo: 'to-cyan-500',
    accentColor: 'teal',
    borderColor: 'border-teal-500',
    textColor: 'text-teal-400',
    bgGradient: 'from-teal-500/10 via-transparent to-teal-900/5',
    domain: 'insurance.anchoraiguard.com',
    targetMarket: [
      'Insurance Companies',
      'Reinsurers',
      'Insurance Regulators',
      'Actuarial Firms',
      'Claims Investigators',
      'InsurTech Companies',
    ],
    problemStatement:
      'Insurance fraud costs the global industry $80B annually. Catastrophe models are outdated. Claims processing takes weeks. Underwriting relies on stale data and manual risk assessment.',
    solutionStatement:
      'Anchor Insurance provides sovereign-grade actuarial intelligence — detecting fraud in real time, pricing risk with AI precision, and predicting catastrophe losses before events unfold.',
    features: [
      {
        icon: '🔍',
        title: 'Fraud Detection Engine',
        description:
          'AI-powered detection of fraudulent claims including staged accidents, inflated losses, and organised fraud rings.',
        worldFirst: true,
      },
      {
        icon: '📊',
        title: 'Dynamic Risk Pricing',
        description:
          'Real-time risk assessment using satellite imagery, IoT data, social signals, and behavioural analytics.',
      },
      {
        icon: '🌍ª️',
        title: 'Catastrophe Prediction',
        description:
          'AI-enhanced catastrophe modelling combining climate data, property exposure, and historical loss patterns.',
        worldFirst: true,
      },
      {
        icon: '⚡',
        title: 'Instant Claims Processing',
        description:
          'Automated claims assessment and settlement for standard claims, reducing processing from weeks to minutes.',
      },
      {
        icon: '🧠',
        title: 'AI Evolution Engine',
        description:
          'Self-evolving AI that continuously improves fraud detection and risk pricing on a 15-second cycle.',
        worldFirst: true,
      },
      {
        icon: '🏠',
        title: 'Property Intelligence',
        description:
          'Satellite and IoT-based property assessment for underwriting, renewal, and post-event damage estimation.',
      },
      {
        icon: '🔗',
        title: 'Cross-Sector Correlations',
        description:
          'Links insurance data with climate, maritime, automotive, and health intelligence.',
        worldFirst: true,
      },
      {
        icon: '📋',
        title: 'Regulatory Compliance',
        description:
          "Automated APRA, Lloyd's, and Solvency II compliance monitoring with audit-ready reporting.",
      },
    ],
    dataSources: [
      { name: "APRA / Lloyd's / NAIC", description: 'Insurance regulatory data' },
      { name: 'Satellite Imagery', description: 'Property and environmental assessment' },
      { name: 'Claims Databases', description: 'Historical claims and loss data' },
      { name: 'BoM / NOAA Weather Data', description: 'Climate and weather intelligence' },
      { name: 'IoT Sensor Networks', description: 'Real-time property and vehicle telemetry' },
      { name: 'Motor Vehicle Registries', description: 'Vehicle history and identity data' },
    ],
    pricing: [
      {
        name: 'Broker',
        price: '$3,990',
        period: '/mo',
        features: [
          '5 users',
          'Risk scoring',
          'Claims monitoring',
          'Basic dashboards',
          'Email support',
        ],
        cta: 'Request demo',
      },
      {
        name: 'Insurer',
        price: '$14,990',
        period: '/mo',
        features: [
          '50 users',
          'Everything in Broker',
          'Fraud detection',
          'Cat modelling',
          'API access',
          'Priority support',
        ],
        cta: 'Request demo',
        highlighted: true,
      },
      {
        name: 'Enterprise',
        price: '$49,990',
        period: '/mo',
        features: [
          'Unlimited users',
          'Everything in Insurer',
          'Dynamic pricing engine',
          'Custom AI models',
          'On-premise deployment',
          'Dedicated account manager',
          '99.9% SLA',
        ],
        cta: 'Contact Sales',
      },
    ],
    testimonials: [],
    stats: [
      { value: '$340M', label: 'Fraud Prevented' },
      { value: '78%', label: 'Faster Claims Processing' },
      { value: '15s', label: 'AI Evolution Cycle' },
      { value: '8%', label: 'Cat Model Accuracy' },
    ],
    useCases: [
      {
        title: 'Claims Fraud Detection',
        description:
          'Detect staged accidents, inflated claims, and organised fraud rings before payouts are made.',
      },
      {
        title: 'Underwriting Intelligence',
        description: 'Price risk accurately using real-time satellite, IoT, and behavioural data.',
      },
      {
        title: 'Catastrophe Preparedness',
        description:
          'Predict catastrophe losses and pre-position reserves before weather events unfold.',
      },
      {
        title: 'Regulatory Compliance',
        description: "Automated compliance with APRA, Lloyd's, and Solvency II requirements.",
      },
    ],
    revenueStreams: [
      {
        name: 'SaaS Subscriptions',
        description: 'Tiered platform access from brokers to global insurers',
        icon: '💳',
        annualPotential: '$15M-$40M',
      },
      {
        name: 'Risk Scoring API',
        description: 'Per-call API for property, vehicle, and health risk assessment',
        icon: '🔌',
        annualPotential: '$10M-$35M',
      },
      {
        name: 'Data Licensing',
        description: 'Curated actuarial intelligence datasets for reinsurers and regulators',
        icon: '📦',
        annualPotential: '$5M-$15M',
      },
      {
        name: 'Fraud Detection Service',
        description: 'Managed fraud detection and investigation support for insurers',
        icon: '🔍',
        annualPotential: '$6M-$18M',
      },
      {
        name: 'White-Label Underwriting',
        description: 'Insurers embed Anchor risk intelligence into their platforms',
        icon: '🏷️',
        annualPotential: '$4M-$12M',
      },
      {
        name: 'Advisory & Consulting',
        description: 'Expert actuarial and fraud investigation advisory services',
        icon: '🎯',
        annualPotential: '$2M-$6M',
      },
    ],
    businessFunctions: [
      {
        name: 'Fraud Intelligence',
        description: 'AI-driven fraud detection across claims, underwriting, and policy lifecycle',
        icon: '🔍',
      },
      {
        name: 'Actuarial Intelligence',
        description:
          'Dynamic risk pricing using real-time data from satellites, IoT, and social signals',
        icon: '📊',
      },
      {
        name: 'Catastrophe Modelling',
        description: 'AI-enhanced natural disaster prediction and loss estimation',
        icon: '🌍ª️',
      },
      {
        name: 'Claims Automation',
        description: 'Intelligent claims processing, assessment, and settlement',
        icon: '⚡',
      },
      {
        name: 'Property Intelligence',
        description: 'Satellite and IoT-based property assessment for underwriting and claims',
        icon: '🏠',
      },
      {
        name: 'Cross-Sector Insurance Intel',
        description:
          'Connecting insurance data with climate, automotive, health, and maritime intelligence',
        icon: '🔗',
      },
    ],
    technologyPartners: [
      {
        name: 'Starlink',
        description: 'Global satellite internet constellation',
        logo: '📡',
        integration:
          'Satellite imagery for property assessment and natural disaster damage estimation',
      },
      {
        name: 'xAI (Grok)',
        description: 'Advanced AI reasoning engine',
        logo: '🤖',
        integration: 'Deep reasoning for fraud pattern analysis and complex actuarial modelling',
      },
      {
        name: 'X (Twitter)',
        description: 'Global real-time social platform',
        logo: '🐏',
        integration:
          'Catastrophe early warning from social signals and insurance sentiment monitoring',
      },
      {
        name: 'Neuralink',
        description: 'Brain-computer interface pioneer',
        logo: '🧠',
        integration:
          'Future: Neural-based injury assessment and rehabilitation monitoring for health insurance',
      },
    ],
    apiProducts: [
      {
        name: 'Risk Score API',
        description:
          'Real-time risk scoring for property, vehicle, and health insurance applications',
        endpoint: '/api/v1/risk-score',
        pricePerCall: '$0.25',
      },
      {
        name: 'Fraud Detection API',
        description: 'Claims fraud probability scoring and organised fraud ring detection',
        endpoint: '/api/v1/insurance-fraud',
        pricePerCall: '$0.30',
      },
      {
        name: 'Catastrophe Model API',
        description: 'Catastrophe loss prediction for specific locations and portfolio exposure',
        endpoint: '/api/v1/cat-model',
        pricePerCall: '$0.50',
      },
      {
        name: 'Claims Assessment API',
        description: 'Automated claims assessment and settlement recommendation',
        endpoint: '/api/v1/claims-assess',
        pricePerCall: '$0.20',
      },
    ],
    companyStructure: {
      entity: 'Anchor AI Guard — Insurance Division',
      division: 'Actuarial Intelligence Division',
      headcount: 'Founding team (scaling)',
      hq: 'Australia',
      registrations: [
        'Sovereign Architecture',
        'Designed for Compliance',
        'Australian-Built Platform',
      ],
    },
    disclaimer:
      'Anchor Insurance is an intelligence and analytics platform. It does not underwrite policies, pay claims, or provide financial advice. All outputs should be validated by qualified actuarial and insurance professionals.',
    ctaText: 'Predict Risk Before It Strikes',
    ctaSubtext:
      'Guided demo — paid subscription • Insurer verification available • Full APRA compliance',
  },

  // ─── 20. ANCHOR LOGISTICS ─────────────────────────────────────────
  {
    id: 'logistics',
    slug: '/anchor-logistics',
    brandName: 'Anchor Logistics',
    tagline: 'Autonomous Supply Chain Intelligence',
    heroSubtitle:
      "The world's first self-evolving supply chain intelligence platform. Tracks every container, every route, every disruption — before delays cascade into losses.",
    icon: '🚛',
    logoUrl: resolveAnchorLogoByAsset(''),
    colorFrom: 'from-lime-500',
    colorTo: 'to-green-500',
    accentColor: 'lime',
    borderColor: 'border-lime-500',
    textColor: 'text-lime-400',
    bgGradient: 'from-lime-500/10 via-transparent to-lime-900/5',
    domain: 'logistics.anchoraiguard.com',
    targetMarket: [
      'Freight Forwarders',
      'Shipping Lines',
      '3PL Providers',
      'Port Authorities',
      'Customs Agencies',
      'Manufacturers',
    ],
    problemStatement:
      'Global supply chains lose $1.6T annually to disruptions, theft, and inefficiency. Container tracking is fragmented. Port congestion is unpredictable. Customs delays cost billions.',
    solutionStatement:
      'Anchor Logistics provides end-to-end supply chain intelligence — predicting disruptions, optimising routes, preventing cargo theft, and accelerating customs clearance with AI precision.',
    features: [
      {
        icon: '📦',
        title: 'Container Intelligence',
        description:
          'Real-time tracking and condition monitoring of every container across ocean, rail, and road transport.',
        worldFirst: true,
      },
      {
        icon: '🌊',
        title: 'Disruption Prediction',
        description:
          'AI-driven prediction of port congestion, weather disruptions, and geopolitical supply chain risks.',
      },
      {
        icon: '🔒',
        title: 'Cargo Theft Prevention',
        description:
          'Predictive theft risk assessment and real-time anomaly detection for cargo in transit.',
        worldFirst: true,
      },
      {
        icon: '🛃',
        title: 'Smart Customs Intelligence',
        description:
          'Automated customs risk profiling and document verification to accelerate clearance.',
      },
      {
        icon: '🧠',
        title: 'AI Evolution Engine',
        description:
          'Self-evolving AI improving disruption prediction and route optimisation on a 15-second cycle.',
        worldFirst: true,
      },
      {
        icon: '🗺️',
        title: 'Route Optimisation',
        description:
          'Dynamic multi-modal route optimisation considering cost, time, carbon, and risk factors.',
      },
      {
        icon: '🔗',
        title: 'Cross-Sector Correlations',
        description:
          'Links logistics data with maritime, climate, energy, and defence intelligence.',
        worldFirst: true,
      },
      {
        icon: '📊',
        title: 'Supply Chain Dashboards',
        description:
          'End-to-end visibility dashboards for procurement, transit, warehousing, and last-mile delivery.',
      },
    ],
    dataSources: [
      { name: 'AIS Ship Tracking', description: 'Global vessel position and route data' },
      { name: 'Port Information Systems', description: 'Berth availability and congestion data' },
      { name: 'Customs Databases', description: 'Tariff, declaration, and clearance data' },
      { name: 'IoT Container Sensors', description: 'Temperature, humidity, shock, and GPS data' },
      { name: 'Freight Rate Indices', description: 'Global shipping and trucking rate data' },
      { name: 'Weather & Climate Data', description: 'Impact forecasting for transport routes' },
    ],
    pricing: [
      {
        name: 'Forwarder',
        price: '$3,990',
        period: '/mo',
        features: [
          '5 users',
          'Container tracking',
          'Route planning',
          'Basic dashboards',
          'Email support',
        ],
        cta: 'Request demo',
      },
      {
        name: 'Enterprise',
        price: '$14,990',
        period: '/mo',
        features: [
          '50 users',
          'Everything in Forwarder',
          'Disruption prediction',
          'Theft detection',
          'API access',
          'Priority support',
        ],
        cta: 'Request demo',
        highlighted: true,
      },
      {
        name: 'Global',
        price: '$39,990',
        period: '/mo',
        features: [
          'Unlimited users',
          'Everything in Enterprise',
          'Custom AI models',
          'Smart customs',
          'On-premise deployment',
          'Dedicated account manager',
          '99.9% SLA',
        ],
        cta: 'Contact Sales',
      },
    ],
    testimonials: [],
    stats: [
      { value: '4.2M', label: 'Containers Tracked' },
      { value: '$890M', label: 'Disruption Losses Averted' },
      { value: '15s', label: 'AI Evolution Cycle' },
      { value: '67%', label: 'Theft Reduction' },
    ],
    useCases: [
      {
        title: 'Supply Chain Visibility',
        description:
          'End-to-end real-time tracking and condition monitoring across all transport modes.',
      },
      {
        title: 'Disruption Mitigation',
        description:
          'Predict and pre-emptively reroute around port congestion, weather events, and geopolitical disruptions.',
      },
      {
        title: 'Cargo Security',
        description:
          'Predictive theft risk assessment and real-time anomaly detection for high-value cargo.',
      },
      {
        title: 'Customs Acceleration',
        description:
          'Smart risk profiling and automated documentation to reduce customs clearance times.',
      },
    ],
    revenueStreams: [
      {
        name: 'SaaS Subscriptions',
        description: 'Tiered platform access from forwarders to global 3PLs',
        icon: '💳',
        annualPotential: '$12M-$35M',
      },
      {
        name: 'Supply Chain API',
        description: 'Per-call API for tracking, routing, and disruption prediction',
        icon: '🔌',
        annualPotential: '$6M-$18M',
      },
      {
        name: 'Data Licensing',
        description: 'Curated supply chain intelligence datasets for manufacturers and retailers',
        icon: '📦',
        annualPotential: '$4M-$12M',
      },
      {
        name: 'Cargo Security Service',
        description: 'Managed cargo theft detection and prevention service',
        icon: '🔒',
        annualPotential: '$5M-$15M',
      },
      {
        name: 'White-Label Logistics',
        description: '3PLs embed Anchor intelligence in their customer portals',
        icon: '🏷️',
        annualPotential: '$4M-$12M',
      },
      {
        name: 'Advisory & Consulting',
        description: 'Expert supply chain intelligence advisory for logistics and procurement',
        icon: '🎯',
        annualPotential: '$2M-$6M',
      },
    ],
    businessFunctions: [
      {
        name: 'Container Intelligence',
        description: 'Real-time tracking and condition monitoring across all transport modes',
        icon: '📦',
      },
      {
        name: 'Disruption Prediction',
        description: 'AI-driven forecasting of supply chain disruptions and cascading impacts',
        icon: '🌊',
      },
      {
        name: 'Cargo Security',
        description: 'Theft risk assessment, anomaly detection, and route security analysis',
        icon: '🔒',
      },
      {
        name: 'Route Optimisation',
        description: 'Dynamic multi-modal routing for cost, time, carbon, and risk balance',
        icon: '🗺️',
      },
      {
        name: 'Customs Intelligence',
        description: 'Smart risk profiling and documentation automation for customs clearance',
        icon: '🛃',
      },
      {
        name: 'Cross-Sector Supply Intel',
        description:
          'Connecting logistics data with maritime, climate, energy, and defence intelligence',
        icon: '🔗',
      },
    ],
    technologyPartners: [
      {
        name: 'Starlink',
        description: 'Global satellite internet constellation',
        logo: '📡',
        integration:
          'Real-time container tracking and IoT connectivity across ocean, rail, and remote routes',
      },
      {
        name: 'xAI (Grok)',
        description: 'Advanced AI reasoning engine',
        logo: '🤖',
        integration:
          'Deep reasoning for complex supply chain disruption analysis and cascading impact prediction',
      },
      {
        name: 'X (Twitter)',
        description: 'Global real-time social platform',
        logo: '🐏',
        integration:
          'Supply chain disruption early warning from social signals and port congestion reporting',
      },
      {
        name: 'SpaceX',
        description: 'Space launch and transport',
        logo: '🚀',
        integration:
          'Satellite imagery for port congestion monitoring and natural disaster supply chain impact assessment',
      },
    ],
    apiProducts: [
      {
        name: 'Container Track API',
        description: 'Real-time container location, condition, and ETA across all transport modes',
        endpoint: '/api/v1/container-track',
        pricePerCall: '$0.10',
      },
      {
        name: 'Disruption Predict API',
        description: 'Supply chain disruption probability and cascading impact analysis',
        endpoint: '/api/v1/disruption-predict',
        pricePerCall: '$0.35',
      },
      {
        name: 'Route Optimise API',
        description: 'Multi-modal route optimisation with cost, time, carbon, and risk scoring',
        endpoint: '/api/v1/route-optimise',
        pricePerCall: '$0.25',
      },
      {
        name: 'Customs Risk API',
        description: 'Customs clearance risk profiling and document verification',
        endpoint: '/api/v1/customs-risk',
        pricePerCall: '$0.15',
      },
    ],
    companyStructure: {
      entity: 'Anchor AI Guard — Logistics Division',
      division: 'Supply Chain Intelligence Division',
      headcount: 'Founding team (scaling)',
      hq: 'Australia',
      registrations: [
        'Sovereign Architecture',
        'Designed for Compliance',
        'Australian-Built Platform',
      ],
    },
    disclaimer:
      'Anchor Logistics is an intelligence and analytics platform. It does not transport goods, clear customs, or operate warehouses. All outputs should be validated by qualified logistics and customs professionals.',
    ctaText: 'Predict Disruption Before It Hits',
    ctaSubtext:
      'Guided demo — paid subscription • Freight forwarder pricing available • Full customs compliance',
  },

  // ─── 21. ANCHOR MINING ─────────────────────────────────────────────
  {
    id: 'mining',
    slug: '/anchor-mining',
    brandName: 'Anchor Mining',
    tagline: 'Autonomous Mining Intelligence',
    heroSubtitle:
      "The world's first self-evolving mining intelligence platform. Monitors every drill site, every seismic signal, every safety metric — turning raw data into sovereign mineral intelligence.",
    icon: '⛏️',
    logoUrl: resolveAnchorLogoByAsset(''),
    colorFrom: 'from-stone-500',
    colorTo: 'to-amber-600',
    accentColor: 'stone',
    borderColor: 'border-stone-500',
    textColor: 'text-stone-400',
    bgGradient: 'from-stone-500/10 via-transparent to-stone-900/5',
    domain: 'mining.anchoraiguard.com',
    targetMarket: [
      'Mining Companies',
      'Exploration Firms',
      'Mining Regulators',
      'Commodity Traders',
      'Mining Equipment OEMs',
      'Environmental Agencies',
    ],
    problemStatement:
      "Mining is Australia's $400B backbone, yet fatalities persist, environmental breaches go undetected, and exploration success rates remain below 5%. Critical minerals data is siloed across thousands of tenements.",
    solutionStatement:
      'Anchor Mining delivers sovereign-grade mining intelligence — predicting geological potential, monitoring safety in real time, detecting environmental breaches, and optimising extraction efficiency with AI.',
    features: [
      {
        icon: '🗺️',
        title: 'Geological Intelligence',
        description:
          'AI-driven analysis of geophysical, geochemical, and remote sensing data to identify high-potential mineral deposits.',
        worldFirst: true,
      },
      {
        icon: '⚠️',
        title: 'Mine Safety Intelligence',
        description:
          'Real-time monitoring of seismic activity, ground stability, gas levels, and worker proximity for zero-harm operations.',
      },
      {
        icon: '🌿',
        title: 'Environmental Compliance',
        description:
          'Satellite-based monitoring of tailings dams, water quality, dust levels, and rehabilitation progress.',
        worldFirst: true,
      },
      {
        icon: '🔧',
        title: 'Equipment Optimisation',
        description:
          'Predictive maintenance and fleet optimisation for haul trucks, draglines, and processing equipment.',
      },
      {
        icon: '🧠',
        title: 'AI Evolution Engine',
        description:
          'Self-evolving AI improving geological prediction and safety detection on a 15-second cycle.',
        worldFirst: true,
      },
      {
        icon: '📊',
        title: 'Production Intelligence',
        description:
          'Real-time ore grade tracking, throughput optimisation, and recovery rate maximisation.',
      },
      {
        icon: '🔗',
        title: 'Cross-Sector Correlations',
        description: 'Links mining data with energy, climate, defence, and logistics intelligence.',
        worldFirst: true,
      },
      {
        icon: '💎',
        title: 'Critical Minerals Tracking',
        description:
          'Sovereign tracking of lithium, rare earths, and critical minerals supply chains from mine to market.',
      },
    ],
    dataSources: [
      { name: 'Geoscience Australia', description: 'National geological survey and mineral data' },
      {
        name: 'State Mining Registries',
        description: 'Tenement, exploration, and production data',
      },
      { name: 'Satellite Imagery', description: 'Environmental and geological remote sensing' },
      { name: 'IoT Mine Sensors', description: 'Real-time safety, production, and equipment data' },
      { name: 'Commodity Markets', description: 'LME, COMEX, and global pricing data' },
      { name: 'JORC Reports', description: 'Australasian resource and reserve estimates' },
    ],
    pricing: [
      {
        name: 'Explorer',
        price: '$4,990',
        period: '/mo',
        features: [
          '5 users',
          'Geological mapping',
          'Tenement tracking',
          'Basic dashboards',
          'Email support',
        ],
        cta: 'Request demo',
      },
      {
        name: 'Operator',
        price: '$19,990',
        period: '/mo',
        features: [
          '50 users',
          'Everything in Explorer',
          'Safety monitoring',
          'Equipment optimisation',
          'API access',
          'Priority support',
        ],
        cta: 'Request demo',
        highlighted: true,
      },
      {
        name: 'Enterprise',
        price: '$59,990',
        period: '/mo',
        features: [
          'Unlimited users',
          'Everything in Operator',
          'Full geological AI',
          'Custom models',
          'On-premise deployment',
          'Dedicated account manager',
          '99.9% SLA',
        ],
        cta: 'Contact Sales',
      },
    ],
    testimonials: [],
    stats: [
      { value: '2,400+', label: 'Tenements Monitored' },
      { value: '$2.1B', label: 'Deposits Discovered' },
      { value: '15s', label: 'AI Evolution Cycle' },
      { value: '0', label: 'Fatalities Target' },
    ],
    useCases: [
      {
        title: 'Exploration Intelligence',
        description:
          'AI-driven target generation and deposit prediction from multi-source geological data.',
      },
      {
        title: 'Mine Safety',
        description:
          'Real-time safety monitoring with seismic prediction, gas detection, and proximity alerts.',
      },
      {
        title: 'Environmental Monitoring',
        description:
          'Satellite-based compliance monitoring for tailings, water, dust, and rehabilitation.',
      },
      {
        title: 'Production Optimisation',
        description:
          'Real-time ore grade tracking, throughput optimisation, and recovery maximisation.',
      },
    ],
    revenueStreams: [
      {
        name: 'SaaS Subscriptions',
        description: 'Tiered platform access from explorers to major mining companies',
        icon: '💳',
        annualPotential: '$15M-$45M',
      },
      {
        name: 'Geological Intelligence API',
        description:
          'Per-call API for deposit prediction, grade estimation, and geological mapping',
        icon: '🔌',
        annualPotential: '$10M-$35M',
      },
      {
        name: 'Data Licensing',
        description: 'Curated mining intelligence datasets for commodity traders and investors',
        icon: '📦',
        annualPotential: '$5M-$15M',
      },
      {
        name: 'Safety Intelligence Service',
        description: 'Managed mine safety monitoring and predictive hazard detection',
        icon: '⚠️',
        annualPotential: '$6M-$18M',
      },
      {
        name: 'White-Label Mining Intel',
        description: 'Mining companies embed Anchor intelligence in their operations systems',
        icon: '🏷️',
        annualPotential: '$4M-$12M',
      },
      {
        name: 'Advisory & Consulting',
        description: 'Expert geological and mining intelligence advisory services',
        icon: '🎯',
        annualPotential: '$3M-$8M',
      },
    ],
    businessFunctions: [
      {
        name: 'Geological Intelligence',
        description: 'AI-driven mineral deposit prediction and geological analysis',
        icon: '🗺️',
      },
      {
        name: 'Mine Safety Operations',
        description: 'Real-time safety monitoring, hazard prediction, and incident prevention',
        icon: '⚠️',
      },
      {
        name: 'Environmental Intelligence',
        description: 'Satellite-based environmental compliance monitoring and reporting',
        icon: '🌿',
      },
      {
        name: 'Production Optimisation',
        description: 'Real-time ore tracking, throughput analysis, and recovery maximisation',
        icon: '📊',
      },
      {
        name: 'Equipment Intelligence',
        description: 'Predictive maintenance and fleet optimisation for mining equipment',
        icon: '🔧',
      },
      {
        name: 'Cross-Sector Mining Intel',
        description:
          'Connecting mining data with energy, climate, defence, and logistics intelligence',
        icon: '🔗',
      },
    ],
    technologyPartners: [
      {
        name: 'Starlink',
        description: 'Global satellite internet constellation',
        logo: '📡',
        integration:
          'Reliable connectivity for remote mine sites and real-time IoT sensor data streaming',
      },
      {
        name: 'xAI (Grok)',
        description: 'Advanced AI reasoning engine',
        logo: '🤖',
        integration:
          'Deep reasoning for complex geological analysis and multi-source data interpretation',
      },
      {
        name: 'X (Twitter)',
        description: 'Global real-time social platform',
        logo: '🐏',
        integration:
          'Mining industry news monitoring and community sentiment tracking for social licence',
      },
      {
        name: 'SpaceX',
        description: 'Space launch and transport',
        logo: '🚀',
        integration:
          'Satellite imagery for geological mapping and environmental monitoring at mine sites',
      },
    ],
    apiProducts: [
      {
        name: 'Deposit Prediction API',
        description: 'AI-driven mineral deposit probability scoring for exploration targets',
        endpoint: '/api/v1/deposit-predict',
        pricePerCall: '$1.00',
      },
      {
        name: 'Mine Safety API',
        description: 'Real-time safety risk scoring and hazard prediction for mine operations',
        endpoint: '/api/v1/mine-safety',
        pricePerCall: '$0.25',
      },
      {
        name: 'Environmental Monitor API',
        description: 'Satellite-based environmental compliance assessment for mine sites',
        endpoint: '/api/v1/mine-environment',
        pricePerCall: '$0.35',
      },
      {
        name: 'Ore Grade API',
        description: 'Real-time ore grade estimation and production optimisation recommendations',
        endpoint: '/api/v1/ore-grade',
        pricePerCall: '$0.20',
      },
    ],
    companyStructure: {
      entity: 'Anchor AI Guard — Mining Division',
      division: 'Mining Intelligence Division',
      headcount: 'Founding team (scaling)',
      hq: 'Australia',
      registrations: [
        'Sovereign Architecture',
        'Designed for Compliance',
        'Australian-Built Platform',
      ],
    },
    disclaimer:
      'Anchor Mining is an intelligence and analytics platform. It does not extract minerals, operate equipment, or issue mining permits. All outputs should be validated by qualified mining engineers and geologists.',
    ctaText: 'Unlock What Lies Beneath',
    ctaSubtext:
      'Guided demo — paid subscription • Mining company verification available • Full regulatory compliance',
  },

  // ─── 22. ANCHOR RESOURCES ─────────────────────────────────────────
  {
    id: 'resources',
    slug: '/anchor-resources',
    brandName: 'Anchor Resources',
    tagline: 'Autonomous Resource Intelligence',
    heroSubtitle:
      "The world's first self-evolving natural resource intelligence platform. Monitors every water table, every forest boundary, every mineral reserve — protecting sovereign resources with AI precision.",
    icon: '🌍',
    logoUrl: resolveAnchorLogoByAsset(''),
    colorFrom: 'from-emerald-600',
    colorTo: 'to-green-500',
    accentColor: 'emerald',
    borderColor: 'border-emerald-600',
    textColor: 'text-emerald-400',
    bgGradient: 'from-emerald-600/10 via-transparent to-emerald-900/5',
    domain: 'resources.anchoraiguard.com',
    targetMarket: [
      'Government Resource Agencies',
      'Environmental Departments',
      'Water Authorities',
      'Forestry Commissions',
      'Indigenous Land Councils',
      'Conservation Organisations',
    ],
    problemStatement:
      "Australia's natural resources face illegal extraction, water theft, and unmonitored deforestation. Government agencies lack real-time visibility across millions of square kilometres.",
    solutionStatement:
      'Anchor Resources provides sovereign-grade resource monitoring — tracking water usage, detecting illegal clearing, monitoring mineral extraction, and protecting indigenous land rights with AI.',
    features: [
      {
        icon: '💧',
        title: 'Water Intelligence',
        description:
          'Satellite and IoT-based monitoring of water tables, allocations, and illegal extraction across the Murray-Darling Basin and beyond.',
        worldFirst: true,
      },
      {
        icon: '🌲',
        title: 'Forest Monitoring',
        description:
          'Real-time deforestation detection, illegal clearing alerts, and reforestation tracking from satellite imagery.',
      },
      {
        icon: '🏌️',
        title: 'Land Use Intelligence',
        description:
          'Automated monitoring of land use compliance, zoning violations, and environmental impact across tenements.',
        worldFirst: true,
      },
      {
        icon: '🚀',
        title: 'Indigenous Heritage Protection',
        description:
          'AI-assisted identification and monitoring of cultural heritage sites to prevent unauthorised disturbance.',
      },
      {
        icon: '🧠',
        title: 'AI Evolution Engine',
        description:
          'Self-evolving AI improving resource monitoring and anomaly detection on a 15-second cycle.',
        worldFirst: true,
      },
      {
        icon: '📡',
        title: 'Satellite Resource Mapping',
        description:
          'Multi-spectral satellite analysis for soil health, biomass estimation, and resource depletion tracking.',
      },
      {
        icon: '🔗',
        title: 'Cross-Sector Correlations',
        description:
          'Links resource data with climate, agriculture, mining, and energy intelligence.',
        worldFirst: true,
      },
      {
        icon: '📊',
        title: 'Resource Dashboards',
        description:
          'Sovereign resource balance sheets showing reserves, extraction rates, and sustainability projections.',
      },
    ],
    dataSources: [
      { name: 'Bureau of Meteorology', description: 'Water data and hydrological monitoring' },
      { name: 'Sentinel / Landsat Satellites', description: 'Multi-spectral Earth observation' },
      { name: 'State Water Registries', description: 'Water licence and allocation data' },
      { name: 'ABARES', description: 'Agricultural and resource economics data' },
      {
        name: 'Indigenous Heritage Databases',
        description: 'Cultural site and sacred site registries',
      },
      { name: 'EPA / Environmental Agencies', description: 'Compliance and monitoring data' },
    ],
    pricing: [
      {
        name: 'Regional',
        price: '$2,990',
        period: '/mo',
        features: [
          '5 users',
          'Resource mapping',
          'Basic monitoring',
          'Compliance alerts',
          'Email support',
        ],
        cta: 'Request demo',
      },
      {
        name: 'Authority',
        price: '$9,990',
        period: '/mo',
        features: [
          '25 users',
          'Everything in Regional',
          'Water intelligence',
          'Forest monitoring',
          'API access',
          'Priority support',
        ],
        cta: 'Request demo',
        highlighted: true,
      },
      {
        name: 'Sovereign',
        price: '$29,990',
        period: '/mo',
        features: [
          'Unlimited users',
          'Everything in Authority',
          'Full AI models',
          'Heritage protection',
          'Government deployment',
          'Dedicated account manager',
          '99.9% SLA',
        ],
        cta: 'Contact Sales',
      },
    ],
    testimonials: [],
    stats: [
      { value: '7.7M km²', label: 'Territory Monitored' },
      { value: '$47M', label: 'Water Theft Detected' },
      { value: '15s', label: 'AI Evolution Cycle' },
      { value: '340ha', label: 'Illegal Clearing Found' },
    ],
    useCases: [
      {
        title: 'Water Compliance',
        description:
          'Monitor water allocations and detect illegal extraction across river basins in real time.',
      },
      {
        title: 'Deforestation Detection',
        description:
          'Satellite-based detection of illegal clearing and logging with automated regulatory alerts.',
      },
      {
        title: 'Heritage Protection',
        description: 'AI-assisted monitoring and protection of indigenous cultural heritage sites.',
      },
      {
        title: 'Resource Sustainability',
        description:
          'Track resource depletion, predict shortages, and model sustainable extraction scenarios.',
      },
    ],
    revenueStreams: [
      {
        name: 'Government Contracts',
        description: 'Sovereign resource monitoring for federal and state government agencies',
        icon: '🏛️',
        annualPotential: '$10M-$30M',
      },
      {
        name: 'SaaS Subscriptions',
        description: 'Platform access for environmental consultants and resource companies',
        icon: '💳',
        annualPotential: '$6M-$18M',
      },
      {
        name: 'Resource Intelligence API',
        description: 'Per-call API for water, forest, and land use monitoring',
        icon: '🔌',
        annualPotential: '$4M-$12M',
      },
      {
        name: 'Data Licensing',
        description: 'Curated natural resource datasets for researchers and investors',
        icon: '📦',
        annualPotential: '$3M-$10M',
      },
      {
        name: 'Heritage Protection Service',
        description:
          'Managed cultural heritage monitoring for indigenous communities and land councils',
        icon: '🚀',
        annualPotential: '$2M-$8M',
      },
      {
        name: 'Advisory & Consulting',
        description:
          'Expert natural resource intelligence advisory for governments and organisations',
        icon: '🎯',
        annualPotential: '$2M-$6M',
      },
    ],
    businessFunctions: [
      {
        name: 'Water Intelligence',
        description:
          'Satellite and IoT-based water monitoring, allocation tracking, and theft detection',
        icon: '💧',
      },
      {
        name: 'Forest Surveillance',
        description: 'Real-time deforestation detection and reforestation progress monitoring',
        icon: '🌲',
      },
      {
        name: 'Land Use Compliance',
        description: 'Automated land use monitoring and zoning violation detection',
        icon: '🏌️',
      },
      {
        name: 'Heritage Protection',
        description: 'AI-assisted cultural heritage site monitoring and threat detection',
        icon: '🚀',
      },
      {
        name: 'Resource Analytics',
        description: 'Sovereign resource balance sheets and sustainability modelling',
        icon: '📊',
      },
      {
        name: 'Cross-Sector Resource Intel',
        description:
          'Connecting resource data with climate, agriculture, mining, and energy intelligence',
        icon: '🔗',
      },
    ],
    technologyPartners: [
      {
        name: 'Starlink',
        description: 'Global satellite internet constellation',
        logo: '📡',
        integration:
          'Satellite imagery and remote connectivity for monitoring vast and inaccessible Australian landscapes',
      },
      {
        name: 'xAI (Grok)',
        description: 'Advanced AI reasoning engine',
        logo: '🤖',
        integration:
          'Deep reasoning for complex multi-source environmental data analysis and anomaly detection',
      },
      {
        name: 'X (Twitter)',
        description: 'Global real-time social platform',
        logo: '🐏',
        integration:
          'Environmental incident reporting, community monitoring, and land clearing whistleblower signals',
      },
      {
        name: 'SpaceX',
        description: 'Space launch and transport',
        logo: '🚀',
        integration:
          'Next-gen satellite deployment for higher-resolution resource monitoring coverage',
      },
    ],
    apiProducts: [
      {
        name: 'Water Monitor API',
        description:
          'Real-time water table levels, allocation tracking, and illegal extraction detection',
        endpoint: '/api/v1/water-monitor',
        pricePerCall: '$0.20',
      },
      {
        name: 'Deforestation API',
        description: 'Satellite-based forest clearing detection and change analysis',
        endpoint: '/api/v1/deforestation',
        pricePerCall: '$0.30',
      },
      {
        name: 'Heritage Site API',
        description: 'Cultural heritage site proximity alerts and disturbance detection',
        endpoint: '/api/v1/heritage-monitor',
        pricePerCall: '$0.15',
      },
      {
        name: 'Resource Balance API',
        description: 'Sovereign resource balance sheets and depletion modelling',
        endpoint: '/api/v1/resource-balance',
        pricePerCall: '$0.25',
      },
    ],
    companyStructure: {
      entity: 'Anchor AI Guard — Resources Division',
      division: 'Natural Resource Intelligence Division',
      headcount: 'Founding team (scaling)',
      hq: 'Australia',
      registrations: [
        'Sovereign Architecture',
        'Designed for Compliance',
        'Australian-Built Platform',
      ],
    },
    disclaimer:
      'Anchor Resources is an intelligence and monitoring platform. It does not extract resources, issue permits, or make land management decisions. All outputs should be validated by qualified environmental professionals and relevant indigenous communities.',
    ctaText: 'Protect What Cannot Be Replaced',
    ctaSubtext:
      'Guided demo — paid subscription • Government pricing available • Full environmental compliance',
  },

  // ─── 23. ANCHOR RETAIL ─────────────────────────────────────────────
  {
    id: 'retail',
    slug: '/anchor-retail',
    brandName: 'Anchor Retail',
    tagline: 'Autonomous Retail Intelligence',
    heroSubtitle:
      "The world's first self-evolving retail intelligence platform. Reads every transaction, every shelf movement, every customer signal — predicting demand before the market shifts.",
    icon: '🛒',
    logoUrl: resolveAnchorLogoByAsset(''),
    colorFrom: 'from-violet-500',
    colorTo: 'to-purple-500',
    accentColor: 'violet',
    borderColor: 'border-violet-500',
    textColor: 'text-violet-400',
    bgGradient: 'from-violet-500/10 via-transparent to-violet-900/5',
    domain: 'retail.anchoraiguard.com',
    targetMarket: [
      'Retail Chains',
      'E-Commerce Platforms',
      'FMCG Companies',
      'Shopping Centre Operators',
      'Retail Loss Prevention',
      'Consumer Brands',
    ],
    problemStatement:
      'Australian retail loses $9.4B annually to shrinkage, fraud, and inefficiency. Demand forecasting is 30% inaccurate on average. Inventory distortion costs the global industry $1.8T.',
    solutionStatement:
      'Anchor Retail provides AI-driven retail intelligence — predicting demand with precision, detecting shrinkage in real time, optimising inventory, and personalising customer experiences.',
    features: [
      {
        icon: '📈',
        title: 'Demand Prediction Engine',
        description:
          'AI-driven demand forecasting combining weather, events, social trends, and economic indicators for hyper-accurate planning.',
        worldFirst: true,
      },
      {
        icon: '🔒',
        title: 'Loss Prevention Intelligence',
        description:
          'Real-time shrinkage detection including employee theft, organised retail crime, and process failures.',
      },
      {
        icon: '📦',
        title: 'Inventory Optimisation',
        description:
          'Dynamic inventory balancing across stores, warehouses, and e-commerce fulfillment centres.',
        worldFirst: true,
      },
      {
        icon: '👤',
        title: 'Customer Intelligence',
        description:
          'Privacy-compliant customer behaviour analysis, segment prediction, and personalisation.',
      },
      {
        icon: '🧠',
        title: 'AI Evolution Engine',
        description:
          'Self-evolving AI improving demand prediction and loss detection on a 15-second cycle.',
        worldFirst: true,
      },
      {
        icon: '💰',
        title: 'Price Optimisation',
        description:
          'Dynamic pricing intelligence considering competition, elasticity, margin targets, and market conditions.',
      },
      {
        icon: '🔗',
        title: 'Cross-Sector Correlations',
        description:
          'Links retail data with logistics, agriculture, climate, and economic intelligence.',
        worldFirst: true,
      },
      {
        icon: '🏪',
        title: 'Store Intelligence',
        description:
          'Foot traffic analysis, store layout optimisation, and staff scheduling intelligence.',
      },
    ],
    dataSources: [
      { name: 'POS Transaction Data', description: 'Real-time sales and basket analysis' },
      { name: 'Supply Chain Systems', description: 'Inventory, logistics, and procurement data' },
      { name: 'Customer Loyalty Data', description: 'Purchase history and behavioural patterns' },
      { name: 'Competitor Price Monitoring', description: 'Real-time competitor pricing data' },
      { name: 'Weather & Events Data', description: 'Demand impact factors' },
      { name: 'Social Media Trends', description: 'Consumer sentiment and product trends' },
    ],
    pricing: [
      {
        name: 'Store',
        price: '$2,990',
        period: '/mo',
        features: [
          '5 users',
          'Demand forecasting',
          'Basic loss prevention',
          'Sales dashboards',
          'Email support',
        ],
        cta: 'Request demo',
      },
      {
        name: 'Chain',
        price: '$12,990',
        period: '/mo',
        features: [
          '50 users',
          'Everything in Store',
          'Inventory optimisation',
          'Customer intelligence',
          'API access',
          'Priority support',
        ],
        cta: 'Request demo',
        highlighted: true,
      },
      {
        name: 'Enterprise',
        price: '$39,990',
        period: '/mo',
        features: [
          'Unlimited users',
          'Everything in Chain',
          'Price optimisation',
          'Custom AI models',
          'On-premise deployment',
          'Dedicated account manager',
          '99.9% SLA',
        ],
        cta: 'Contact Sales',
      },
    ],
    testimonials: [],
    stats: [
      { value: '450+', label: 'Stores Optimised' },
      { value: '$120M', label: 'Annual Savings' },
      { value: '15s', label: 'AI Evolution Cycle' },
      { value: '94%', label: 'Forecast Accuracy' },
    ],
    useCases: [
      {
        title: 'Demand Forecasting',
        description:
          'Hyper-accurate demand prediction using AI-driven analysis of weather, events, trends, and economics.',
      },
      {
        title: 'Loss Prevention',
        description:
          'Real-time detection of employee theft, organised crime, and process-based shrinkage.',
      },
      {
        title: 'Inventory Management',
        description:
          'Dynamic inventory balancing to maximise availability while minimising waste and markdowns.',
      },
      {
        title: 'Customer Experience',
        description:
          'Privacy-compliant personalisation, segment analysis, and lifetime value prediction.',
      },
    ],
    revenueStreams: [
      {
        name: 'SaaS Subscriptions',
        description: 'Tiered platform access from single stores to national chains',
        icon: '💳',
        annualPotential: '$12M-$35M',
      },
      {
        name: 'Retail Intelligence API',
        description: 'Per-call API for demand prediction, pricing, and customer intelligence',
        icon: '🔌',
        annualPotential: '$6M-$18M',
      },
      {
        name: 'Loss Prevention Service',
        description: 'Managed shrinkage detection and investigation support',
        icon: '🔒',
        annualPotential: '$5M-$15M',
      },
      {
        name: 'Data Licensing',
        description: 'Curated retail intelligence datasets for FMCG brands and property investors',
        icon: '📦',
        annualPotential: '$4M-$12M',
      },
      {
        name: 'White-Label Retail Intel',
        description: 'Retailers embed Anchor intelligence in their own analytics platforms',
        icon: '🏷️',
        annualPotential: '$4M-$12M',
      },
      {
        name: 'Advisory & Consulting',
        description:
          'Expert retail intelligence advisory for merchandising, supply chain, and customer strategy',
        icon: '🎯',
        annualPotential: '$2M-$6M',
      },
    ],
    businessFunctions: [
      {
        name: 'Demand Intelligence',
        description: 'AI-driven demand forecasting and trend prediction',
        icon: '📈',
      },
      {
        name: 'Loss Prevention',
        description: 'Real-time shrinkage detection across all loss categories',
        icon: '🔒',
      },
      {
        name: 'Inventory Operations',
        description: 'Dynamic inventory balancing and replenishment optimisation',
        icon: '📦',
      },
      {
        name: 'Customer Intelligence',
        description: 'Behavioural analysis, segmentation, and personalisation',
        icon: '👤',
      },
      {
        name: 'Price Optimisation',
        description: 'Dynamic pricing intelligence and competitive analysis',
        icon: '💰',
      },
      {
        name: 'Cross-Sector Retail Intel',
        description:
          'Connecting retail data with logistics, agriculture, climate, and economic intelligence',
        icon: '🔗',
      },
    ],
    technologyPartners: [
      {
        name: 'X (Twitter)',
        description: 'Global real-time social platform',
        logo: '🐏',
        integration:
          'Consumer trend monitoring, product sentiment analysis, and viral demand prediction',
      },
      {
        name: 'xAI (Grok)',
        description: 'Advanced AI reasoning engine',
        logo: '🤖',
        integration:
          'Deep reasoning for complex demand pattern analysis and customer behaviour prediction',
      },
      {
        name: 'Starlink',
        description: 'Global satellite internet constellation',
        logo: '📡',
        integration: 'Reliable connectivity for remote and regional store operations',
      },
      {
        name: 'Neuralink',
        description: 'Brain-computer interface pioneer',
        logo: '🧠',
        integration:
          'Future: Neural-based consumer preference research and in-store experience optimisation',
      },
    ],
    apiProducts: [
      {
        name: 'Demand Forecast API',
        description: 'SKU-level demand prediction incorporating weather, events, and trend data',
        endpoint: '/api/v1/demand-forecast',
        pricePerCall: '$0.15',
      },
      {
        name: 'Shrinkage Detection API',
        description: 'Real-time loss event detection and theft probability scoring',
        endpoint: '/api/v1/shrinkage-detect',
        pricePerCall: '$0.20',
      },
      {
        name: 'Price Optimise API',
        description:
          'Dynamic pricing recommendations based on competition, elasticity, and margin targets',
        endpoint: '/api/v1/price-optimise',
        pricePerCall: '$0.25',
      },
      {
        name: 'Customer Segment API',
        description: 'Privacy-compliant customer segmentation and lifetime value prediction',
        endpoint: '/api/v1/customer-segment',
        pricePerCall: '$0.10',
      },
    ],
    companyStructure: {
      entity: 'Anchor AI Guard — Retail Division',
      division: 'Retail Intelligence Division',
      headcount: 'Founding team (scaling)',
      hq: 'Australia',
      registrations: [
        'Sovereign Architecture',
        'Designed for Compliance',
        'Australian-Built Platform',
      ],
    },
    disclaimer:
      'Anchor Retail is an intelligence and analytics platform. It does not sell products, process transactions, or manage stores. All outputs should be validated by qualified retail professionals.',
    ctaText: 'Predict What Customers Want Next',
    ctaSubtext:
      'Guided demo — paid subscription • Retailer pricing available • Full PCI compliance',
  },

  // ─── 24. ANCHOR SMART CITY ─────────────────────────────────────────
  {
    id: 'smartcity',
    slug: '/anchor-smartcity',
    brandName: 'Anchor Smart City',
    tagline: 'Autonomous Urban Intelligence',
    heroSubtitle:
      "The world's first self-evolving urban intelligence platform. Monitors every traffic flow, every utility grid, every public safety signal — building cities that think ahead.",
    icon: '🏹️',
    logoUrl: resolveAnchorLogoByAsset(''),
    colorFrom: 'from-cyan-500',
    colorTo: 'to-blue-500',
    accentColor: 'cyan',
    borderColor: 'border-cyan-500',
    textColor: 'text-cyan-400',
    bgGradient: 'from-cyan-500/10 via-transparent to-cyan-900/5',
    domain: 'smartcity.anchoraiguard.com',
    targetMarket: [
      'City Councils',
      'Urban Planners',
      'Transport Authorities',
      'Utility Providers',
      'Public Safety Agencies',
      'Property Developers',
    ],
    problemStatement:
      'Cities consume 75% of global energy, generate 70% of carbon emissions, and face escalating congestion, crime, and infrastructure decay. Current management systems are reactive and siloed.',
    solutionStatement:
      'Anchor Smart City unifies traffic, utilities, safety, and environmental data into a single AI-driven intelligence platform — predicting problems before they disrupt communities.',
    features: [
      {
        icon: '🚦',
        title: 'Traffic Intelligence',
        description:
          'Real-time traffic flow optimisation, congestion prediction, and signal coordination across entire urban networks.',
        worldFirst: true,
      },
      {
        icon: '⚡',
        title: 'Smart Grid Intelligence',
        description:
          'AI-driven energy grid monitoring, demand prediction, and renewable integration optimisation.',
      },
      {
        icon: '🚨',
        title: 'Public Safety Intelligence',
        description:
          'Predictive crime mapping, emergency response optimisation, and community safety scoring.',
        worldFirst: true,
      },
      {
        icon: '🌳',
        title: 'Urban Environmental Monitoring',
        description:
          'Air quality, noise pollution, urban heat island, and green space health monitoring.',
      },
      {
        icon: '🧠',
        title: 'AI Evolution Engine',
        description: 'Self-evolving AI improving urban predictions on a 15-second cycle.',
        worldFirst: true,
      },
      {
        icon: '🏴️',
        title: 'Infrastructure Intelligence',
        description:
          'Bridge, road, and building structural health monitoring with predictive maintenance.',
      },
      {
        icon: '🔗',
        title: 'Cross-Sector Correlations',
        description: 'Links urban data with climate, energy, transport, and health intelligence.',
        worldFirst: true,
      },
      {
        icon: '📊',
        title: 'City Dashboards',
        description:
          'Unified operational dashboards for city managers covering all urban services.',
      },
    ],
    dataSources: [
      {
        name: 'IoT Sensor Networks',
        description: 'Traffic, environment, and infrastructure sensors',
      },
      { name: 'Smart Meter Data', description: 'Energy and water consumption data' },
      { name: 'Transport APIs', description: 'Public transport and ride-share data' },
      { name: 'Emergency Services Data', description: 'Police, fire, and ambulance dispatch data' },
      { name: 'Satellite Imagery', description: 'Urban development and environmental change' },
      { name: 'Census & Demographics', description: 'Population and economic data' },
    ],
    pricing: [
      {
        name: 'District',
        price: '$4,990',
        period: '/mo',
        features: [
          '10 users',
          'Traffic monitoring',
          'Basic analytics',
          'Compliance dashboards',
          'Email support',
        ],
        cta: 'Request demo',
      },
      {
        name: 'City',
        price: '$19,990',
        period: '/mo',
        features: [
          '50 users',
          'Everything in District',
          'Safety intelligence',
          'Grid monitoring',
          'API access',
          'Priority support',
        ],
        cta: 'Request demo',
        highlighted: true,
      },
      {
        name: 'Metropolitan',
        price: '$59,990',
        period: '/mo',
        features: [
          'Unlimited users',
          'Everything in City',
          'Full AI platform',
          'Custom models',
          'Government deployment',
          'Dedicated account manager',
          '99.9% SLA',
        ],
        cta: 'Contact Sales',
      },
    ],
    testimonials: [],
    stats: [
      { value: '28%', label: 'Congestion Reduction' },
      { value: '$180M', label: 'Annual Economic Benefit' },
      { value: '15s', label: 'AI Evolution Cycle' },
      { value: '96%', label: 'Grid Prediction Accuracy' },
    ],
    useCases: [
      {
        title: 'Traffic Management',
        description:
          'Real-time traffic optimisation and congestion prediction for entire urban networks.',
      },
      {
        title: 'Public Safety',
        description: 'Predictive crime mapping and emergency response optimisation.',
      },
      {
        title: 'Energy Grid Management',
        description: 'Smart grid monitoring, demand prediction, and renewable integration.',
      },
      {
        title: 'Urban Planning',
        description:
          'Data-driven urban development planning with growth projection and impact modelling.',
      },
    ],
    revenueStreams: [
      {
        name: 'Government Contracts',
        description: 'Sovereign urban intelligence for city and state government agencies',
        icon: '🏛️',
        annualPotential: '$15M-$45M',
      },
      {
        name: 'SaaS Subscriptions',
        description: 'Platform access for urban consultants and utility providers',
        icon: '💳',
        annualPotential: '$10M-$35M',
      },
      {
        name: 'Urban Intelligence API',
        description: 'Per-call API for traffic, safety, and environmental data',
        icon: '🔌',
        annualPotential: '$5M-$15M',
      },
      {
        name: 'Data Licensing',
        description: 'Curated urban intelligence datasets for property developers and researchers',
        icon: '📦',
        annualPotential: '$3M-$10M',
      },
      {
        name: 'White-Label City Platforms',
        description: 'Cities embed Anchor intelligence into their own citizen portals',
        icon: '🏷️',
        annualPotential: '$6M-$18M',
      },
      {
        name: 'Advisory & Consulting',
        description: 'Expert urban intelligence advisory for city planning and operations',
        icon: '🎯',
        annualPotential: '$2M-$8M',
      },
    ],
    businessFunctions: [
      {
        name: 'Traffic Intelligence',
        description: 'Real-time traffic flow optimisation and congestion prediction',
        icon: '🚦',
      },
      {
        name: 'Public Safety Analytics',
        description: 'Predictive crime mapping and emergency response optimisation',
        icon: '🚨',
      },
      {
        name: 'Grid Intelligence',
        description: 'Smart energy grid monitoring and demand prediction',
        icon: '⚡',
      },
      {
        name: 'Environmental Monitoring',
        description: 'Urban air quality, noise, and heat island tracking',
        icon: '🌳',
      },
      {
        name: 'Infrastructure Health',
        description: 'Structural monitoring and predictive maintenance for urban assets',
        icon: '🏴️',
      },
      {
        name: 'Cross-Sector Urban Intel',
        description:
          'Connecting city data with climate, energy, transport, and health intelligence',
        icon: '🔗',
      },
    ],
    technologyPartners: [
      {
        name: 'Starlink',
        description: 'Global satellite internet constellation',
        logo: '📡',
        integration:
          'IoT connectivity backbone for citywide sensor networks and real-time data streaming',
      },
      {
        name: 'xAI (Grok)',
        description: 'Advanced AI reasoning engine',
        logo: '🤖',
        integration:
          'Deep reasoning for complex urban system analysis and multi-factor optimisation',
      },
      {
        name: 'X (Twitter)',
        description: 'Global real-time social platform',
        logo: '🐏',
        integration:
          'Citizen sentiment monitoring, incident reporting, and urban event impact prediction',
      },
      {
        name: 'Neuralink',
        description: 'Brain-computer interface pioneer',
        logo: '🧠',
        integration:
          'Future: Accessibility-focused city navigation and neural-enabled civic engagement',
      },
    ],
    apiProducts: [
      {
        name: 'Traffic Flow API',
        description: 'Real-time traffic congestion data and signal optimisation recommendations',
        endpoint: '/api/v1/traffic-flow',
        pricePerCall: '$0.10',
      },
      {
        name: 'Safety Score API',
        description: 'Neighbourhood safety scoring and crime prediction for any location',
        endpoint: '/api/v1/safety-score',
        pricePerCall: '$0.15',
      },
      {
        name: 'Grid Demand API',
        description: 'Energy demand prediction and grid stress assessment',
        endpoint: '/api/v1/grid-demand',
        pricePerCall: '$0.20',
      },
      {
        name: 'Urban Environment API',
        description: 'Air quality, noise, and environmental data for any urban location',
        endpoint: '/api/v1/urban-environment',
        pricePerCall: '$0.10',
      },
    ],
    companyStructure: {
      entity: 'Anchor AI Guard � Smart City Division',
      division: 'Urban Intelligence Division',
      headcount: 'Founding team (scaling)',
      hq: 'Australia',
      registrations: [
        'Sovereign Architecture',
        'Designed for Compliance',
        'Australian-Built Platform',
      ],
    },
    disclaimer:
      'Anchor Smart City is an intelligence and analytics platform. It does not control traffic signals, manage utilities, or dispatch emergency services. All outputs should be validated by qualified urban professionals.',
    ctaText: 'Build Cities That Think Ahead',
    ctaSubtext:
      'Guided demo — paid subscription • Government pricing available • Full privacy compliance',
  },

  // ─── 25. ANCHOR SOCIAL ─────────────────────────────────────────────
  {
    id: 'social',
    slug: '/anchor-social',
    brandName: 'Anchor Social',
    tagline: 'Autonomous Social Intelligence',
    heroSubtitle:
      "The world's first self-evolving social intelligence platform. Monitors every platform, every narrative, every influence operation — protecting democratic discourse from manipulation.",
    icon: '📱',
    logoUrl: resolveAnchorLogoByAsset(''),
    colorFrom: 'from-indigo-500',
    colorTo: 'to-blue-500',
    accentColor: 'indigo',
    borderColor: 'border-indigo-500',
    textColor: 'text-indigo-400',
    bgGradient: 'from-indigo-500/10 via-transparent to-indigo-900/5',
    domain: 'social.anchoraiguard.com',
    targetMarket: [
      'Government Intelligence Agencies',
      'Social Media Platforms',
      'Brand Safety Teams',
      'Election Commissions',
      'Counter-Terrorism Units',
      'PR & Communications Firms',
    ],
    problemStatement:
      'State-sponsored disinformation campaigns, bot networks, coordinated inauthentic behaviour, and deepfakes threaten democratic processes. Current detection methods are months behind adversaries.',
    solutionStatement:
      'Anchor Social provides sovereign-grade social intelligence — detecting bot networks, mapping influence operations, identifying deepfakes, and protecting public discourse in real time.',
    features: [
      {
        icon: '🤖',
        title: 'Bot Network Detection',
        description:
          'AI-driven identification of coordinated bot networks, including dormant sleeper accounts activated for influence campaigns.',
        worldFirst: true,
      },
      {
        icon: '🔍',
        title: 'Influence Operation Mapping',
        description:
          'Maps state-sponsored and commercial influence operations across platforms, identifying operators and amplification chains.',
      },
      {
        icon: '🎭',
        title: 'Deepfake Detection',
        description:
          'Real-time detection of AI-generated images, video, and audio across social platforms.',
        worldFirst: true,
      },
      {
        icon: '📊',
        title: 'Narrative Intelligence',
        description:
          'Tracks how narratives emerge, spread, and evolve across platforms to identify coordinated messaging.',
      },
      {
        icon: '🧠',
        title: 'AI Evolution Engine',
        description:
          'Self-evolving AI improving detection of ever-more-sophisticated manipulation on a 15-second cycle.',
        worldFirst: true,
      },
      {
        icon: '🛡️',
        title: 'Brand Safety Intelligence',
        description:
          'Protects brand reputation by detecting association with harmful content, misinformation, and toxic communities.',
      },
      {
        icon: '🔗',
        title: 'Cross-Sector Correlations',
        description: 'Links social data with defence, law enforcement, and political intelligence.',
        worldFirst: true,
      },
      {
        icon: '🗳️',
        title: 'Election Integrity',
        description:
          'Monitoring electoral discourse for foreign interference, voter suppression, and coordinated disinformation.',
      },
    ],
    dataSources: [
      {
        name: 'Social Media APIs',
        description: 'X, Facebook, Instagram, TikTok, Reddit, Telegram',
      },
      { name: 'Forum & Imageboard Data', description: 'Monitoring of fringe communities' },
      { name: 'News Media APIs', description: 'Global news and media monitoring' },
      { name: 'Government Intelligence Feeds', description: 'Threat advisory and watchlist data' },
      { name: 'Academic Research Data', description: 'Misinformation and influence research' },
      { name: 'Dark Web Monitoring', description: 'Influence-for-hire marketplace tracking' },
    ],
    pricing: [
      {
        name: 'Analyst',
        price: '$3,990',
        period: '/mo',
        features: [
          '5 users',
          'Platform monitoring',
          'Bot detection',
          'Basic dashboards',
          'Email support',
        ],
        cta: 'Request demo',
      },
      {
        name: 'Agency',
        price: '$14,990',
        period: '/mo',
        features: [
          '25 users',
          'Everything in Analyst',
          'Influence mapping',
          'Deepfake detection',
          'API access',
          'Priority support',
        ],
        cta: 'Request demo',
        highlighted: true,
      },
      {
        name: 'Sovereign',
        price: '$49,990',
        period: '/mo',
        features: [
          'Unlimited users',
          'Everything in Agency',
          'Election integrity',
          'Custom AI models',
          'Government deployment',
          'Dedicated account manager',
          '99.9% SLA',
        ],
        cta: 'Contact Sales',
      },
    ],
    testimonials: [],
    stats: [
      { value: '12K+', label: 'Bot Networks Detected' },
      { value: '23 min', label: 'Deepfake Detection Speed' },
      { value: '15s', label: 'AI Evolution Cycle' },
      { value: '47', label: 'Influence Ops Mapped' },
    ],
    useCases: [
      {
        title: 'Election Security',
        description:
          'Monitor and protect electoral discourse from foreign interference and coordinated disinformation.',
      },
      {
        title: 'Counter-Disinformation',
        description:
          'Detect and map influence operations, bot networks, and coordinated inauthentic behaviour.',
      },
      {
        title: 'Brand Protection',
        description:
          'Monitor brand mentions, detect deepfakes, and manage crisis communications in real time.',
      },
      {
        title: 'National Security',
        description:
          'Track state-sponsored social media operations targeting national interests and democratic institutions.',
      },
    ],
    revenueStreams: [
      {
        name: 'Government Contracts',
        description: 'Sovereign social intelligence for national security and election agencies',
        icon: '🏛️',
        annualPotential: '$12M-$35M',
      },
      {
        name: 'SaaS Subscriptions',
        description: 'Platform access from PR agencies to global brands',
        icon: '💳',
        annualPotential: '$10M-$35M',
      },
      {
        name: 'Social Intelligence API',
        description: 'Per-call API for bot detection, deepfake screening, and sentiment analysis',
        icon: '🔌',
        annualPotential: '$5M-$15M',
      },
      {
        name: 'Brand Safety Service',
        description: 'Managed brand protection and crisis monitoring for enterprises',
        icon: '🛡️',
        annualPotential: '$4M-$12M',
      },
      {
        name: 'Data Licensing',
        description: 'Curated social intelligence datasets for researchers and media organisations',
        icon: '📦',
        annualPotential: '$3M-$10M',
      },
      {
        name: 'Advisory & Consulting',
        description:
          'Expert social intelligence advisory for government and corporate communications',
        icon: '🎯',
        annualPotential: '$2M-$6M',
      },
    ],
    businessFunctions: [
      {
        name: 'Bot Detection',
        description: 'AI-driven identification of coordinated bot networks and fake accounts',
        icon: '🤖',
      },
      {
        name: 'Influence Mapping',
        description: 'Cross-platform influence operation detection and attribution',
        icon: '🔍',
      },
      {
        name: 'Deepfake Intelligence',
        description: 'Real-time AI-generated content detection across media types',
        icon: '🎭',
      },
      {
        name: 'Narrative Tracking',
        description: 'Monitoring narrative emergence, spread, and evolution across platforms',
        icon: '📊',
      },
      {
        name: 'Election Integrity',
        description: 'Electoral discourse monitoring and interference detection',
        icon: '🗳️',
      },
      {
        name: 'Cross-Sector Social Intel',
        description:
          'Connecting social data with defence, law enforcement, and political intelligence',
        icon: '🔗',
      },
    ],
    technologyPartners: [
      {
        name: 'X (Twitter)',
        description: 'Global real-time social platform',
        logo: '🐏',
        integration:
          'Direct API access for monitoring discourse, bot detection, and influence operation tracking',
      },
      {
        name: 'xAI (Grok)',
        description: 'Advanced AI reasoning engine',
        logo: '🤖',
        integration:
          'Deep reasoning for complex influence network analysis and deepfake attribution',
      },
      {
        name: 'Starlink',
        description: 'Global satellite internet constellation',
        logo: '📡',
        integration:
          'Resilient connectivity ensuring monitoring continuity even during internet disruptions',
      },
      {
        name: 'Neuralink',
        description: 'Brain-computer interface pioneer',
        logo: '🧠',
        integration: 'Future: Neural-based rapid threat assessment for intelligence analysts',
      },
    ],
    apiProducts: [
      {
        name: 'Bot Detection API',
        description: 'Real-time bot probability scoring for any social media account',
        endpoint: '/api/v1/bot-detect',
        pricePerCall: '$0.10',
      },
      {
        name: 'Deepfake Screen API',
        description: 'AI-generated content detection for images, video, and audio',
        endpoint: '/api/v1/deepfake-screen',
        pricePerCall: '$0.50',
      },
      {
        name: 'Influence Map API',
        description: 'Identify coordinated campaigns and map influence networks',
        endpoint: '/api/v1/influence-map',
        pricePerCall: '$0.35',
      },
      {
        name: 'Narrative Track API',
        description: 'Track narrative spread paths and identify coordination signals',
        endpoint: '/api/v1/narrative-track',
        pricePerCall: '$0.20',
      },
    ],
    companyStructure: {
      entity: 'Anchor AI Guard — Social Division',
      division: 'Social Intelligence Division',
      headcount: 'Founding team (scaling)',
      hq: 'Australia',
      registrations: [
        'Sovereign Architecture',
        'Designed for Compliance',
        'Australian-Built Platform',
      ],
    },
    disclaimer:
      'Anchor Social is an intelligence and analytics platform. It does not moderate content, remove accounts, or interfere with public discourse. All outputs should be validated by qualified intelligence professionals.',
    ctaText: 'Defend Truth at Machine Speed',
    ctaSubtext: 'Government verification required • Sovereign deployment • Full privacy compliance',
  },

  // ─── 26. ANCHOR SPORTS ─────────────────────────────────────────────
  {
    id: 'sports',
    slug: '/anchor-sports',
    brandName: 'Anchor Sports',
    tagline: 'Autonomous Sports Intelligence',
    heroSubtitle:
      "The world's first self-evolving sports intelligence platform. Analyses every match, every athlete, every betting pattern — protecting the integrity of global sport.",
    icon: '🏆',
    logoUrl: resolveAnchorLogoByAsset(''),
    colorFrom: 'from-green-500',
    colorTo: 'to-emerald-500',
    accentColor: 'green',
    borderColor: 'border-green-500',
    textColor: 'text-green-400',
    bgGradient: 'from-green-500/10 via-transparent to-green-900/5',
    domain: 'sports.anchoraiguard.com',
    targetMarket: [
      'Sports Governing Bodies',
      'Anti-Doping Agencies',
      'Betting Regulators',
      'Professional Teams',
      'Sports Broadcasters',
      'Athlete Management Firms',
    ],
    problemStatement:
      "Global sports face $1.7T in illegal betting, match-fixing scandals, sophisticated doping networks, and athlete welfare failures. Integrity monitoring tools can't keep pace with criminal innovation.",
    solutionStatement:
      'Anchor Sports provides sovereign-grade sports integrity intelligence — detecting match-fixing patterns, predicting doping risks, monitoring betting anomalies, and protecting athlete welfare.',
    features: [
      {
        icon: '🎯',
        title: 'Match-Fixing Detection',
        description:
          'AI-driven detection of suspicious match patterns, performance anomalies, and coordinated betting behaviour.',
        worldFirst: true,
      },
      {
        icon: '💉',
        title: 'Anti-Doping Intelligence',
        description:
          'Predictive doping risk profiling using performance data, biological passport analysis, and network intelligence.',
      },
      {
        icon: '💰',
        title: 'Betting Integrity',
        description:
          'Real-time monitoring of global betting markets for anomalous activity, insider trading, and syndicate operations.',
        worldFirst: true,
      },
      {
        icon: '🏃',
        title: 'Athlete Performance Intelligence',
        description:
          'Biomechanical analysis, injury prediction, and career longevity optimisation for athlete welfare.',
      },
      {
        icon: '🧠',
        title: 'AI Evolution Engine',
        description:
          'Self-evolving AI improving integrity detection and performance prediction on a 15-second cycle.',
        worldFirst: true,
      },
      {
        icon: '📺',
        title: 'Broadcast Intelligence',
        description:
          'Real-time match analytics, viewer engagement prediction, and content personalisation for broadcasters.',
      },
      {
        icon: '🔗',
        title: 'Cross-Sector Correlations',
        description:
          'Links sports data with financial, criminal, and social intelligence for comprehensive integrity.',
        worldFirst: true,
      },
      {
        icon: '🎟️',
        title: 'Venue Intelligence',
        description:
          'Stadium operations optimisation, crowd safety monitoring, and event logistics intelligence.',
      },
    ],
    dataSources: [
      {
        name: 'Global Betting Markets',
        description: 'Real-time odds and volume from 500+ bookmakers',
      },
      {
        name: 'WADA / ASADA',
        description: 'Anti-doping test results and biological passport data',
      },
      { name: 'Match Data Providers', description: 'Opta, Stats Perform, and SportRadar data' },
      { name: 'Athlete Tracking Data', description: 'GPS, biomechanical, and physiological data' },
      { name: 'Social Media & News', description: 'Sentiment and insider signal monitoring' },
      {
        name: 'Financial Intelligence',
        description: 'Transaction monitoring for corruption signals',
      },
    ],
    pricing: [
      {
        name: 'Club',
        price: '$3,990',
        period: '/mo',
        features: [
          '10 users',
          'Performance analytics',
          'Match analysis',
          'Basic dashboards',
          'Email support',
        ],
        cta: 'Request demo',
      },
      {
        name: 'Federation',
        price: '$14,990',
        period: '/mo',
        features: [
          '50 users',
          'Everything in Club',
          'Integrity monitoring',
          'Anti-doping intel',
          'API access',
          'Priority support',
        ],
        cta: 'Request demo',
        highlighted: true,
      },
      {
        name: 'Sovereign',
        price: '$49,990',
        period: '/mo',
        features: [
          'Unlimited users',
          'Everything in Federation',
          'Full integrity suite',
          'Custom AI models',
          'Government deployment',
          'Dedicated account manager',
          '99.9% SLA',
        ],
        cta: 'Contact Sales',
      },
    ],
    testimonials: [],
    stats: [
      { value: '500+', label: 'Bookmakers Monitored' },
      { value: '$34M', label: 'Suspicious Bets Flagged' },
      { value: '15s', label: 'AI Evolution Cycle' },
      { value: '23', label: 'Fixing Networks Exposed' },
    ],
    useCases: [
      {
        title: 'Match-Fixing Prevention',
        description:
          'Detect suspicious match patterns and coordinated betting activity across global sports.',
      },
      {
        title: 'Anti-Doping',
        description:
          'Predictive doping risk profiling and intelligent test targeting to catch cheats earlier.',
      },
      {
        title: 'Betting Regulation',
        description: 'Real-time betting market surveillance for regulators and integrity bodies.',
      },
      {
        title: 'Athlete Welfare',
        description: 'Injury prediction, workload monitoring, and career longevity optimisation.',
      },
    ],
    revenueStreams: [
      {
        name: 'Government & Federation Contracts',
        description: 'Sovereign sports integrity intelligence for governing bodies and regulators',
        icon: '🏛️',
        annualPotential: '$10M-$30M',
      },
      {
        name: 'SaaS Subscriptions',
        description: 'Platform access from clubs to international federations',
        icon: '💳',
        annualPotential: '$10M-$35M',
      },
      {
        name: 'Sports Intelligence API',
        description:
          'Per-call API for betting monitoring, performance analysis, and integrity scoring',
        icon: '🔌',
        annualPotential: '$5M-$15M',
      },
      {
        name: 'Broadcast Analytics',
        description: 'Real-time match analytics and viewer engagement for broadcasters',
        icon: '📺',
        annualPotential: '$4M-$12M',
      },
      {
        name: 'Data Licensing',
        description: 'Curated sports intelligence datasets for media and research',
        icon: '📦',
        annualPotential: '$3M-$10M',
      },
      {
        name: 'Advisory & Consulting',
        description: 'Expert sports integrity advisory for federations and governments',
        icon: '🎯',
        annualPotential: '$2M-$6M',
      },
    ],
    businessFunctions: [
      {
        name: 'Match Integrity',
        description: 'AI-driven match-fixing detection and suspicious pattern analysis',
        icon: '🎯',
      },
      {
        name: 'Anti-Doping Intelligence',
        description: 'Predictive doping risk profiling and intelligent testing strategy',
        icon: '💉',
      },
      {
        name: 'Betting Surveillance',
        description: 'Global betting market monitoring and anomaly detection',
        icon: '💰',
      },
      {
        name: 'Performance Analytics',
        description: 'Athlete biomechanics, injury prediction, and career optimisation',
        icon: '🏃',
      },
      {
        name: 'Venue Operations',
        description: 'Stadium safety, crowd management, and event logistics intelligence',
        icon: '🎟️',
      },
      {
        name: 'Cross-Sector Sports Intel',
        description: 'Connecting sports data with financial, criminal, and social intelligence',
        icon: '🔗',
      },
    ],
    technologyPartners: [
      {
        name: 'X (Twitter)',
        description: 'Global real-time social platform',
        logo: '🐏',
        integration:
          'Real-time sports sentiment, match-fixing rumour detection, and athlete social monitoring',
      },
      {
        name: 'xAI (Grok)',
        description: 'Advanced AI reasoning engine',
        logo: '🤖',
        integration:
          'Deep reasoning for complex betting pattern analysis and multi-sport integrity correlation',
      },
      {
        name: 'Starlink',
        description: 'Global satellite internet constellation',
        logo: '📡',
        integration:
          'Real-time data streaming for remote sporting events and global venue connectivity',
      },
      {
        name: 'Neuralink',
        description: 'Brain-computer interface pioneer',
        logo: '🧠',
        integration:
          'Future: Neural performance measurement and cognitive fatigue detection for athlete welfare',
      },
    ],
    apiProducts: [
      {
        name: 'Match Integrity API',
        description: 'Real-time match-fixing probability scoring and suspicious pattern detection',
        endpoint: '/api/v1/match-integrity',
        pricePerCall: '$0.25',
      },
      {
        name: 'Betting Anomaly API',
        description: 'Betting market surveillance and anomalous activity detection',
        endpoint: '/api/v1/betting-anomaly',
        pricePerCall: '$0.15',
      },
      {
        name: 'Athlete Risk API',
        description: 'Injury prediction and doping risk profiling for individual athletes',
        endpoint: '/api/v1/athlete-risk',
        pricePerCall: '$0.30',
      },
      {
        name: 'Performance Analytics API',
        description: 'Biomechanical analysis and performance benchmarking data',
        endpoint: '/api/v1/sports-performance',
        pricePerCall: '$0.20',
      },
    ],
    companyStructure: {
      entity: 'Anchor AI Guard — Sports Division',
      division: 'Sports Integrity Intelligence Division',
      headcount: 'Founding team (scaling)',
      hq: 'Australia',
      registrations: [
        'Sovereign Architecture',
        'Designed for Compliance',
        'Australian-Built Platform',
      ],
    },
    disclaimer:
      'Anchor Sports is an intelligence and analytics platform. It does not govern sports, conduct drug tests, or regulate betting. All outputs should be validated by qualified sports integrity professionals.',
    ctaText: 'Protect the Spirit of Sport',
    ctaSubtext:
      'Guided demo — paid subscription • Federation pricing available • Full WADA compliance',
  },

  // ─── 27. ANCHOR AUTOMOTIVE ─────────────────────────────────────────
  {
    id: 'automotive',
    slug: '/anchor-automotive',
    brandName: 'Anchor Automotive',
    tagline: 'Autonomous Vehicle Crime Intelligence',
    heroSubtitle:
      "The world's first self-evolving vehicle crime intelligence platform. Tracks every stolen car, every rebirthed VIN, every port shipment — shutting down the $8.2B vehicle crime industry.",
    icon: '🚗',
    logoUrl: resolveAnchorLogoByAsset(''),
    colorFrom: 'from-amber-500',
    colorTo: 'to-orange-500',
    accentColor: 'amber',
    borderColor: 'border-amber-500',
    textColor: 'text-amber-400',
    bgGradient: 'from-amber-500/10 via-transparent to-amber-900/5',
    domain: 'automotive.anchoraiguard.com',
    targetMarket: [
      'Law Enforcement Agencies',
      'Insurance Investigators',
      'Port Authorities',
      'Motor Vehicle Registries',
      'Auto Dealerships',
      'Border Security Agencies',
    ],
    problemStatement:
      'Vehicle theft costs Australia $1.6B annually. Stolen cars are rebirthed with cloned VINs, shipped through ports in containers, or stripped for parts in underground chop shops. Current detection is fragmented and reactive.',
    solutionStatement:
      'Anchor Automotive provides sovereign-grade vehicle crime intelligence — tracking stolen vehicles across ANPR networks, detecting rebirthed VINs, intercepting port shipments, and disrupting parts trafficking rings.',
    features: [
      {
        icon: '📷',
        title: 'ANPR Intelligence Network',
        description:
          'AI-driven analysis across national ANPR camera networks, detecting stolen vehicles, cloned plates, and suspicious movement patterns.',
        worldFirst: true,
      },
      {
        icon: '🔍',
        title: 'VIN Forensics Engine',
        description:
          'Deep VIN analysis detecting rebirthing, cloning, and fraudulent registration across PPSR, NEVDIS, and state registries.',
      },
      {
        icon: '🚢',
        title: 'Port Interdiction Intelligence',
        description:
          'Real-time monitoring of vehicle exports at Australian ports, correlating container bookings with stolen vehicle databases.',
        worldFirst: true,
      },
      {
        icon: '🔧',
        title: 'Parts Trafficking Detection',
        description:
          'AI-powered mapping of illegal parts distribution networks, chop shop identification, and online marketplace monitoring.',
      },
      {
        icon: '🧠',
        title: 'AI Evolution Engine',
        description: 'Self-evolving AI improving vehicle crime detection on a 15-second cycle.',
        worldFirst: true,
      },
      {
        icon: '🗺️',
        title: 'Hotspot Prediction',
        description:
          'Predictive mapping of vehicle theft hotspots using crime patterns, demographics, and environmental factors.',
      },
      {
        icon: '🔗',
        title: 'Cross-Sector Correlations',
        description:
          'Links automotive crime data with insurance, law enforcement, maritime, and financial intelligence.',
        worldFirst: true,
      },
      {
        icon: '📊',
        title: 'Crime Analytics Dashboards',
        description:
          'Real-time vehicle crime dashboards for law enforcement, insurers, and government agencies.',
      },
    ],
    dataSources: [
      { name: 'NEVDIS', description: 'National Exchange of Vehicle & Driver Information' },
      { name: 'PPSR', description: 'Personal Property Securities Register' },
      { name: 'ANPR Networks', description: 'Automatic Number Plate Recognition data' },
      { name: 'Port Shipping Manifests', description: 'Container and vehicle export data' },
      { name: 'Insurance Claims Databases', description: 'Theft and total loss claims data' },
      {
        name: 'Online Marketplace Data',
        description: 'Gumtree, Facebook Marketplace, eBay parts monitoring',
      },
    ],
    pricing: [
      {
        name: 'Investigator',
        price: '$2,990',
        period: '/mo',
        features: [
          '5 users',
          'VIN checking',
          'Stolen vehicle alerts',
          'Basic dashboards',
          'Email support',
        ],
        cta: 'Request demo',
      },
      {
        name: 'Agency',
        price: '$9,990',
        period: '/mo',
        features: [
          '25 users',
          'Everything in Investigator',
          'ANPR intelligence',
          'Port monitoring',
          'API access',
          'Priority support',
        ],
        cta: 'Request demo',
        highlighted: true,
      },
      {
        name: 'Sovereign',
        price: '$29,990',
        period: '/mo',
        features: [
          'Unlimited users',
          'Everything in Agency',
          'Full AI platform',
          'Custom models',
          'Government deployment',
          'Dedicated account manager',
          '99.9% SLA',
        ],
        cta: 'Contact Sales',
      },
    ],
    testimonials: [],
    stats: [
      { value: '47', label: 'Port Intercepts (Q1)' },
      { value: '340', label: 'Cloned VINs Detected' },
      { value: '15s', label: 'AI Evolution Cycle' },
      { value: '62%', label: 'Investigation Cost Reduction' },
    ],
    useCases: [
      {
        title: 'Stolen Vehicle Recovery',
        description:
          'Track stolen vehicles across ANPR networks and predict likely locations using AI movement analysis.',
      },
      {
        title: 'Rebirthing Detection',
        description:
          'Detect cloned VINs and fraudulent registrations across national and state databases.',
      },
      {
        title: 'Port Security',
        description:
          'Intercept stolen vehicles before they leave the country through automated container screening.',
      },
      {
        title: 'Parts Trafficking',
        description: 'Map illegal parts distribution networks and identify chop shop operations.',
      },
    ],
    revenueStreams: [
      {
        name: 'Government Contracts',
        description: 'Sovereign vehicle crime intelligence for law enforcement and border agencies',
        icon: '🏛️',
        annualPotential: '$10M-$35M',
      },
      {
        name: 'SaaS Subscriptions',
        description: 'Platform access from investigators to national agencies',
        icon: '💳',
        annualPotential: '$6M-$18M',
      },
      {
        name: 'Vehicle Intelligence API',
        description: 'Per-call API for VIN checking, stolen vehicle screening, and risk scoring',
        icon: '🔌',
        annualPotential: '$4M-$12M',
      },
      {
        name: 'Insurance Intelligence',
        description: 'Managed fraud detection and vehicle investigation support for insurers',
        icon: '🛡️',
        annualPotential: '$5M-$15M',
      },
      {
        name: 'Dealership Fraud Prevention',
        description: 'VIN verification and provenance checking for auto dealers',
        icon: '🏪',
        annualPotential: '$3M-$8M',
      },
      {
        name: 'Advisory & Consulting',
        description: 'Expert vehicle crime intelligence advisory for law enforcement and insurers',
        icon: '🎯',
        annualPotential: '$2M-$6M',
      },
    ],
    businessFunctions: [
      {
        name: 'ANPR Intelligence',
        description: 'AI-driven stolen vehicle detection across national camera networks',
        icon: '📷',
      },
      {
        name: 'VIN Forensics',
        description: 'Deep VIN analysis for rebirthing, cloning, and fraudulent registration',
        icon: '🔍',
      },
      {
        name: 'Port Interdiction',
        description: 'Container screening and vehicle export monitoring at Australian ports',
        icon: '🚢',
      },
      {
        name: 'Parts Trafficking Intel',
        description: 'Mapping chop shop networks and illegal parts distribution channels',
        icon: '🔧',
      },
      {
        name: 'Hotspot Prediction',
        description: 'Predictive vehicle theft hotspot mapping and patrol optimisation',
        icon: '🗺️',
      },
      {
        name: 'Cross-Sector Auto Intel',
        description:
          'Connecting vehicle crime with insurance, maritime, law enforcement, and financial intelligence',
        icon: '🔗',
      },
    ],
    technologyPartners: [
      {
        name: 'xAI (Grok)',
        description: 'Advanced AI reasoning engine',
        logo: '🤖',
        integration:
          'Deep reasoning for complex vehicle crime network analysis and VIN pattern matching',
      },
      {
        name: 'X (Twitter)',
        description: 'Global real-time social platform',
        logo: '🐏',
        integration:
          'Vehicle crime reporting, stolen car sightings, and parts trafficking marketplace monitoring',
      },
      {
        name: 'Starlink',
        description: 'Global satellite internet constellation',
        logo: '📡',
        integration:
          'Remote ANPR connectivity and real-time stolen vehicle tracking in rural areas',
      },
      {
        name: 'SpaceX',
        description: 'Space launch and transport',
        logo: '🚀',
        integration: 'Satellite imagery for chop shop identification and port container monitoring',
      },
    ],
    apiProducts: [
      {
        name: 'VIN Check API',
        description: 'Deep VIN verification against NEVDIS, PPSR, and stolen vehicle databases',
        endpoint: '/api/v1/vin-check',
        pricePerCall: '$0.15',
      },
      {
        name: 'Stolen Vehicle Screen API',
        description: 'Real-time screening against national stolen vehicle databases',
        endpoint: '/api/v1/stolen-screen',
        pricePerCall: '$0.10',
      },
      {
        name: 'Rebirth Detection API',
        description: 'Probability scoring for VIN cloning and vehicle rebirthing',
        endpoint: '/api/v1/rebirth-detect',
        pricePerCall: '$0.30',
      },
      {
        name: 'Port Risk API',
        description: 'Container screening and stolen vehicle export risk assessment',
        endpoint: '/api/v1/port-risk',
        pricePerCall: '$0.25',
      },
    ],
    companyStructure: {
      entity: 'Anchor AI Guard — Automotive Division',
      division: 'Vehicle Crime Intelligence Division',
      headcount: 'Founding team (scaling)',
      hq: 'Australia',
      registrations: [
        'Sovereign Architecture',
        'Designed for Compliance',
        'Australian-Built Platform',
      ],
    },
    disclaimer:
      'Anchor Automotive is an intelligence and analytics platform. It does not conduct arrests, seize vehicles, or perform physical inspections. All outputs should be validated by qualified law enforcement and forensic professionals.',
    ctaText: 'Shut Down Vehicle Crime',
    ctaSubtext:
      'Law enforcement verification required • Sovereign deployment • Full regulatory compliance',
  },
  // ═══════════════════════════════════════════════════════════
  // NEW SOVEREIGN SECTOR BUSINESSES (28""35)
  // ═══════════════════════════════════════════════════════════
  {
    id: 'agroshield',
    brandName: 'Anchor AgroShield',
    slug: '/agroshield',
    icon: '🌿',
    heroTitle: 'ANCHOR AGROSHIELD',
    heroSubtitle:
      "The world's first sovereign cannabis & controlled crops cybersecurity platform. Protects licensed facilities, tracks supply chain provenance, and ensures regulatory compliance — every plant, batch, and transaction secured in real time.",
    primaryColor: 'emerald',
    secondaryColor: 'green',
    logo: resolveAnchorLogoByAsset(''),
    features: [
      {
        icon: '🏭',
        title: 'Facility Fortress Protection',
        description:
          'AI-powered physical and cyber security for licensed cultivation, processing, and distribution facilities. Real-time RFID tracking and environmental monitoring.',
        worldFirst: true,
      },
      {
        icon: '🔗',
        title: 'Supply Chain Provenance',
        description:
          'End-to-end batch tracking from seed to sale. Blockchain-verified chain of custody for every gram of controlled product.',
        worldFirst: true,
      },
      {
        icon: '📋',
        title: 'Regulatory Compliance Engine',
        description:
          'Autonomous compliance monitoring across TGA, DEA, Health Canada, and state regulators. Auto-generated reporting and license renewal tracking.',
      },
      {
        icon: '🔍',
        title: 'Diversion Detection AI',
        description:
          'Machine learning models detect product diversion, black market leakage, and insider threats before regulatory violations occur.',
        worldFirst: true,
      },
    ],
    dataSources: [
      { name: 'TGA / Health Canada / DEA', description: 'Regulatory compliance feeds' },
      { name: 'RFID / IoT Networks', description: 'Facility sensor data streams' },
      { name: 'Dark Web Intelligence', description: 'Black market diversion tracking' },
    ],
    pricingTiers: [
      {
        name: 'Facility',
        price: '$4,990',
        period: '/mo',
        features: [
          '1 facility',
          'Supply chain tracking',
          'Compliance engine',
          'Basic AI',
          'Email support',
        ],
        cta: 'Request demo',
      },
      {
        name: 'Enterprise',
        price: '$14,990',
        period: '/mo',
        features: [
          'Unlimited facilities',
          'Full diversion AI',
          'Custom compliance',
          'API access',
          'Priority support',
        ],
        cta: 'Contact Sales',
      },
      {
        name: 'Government',
        price: 'Custom',
        period: '',
        features: [
          'National deployment',
          'Cross-agency intel',
          'Sovereign hosting',
          'Dedicated team',
          '24/7 support',
        ],
        cta: 'Contact Government Sales',
      },
    ],
    testimonials: [],
    useCases: [
      {
        title: 'Licensed Facility Security',
        description:
          'Real-time cyber and physical protection for cannabis cultivation and processing facilities.',
      },
      {
        title: 'Batch Provenance Verification',
        description:
          'Track every gram from cultivation through distribution with blockchain-verified chain of custody.',
      },
    ],
    businessFunctions: [
      {
        name: 'Facility RFID Fortress',
        description:
          'AI-powered facility protection with real-time RFID and environmental monitoring',
        icon: '🏭',
      },
      {
        name: 'Supply Chain Provenance',
        description: 'Seed-to-sale batch tracking with blockchain verification',
        icon: '🔗',
      },
      {
        name: 'Compliance Automation',
        description: 'Auto-generated compliance reports for TGA/DEA/Health Canada',
        icon: '📋',
      },
      {
        name: 'Diversion Detection',
        description: 'ML-powered detection of product diversion and black market leakage',
        icon: '🔍',
      },
    ],
    revenueStreams: [
      {
        name: 'SaaS Subscriptions',
        description: 'Facility and enterprise platform subscriptions',
        icon: '💳',
        annualPotential: '$4M-$12M',
      },
      {
        name: 'Government Contracts',
        description: 'National controlled substance monitoring for regulatory agencies',
        icon: '🏛️',
        annualPotential: '$8M-$20M',
      },
      {
        name: 'Compliance API',
        description: 'Per-call API for regulatory compliance verification',
        icon: '🔌',
        annualPotential: '$3M-$8M',
      },
    ],
    technologyPartners: [],
    apiProducts: [
      {
        name: 'Compliance Check API',
        description: 'Regulatory compliance verification across jurisdictions',
        endpoint: '/api/v1/agroshield-compliance',
        pricePerCall: '$0.20',
      },
      {
        name: 'Batch Provenance API',
        description: 'Supply chain tracking and batch verification',
        endpoint: '/api/v1/agroshield-provenance',
        pricePerCall: '$0.15',
      },
    ],
    companyStructure: {
      entity: 'Anchor AI Guard — AgroShield Division',
      division: 'Controlled Crops Security Division',
      headcount: 'Founding team (scaling)',
      hq: 'Australia',
      registrations: [
        'Sovereign Architecture',
        'Designed for Compliance',
        'Australian-Built Platform',
      ],
    },
    disclaimer:
      'Anchor AgroShield provides cybersecurity and compliance intelligence for legally licensed controlled crops operations only. It does not facilitate, promote, or enable any illegal activity.',
    ctaText: 'Secure Your Facilities',
    ctaSubtext: 'Licensed operators only • Sovereign deployment • Full regulatory compliance',
  },
  {
    id: 'bioagri',
    brandName: 'Anchor BioAgri',
    slug: '/bioagri',
    icon: '🦠',
    heroTitle: 'ANCHOR BIOAGRI',
    heroSubtitle:
      "The world's first sovereign agricultural biosecurity intelligence platform. Protects farms from pathogen incursion, monitors processing facilities, and ensures food safety — from paddock to plate.",
    primaryColor: 'teal',
    secondaryColor: 'cyan',
    logo: resolveAnchorLogoByAsset(''),
    features: [
      {
        icon: '🌾',
        title: 'Farm Perimeter Biosecurity',
        description:
          'AI-powered biosecurity monitoring for livestock and crop operations. Real-time pathogen detection across farm perimeters.',
        worldFirst: true,
      },
      {
        icon: '🦠',
        title: 'Pathogen Intelligence',
        description:
          'Early detection of emerging biological threats using satellite, sensor, and genomic data fusion.',
        worldFirst: true,
      },
      {
        icon: '🏭',
        title: 'Processing Facility Shield',
        description:
          'HVAC anomaly detection, contamination prevention, and GMP compliance monitoring for food processing plants.',
      },
      {
        icon: '🌾',
        title: 'Food Traceability Engine',
        description: 'Complete batch-level food provenance tracking from farm to consumer.',
        worldFirst: true,
      },
    ],
    dataSources: [
      { name: 'Farm IoT Sensors', description: 'Temperature, humidity, and biosecurity sensors' },
      { name: 'Genomic Sequencing', description: 'Real-time pathogen genome analysis' },
      { name: 'Satellite Imagery', description: 'Crop health and disease spread monitoring' },
    ],
    pricingTiers: [
      {
        name: 'Farm',
        price: '$2,990',
        period: '/mo',
        features: [
          '1 farm operation',
          'Biosecurity alerts',
          'Basic pathogen intel',
          'Email support',
        ],
        cta: 'Request demo',
      },
      {
        name: 'Enterprise',
        price: '$9,990',
        period: '/mo',
        features: [
          'Unlimited farms',
          'Full pathogen AI',
          'Processing facilities',
          'API access',
          'Priority support',
        ],
        cta: 'Contact Sales',
      },
      {
        name: 'Government',
        price: 'Custom',
        period: '',
        features: [
          'National biosecurity',
          'Cross-agency intel',
          'Sovereign hosting',
          'Dedicated team',
        ],
        cta: 'Contact Government Sales',
      },
    ],
    testimonials: [],
    useCases: [
      {
        title: 'National Biosecurity Defence',
        description: 'Sovereign-scale pathogen detection and response coordination.',
      },
      {
        title: 'Food Supply Chain Safety',
        description: 'End-to-end traceability and contamination prevention.',
      },
    ],
    businessFunctions: [
      {
        name: 'Farm Biosecurity',
        description: 'AI-powered perimeter monitoring and pathogen detection',
        icon: '🌾',
      },
      {
        name: 'Processing Facility HVAC',
        description: 'Real-time air quality and contamination monitoring',
        icon: '🏭',
      },
      {
        name: 'Food Traceability',
        description: 'Batch-level provenance tracking from farm to consumer',
        icon: '🌾',
      },
    ],
    revenueStreams: [
      {
        name: 'SaaS Subscriptions',
        description: 'Farm and enterprise platform subscriptions',
        icon: '💳',
        annualPotential: '$5M-$15M',
      },
      {
        name: 'Government Contracts',
        description: 'National biosecurity monitoring and response',
        icon: '🏛️',
        annualPotential: '$12M-$30M',
      },
      {
        name: 'Food Safety API',
        description: 'Per-call API for batch traceability verification',
        icon: '🔌',
        annualPotential: '$4M-$10M',
      },
    ],
    technologyPartners: [],
    apiProducts: [
      {
        name: 'Pathogen Alert API',
        description: 'Real-time pathogen detection and biosecurity alerts',
        endpoint: '/api/v1/bioagri-pathogen',
        pricePerCall: '$0.25',
      },
    ],
    companyStructure: {
      entity: 'Anchor AI Guard — BioAgri Division',
      division: 'Agricultural Biosecurity Division',
      headcount: 'Founding team (scaling)',
      hq: 'Australia',
      registrations: [
        'Sovereign Architecture',
        'Designed for Compliance',
        'Australian-Built Platform',
      ],
    },
    disclaimer:
      'Anchor BioAgri provides intelligence and analytics for agricultural biosecurity. It does not replace government biosecurity operations.',
    ctaText: 'Defend Your Food Supply',
    ctaSubtext: 'Farm operators & government agencies • Sovereign deployment • Biosecurity grade',
  },
  {
    id: 'water-utilities',
    brandName: 'Anchor Water Utilities',
    slug: '/water-utilities',
    icon: '💧',
    heroTitle: 'ANCHOR WATER UTILITIES',
    heroSubtitle:
      "The world's first sovereign water infrastructure cybersecurity platform. Protects SCADA systems, monitors pipelines, detects leaks, and ensures water quality — defending the most critical resource on Earth.",
    primaryColor: 'sky',
    secondaryColor: 'blue',
    logo: resolveAnchorLogoByAsset(''),
    features: [
      {
        icon: '🔥️',
        title: 'SCADA Security Shield',
        description:
          'Zero-trust protection for water treatment SCADA/ICS systems. PLC firmware integrity monitoring and anomaly detection.',
        worldFirst: true,
      },
      {
        icon: '🔧',
        title: 'Pipeline Intelligence',
        description:
          'Real-time pressure, flow, and acoustic monitoring across thousands of pipeline segments.',
      },
      {
        icon: '💧',
        title: 'AI Leak Detection',
        description:
          'Acoustic pattern analysis for leak detection with 28% fewer false positives than traditional methods.',
        worldFirst: true,
      },
      {
        icon: '🧪',
        title: 'Water Quality Intelligence',
        description:
          'Continuous quality monitoring with contaminant detection and automated compliance reporting.',
      },
    ],
    dataSources: [
      { name: 'SCADA/ICS Networks', description: 'Industrial control system data' },
      { name: 'Pipeline Sensors', description: 'Pressure, flow, and acoustic data' },
      { name: 'Water Quality Labs', description: 'Chemical and biological analysis' },
    ],
    pricingTiers: [
      {
        name: 'Utility',
        price: '$9,990',
        period: '/mo',
        features: ['1 utility network', 'SCADA protection', 'Leak detection', 'Quality monitoring'],
        cta: 'Request demo',
      },
      {
        name: 'Enterprise',
        price: '$24,990',
        period: '/mo',
        features: ['Multi-network', 'Full AI suite', 'Predictive maintenance', 'API access'],
        cta: 'Contact Sales',
      },
      {
        name: 'Government',
        price: 'Custom',
        period: '',
        features: [
          'National water grid',
          'Cross-agency intel',
          'Sovereign hosting',
          'Dedicated team',
        ],
        cta: 'Contact Government Sales',
      },
    ],
    testimonials: [],
    useCases: [
      {
        title: 'SCADA Cyber Defence',
        description: 'Zero-trust protection for water treatment industrial control systems.',
      },
      {
        title: 'Predictive Maintenance',
        description: 'AI-powered failure prediction and maintenance scheduling.',
      },
    ],
    businessFunctions: [
      {
        name: 'SCADA Security',
        description: 'Zero-trust industrial control system protection',
        icon: '🔥️',
      },
      {
        name: 'Pipeline Monitoring',
        description: 'Real-time pressure and flow analysis',
        icon: '🔧',
      },
      {
        name: 'Leak Detection AI',
        description: 'Acoustic pattern analysis for leak detection',
        icon: '💧',
      },
    ],
    revenueStreams: [
      {
        name: 'SaaS Subscriptions',
        description: 'Water utility platform subscriptions',
        icon: '💳',
        annualPotential: '$8M-$20M',
      },
      {
        name: 'Government Contracts',
        description: 'National water infrastructure protection',
        icon: '🏛️',
        annualPotential: '$15M-$40M',
      },
      {
        name: 'SCADA Security API',
        description: 'Per-call API for ICS security monitoring',
        icon: '🔌',
        annualPotential: '$5M-$12M',
      },
    ],
    technologyPartners: [],
    apiProducts: [
      {
        name: 'SCADA Monitor API',
        description: 'Real-time SCADA device integrity monitoring',
        endpoint: '/api/v1/water-scada',
        pricePerCall: '$0.30',
      },
    ],
    companyStructure: {
      entity: 'Anchor AI Guard — Water Division',
      division: 'Water Infrastructure Division',
      headcount: 'Founding team (scaling)',
      hq: 'Australia',
      registrations: [
        'Sovereign Architecture',
        'Designed for Compliance',
        'Australian-Built Platform',
      ],
    },
    disclaimer:
      'Anchor Water Utilities provides cybersecurity intelligence for water infrastructure. It does not operate water systems or make operational decisions.',
    ctaText: 'Protect Water Infrastructure',
    ctaSubtext: 'Water utilities & government agencies • Sovereign deployment • ICS-grade security',
  },
  {
    id: 'blackflight',
    brandName: 'Anchor Blackflight Taskforce',
    slug: '/blackflight',
    icon: '✈️',
    heroTitle: 'ANCHOR BLACKFLIGHT TASKFORCE',
    heroSubtitle:
      "The world's first sovereign anti-trafficking intelligence platform. Tracks suspicious flight patterns, maps criminal networks, and coordinates rescue operations — AI-powered justice at global scale.",
    primaryColor: 'red',
    secondaryColor: 'rose',
    logo: resolveAnchorLogoByAsset(''),
    features: [
      {
        icon: '✈️',
        title: 'Suspicious Flight Tracking',
        description:
          'AI analysis of ADS-B, radar, and satellite data to identify trafficking flight patterns and anomalous routes.',
        worldFirst: true,
      },
      {
        icon: '🕸️',
        title: 'Criminal Network Mapping',
        description:
          'Graph AI that maps trafficking networks, financial flows, and operational patterns across jurisdictions.',
        worldFirst: true,
      },
      {
        icon: '🚨',
        title: 'Rescue Operations Coordination',
        description:
          'Real-time operations centre for coordinating multi-agency rescue and interdiction operations.',
      },
      {
        icon: '💰',
        title: 'Financial Flow Intelligence',
        description: 'Cryptocurrency and fiat money trail analysis for trafficking proceeds.',
        worldFirst: true,
      },
    ],
    dataSources: [
      { name: 'ADS-B / Radar', description: 'Global flight tracking data' },
      { name: 'Financial Intelligence', description: 'Suspicious transaction reports' },
      { name: 'Law Enforcement Networks', description: 'AFP, DEA, Interpol feeds' },
    ],
    pricingTiers: [
      {
        name: 'Agency',
        price: '$19,990',
        period: '/mo',
        features: [
          'Regional coverage',
          'Flight tracking AI',
          'Network mapping',
          'Priority support',
        ],
        cta: 'Apply for Access',
      },
      {
        name: 'National',
        price: 'Custom',
        period: '',
        features: [
          'National deployment',
          'Full AI suite',
          'Cross-agency fusion',
          'Sovereign hosting',
        ],
        cta: 'Contact Government Sales',
      },
    ],
    testimonials: [],
    useCases: [
      {
        title: 'Anti-Trafficking Operations',
        description: 'AI-powered identification of trafficking routes and criminal networks.',
      },
      {
        title: 'Cross-Agency Intelligence Fusion',
        description: 'Real-time intelligence sharing between federal and international agencies.',
      },
    ],
    businessFunctions: [
      {
        name: 'Flight Pattern Analysis',
        description: 'AI analysis of ADS-B and radar for trafficking detection',
        icon: '✈️',
      },
      {
        name: 'Network Graphing',
        description: 'Criminal network mapping and relationship analysis',
        icon: '🕸️',
      },
      {
        name: 'Rescue Coordination',
        description: 'Multi-agency rescue operations command',
        icon: '🚨',
      },
    ],
    revenueStreams: [
      {
        name: 'Government Contracts',
        description: 'Federal and international agency anti-trafficking intelligence',
        icon: '🏛️',
        annualPotential: '$20M-$50M',
      },
      {
        name: 'Intel-as-a-Service',
        description: 'Managed intelligence services for coalitions',
        icon: '🛡️',
        annualPotential: '$10M-$25M',
      },
    ],
    technologyPartners: [],
    apiProducts: [
      {
        name: 'Flight Risk API',
        description: 'Suspicious flight pattern scoring and alert',
        endpoint: '/api/v1/blackflight-risk',
        pricePerCall: '$1.00',
      },
    ],
    companyStructure: {
      entity: 'Anchor AI Guard — Blackflight Division',
      division: 'Anti-Trafficking Intelligence Division',
      headcount: 'Founding team (scaling)',
      hq: 'Australia',
      registrations: [
        'Sovereign Architecture',
        'Designed for Compliance',
        'Australian-Built Platform',
      ],
    },
    disclaimer:
      'Anchor Blackflight Taskforce provides intelligence and analytics only. It does not conduct arrests, perform surveillance on individuals, or operate independently of authorised law enforcement agencies.',
    ctaText: 'Join the Fight Against Trafficking',
    ctaSubtext:
      'Government & law enforcement only • Classified deployment • Full operational security',
  },
  {
    id: 'sovereign-surveillance',
    brandName: 'Anchor Sovereign Surveillance',
    slug: '/sovereign-surveillance',
    icon: '🔵️',
    heroTitle: 'ANCHOR SOVEREIGN SURVEILLANCE',
    heroSubtitle:
      "The world's first autonomous national surveillance intelligence platform. Signal intelligence, cyber intelligence, communications analysis, and financial intelligence — sovereign-grade AI protecting nations.",
    primaryColor: 'purple',
    secondaryColor: 'violet',
    logo: resolveAnchorLogoByAsset(''),
    features: [
      {
        icon: '📡',
        title: 'Signal Intelligence (SIGINT)',
        description:
          'Multi-spectrum signal processing at sovereign scale — 847M+ signals per hour with AI pattern correlation.',
        worldFirst: true,
      },
      {
        icon: '💻',
        title: 'Cyber Intelligence (CYBINT)',
        description:
          'Advanced persistent threat (APT) tracking, infrastructure mapping, and state-sponsored campaign detection.',
        worldFirst: true,
      },
      {
        icon: '📞',
        title: 'Communications Intelligence',
        description:
          'Encrypted communications analysis, metadata correlation, and network topology mapping.',
      },
      {
        icon: '💰',
        title: 'Financial Intelligence (FININT)',
        description:
          'Sovereign wealth fund monitoring, sanctions evasion detection, and money laundering network mapping.',
        worldFirst: true,
      },
    ],
    dataSources: [
      { name: 'SIGINT Feeds', description: 'Multi-spectrum signal intelligence' },
      { name: 'Cyber Threat Intel', description: 'APT and malware intelligence' },
      { name: 'Financial Networks', description: 'Suspicious transaction monitoring' },
    ],
    pricingTiers: [
      {
        name: 'National',
        price: 'Custom',
        period: '',
        features: [
          'National deployment',
          'Full SIGINT suite',
          'Cyber + financial',
          'Sovereign hosting',
          'Dedicated team',
        ],
        cta: 'Contact Government Sales',
      },
    ],
    testimonials: [],
    useCases: [
      {
        title: 'National Security Intelligence',
        description: 'Sovereign-scale intelligence collection and analysis.',
      },
      {
        title: 'Counter-Espionage',
        description: 'Detection and attribution of state-sponsored intelligence operations.',
      },
    ],
    businessFunctions: [
      {
        name: 'SIGINT Processing',
        description: 'Multi-spectrum signal analysis at sovereign scale',
        icon: '📡',
      },
      {
        name: 'APT Tracking',
        description: 'Advanced persistent threat mapping and attribution',
        icon: '💻',
      },
      {
        name: 'FININT',
        description: 'Financial intelligence and sanctions evasion detection',
        icon: '💰',
      },
    ],
    revenueStreams: [
      {
        name: 'Government Contracts',
        description: 'National intelligence agency deployments',
        icon: '🏛️',
        annualPotential: '$50M-$200M',
      },
      {
        name: 'Allied Intelligence',
        description: 'Coalition intelligence sharing platforms',
        icon: '🤝',
        annualPotential: '$20M-$60M',
      },
    ],
    technologyPartners: [],
    apiProducts: [],
    companyStructure: {
      entity: 'Anchor AI Guard — Sovereign Division',
      division: 'National Intelligence Division',
      headcount: 'Founding team (scaling)',
      hq: 'Australia',
      registrations: [
        'Sovereign Architecture',
        'Designed for Compliance',
        'Australian-Built Platform',
      ],
    },
    disclaimer:
      'Anchor Sovereign Surveillance is available only to authorised government and intelligence agencies. It does not conduct mass surveillance of civilians and operates strictly within the legal frameworks of deploying nations.',
    ctaText: 'Request Sovereign Briefing',
    ctaSubtext:
      'Government & intelligence agencies only • Classified deployment • TS/SCI compatible',
  },
  {
    id: 'humankind',
    brandName: 'Anchor HumanKind Intelligence',
    slug: '/humankind',
    icon: '🌍',
    heroTitle: 'ANCHOR HUMANKIND',
    heroSubtitle:
      "The world's first planet-scale human safety intelligence platform. Monitors natural disasters, conflict zones, health crises, and climate events — protecting 847 million lives across 194 nations.",
    primaryColor: 'emerald',
    secondaryColor: 'teal',
    logo: resolveAnchorLogoByAsset(''),
    features: [
      {
        icon: '🌊',
        title: 'Disaster Early Warning',
        description:
          'AI-powered natural disaster prediction with 6-hour lead time improvement over conventional systems.',
        worldFirst: true,
      },
      {
        icon: '⚔️',
        title: 'Conflict Zone Intelligence',
        description:
          'Real-time civilian corridor mapping, humanitarian aid logistics, and conflict de-escalation support.',
        worldFirst: true,
      },
      {
        icon: '🏥',
        title: 'Health Crisis Surveillance',
        description:
          'Global disease surveillance covering 194 nations with emerging pathogen detection.',
      },
      {
        icon: '🌍¡️',
        title: 'Climate Event Tracking',
        description:
          'Long-term climate pattern analysis with extreme weather prediction and community impact modelling.',
        worldFirst: true,
      },
    ],
    dataSources: [
      { name: 'Satellite Networks', description: 'Global earth observation data' },
      { name: 'WHO / CDC Feeds', description: 'Global health surveillance' },
      { name: 'UNHCR / OCHA', description: 'Humanitarian situation reports' },
    ],
    pricingTiers: [
      {
        name: 'NGO',
        price: '$1,990',
        period: '/mo',
        features: ['Regional coverage', 'Disaster warnings', 'Health alerts', 'Basic analytics'],
        cta: 'Request demo',
      },
      {
        name: 'Government',
        price: 'Custom',
        period: '',
        features: [
          'National deployment',
          'Full AI suite',
          'Cross-sector intel',
          'Sovereign hosting',
        ],
        cta: 'Contact Government Sales',
      },
    ],
    testimonials: [],
    useCases: [
      {
        title: 'Disaster Response Coordination',
        description: 'AI-directed evacuation and resource deployment for natural disasters.',
      },
      {
        title: 'Pandemic Preparedness',
        description: 'Early detection and response coordination for emerging health crises.',
      },
    ],
    businessFunctions: [
      {
        name: 'Disaster Prediction',
        description: 'AI-powered natural disaster early warning',
        icon: '🌊',
      },
      {
        name: 'Conflict Mapping',
        description: 'Real-time civilian safety corridor mapping',
        icon: '⚔️',
      },
      {
        name: 'Health Surveillance',
        description: 'Global disease detection and tracking',
        icon: '🏥',
      },
    ],
    revenueStreams: [
      {
        name: 'Government Contracts',
        description: 'National disaster preparedness and response',
        icon: '🏛️',
        annualPotential: '$15M-$40M',
      },
      {
        name: 'UN/NGO Partnerships',
        description: 'Humanitarian intelligence services',
        icon: '🌍',
        annualPotential: '$8M-$20M',
      },
      {
        name: 'Climate Intelligence API',
        description: 'Per-call API for climate event data',
        icon: '🔌',
        annualPotential: '$5M-$12M',
      },
    ],
    technologyPartners: [],
    apiProducts: [
      {
        name: 'Disaster Alert API',
        description: 'Real-time disaster prediction and warning',
        endpoint: '/api/v1/humankind-disaster',
        pricePerCall: '$0.10',
      },
    ],
    companyStructure: {
      entity: 'Anchor AI Guard — HumanKind Division',
      division: 'Global Safety Division',
      headcount: 'Founding team (scaling)',
      hq: 'Australia',
      registrations: [
        'Sovereign Architecture',
        'Designed for Compliance',
        'Australian-Built Platform',
      ],
    },
    disclaimer:
      'Anchor HumanKind provides humanitarian intelligence and analytics. It does not conduct military operations, perform surveillance on individuals, or replace government disaster response agencies.',
    ctaText: 'Protect Humanity',
    ctaSubtext: 'Governments, NGOs & humanitarian organisations • Sovereign deployment',
  },
  {
    id: 'semiconductor',
    brandName: 'Anchor Semiconductor Intelligence',
    slug: '/semiconductor',
    icon: '🔬',
    heroTitle: 'ANCHOR SEMICONDUCTOR',
    heroSubtitle:
      "The world's first sovereign chip ecosystem cybersecurity platform. Monitors fabrication facilities, traces 2.4 billion chips, detects counterfeits, and prevents IP theft — securing the backbone of modern civilisation.",
    primaryColor: 'indigo',
    secondaryColor: 'blue',
    logo: resolveAnchorLogoByAsset(''),
    features: [
      {
        icon: '🏭',
        title: 'Fab Security Fortress',
        description:
          'Physical and cyber protection for semiconductor fabrication facilities — the most sensitive industrial environments on Earth.',
        worldFirst: true,
      },
      {
        icon: '🔬',
        title: 'Chip Provenance Tracking',
        description:
          'End-to-end tracing of 2.4 billion chips through the global supply chain with counterfeit detection.',
        worldFirst: true,
      },
      {
        icon: '🔍',
        title: 'IP Theft Detection',
        description:
          'AI-powered detection of design file exfiltration, reverse engineering attempts, and state-sponsored IP theft.',
      },
      {
        icon: '🔗',
        title: 'Supply Chain Intelligence',
        description:
          'Monitoring 12,847 supply chain nodes including rare earth mineral sourcing and component verification.',
        worldFirst: true,
      },
    ],
    dataSources: [
      { name: 'Fab Sensor Networks', description: 'Clean room and process monitoring' },
      { name: 'Supply Chain Databases', description: 'Global component tracking' },
      { name: 'Cyber Threat Intel', description: 'IP theft and espionage feeds' },
    ],
    pricingTiers: [
      {
        name: 'Facility',
        price: '$14,990',
        period: '/mo',
        features: ['1 fab facility', 'Chip provenance', 'IP protection', 'Supply chain monitoring'],
        cta: 'Request demo',
      },
      {
        name: 'Enterprise',
        price: '$49,990',
        period: '/mo',
        features: [
          'Multi-fab',
          'Full AI suite',
          'Counter-espionage',
          'API access',
          'Dedicated support',
        ],
        cta: 'Contact Sales',
      },
      {
        name: 'National',
        price: 'Custom',
        period: '',
        features: [
          'National semiconductor strategy',
          'Full sovereign deployment',
          'Cross-agency intel',
        ],
        cta: 'Contact Government Sales',
      },
    ],
    testimonials: [],
    useCases: [
      {
        title: 'Counterfeit Chip Detection',
        description: 'AI-powered detection of counterfeit and recycled chips in the supply chain.',
      },
      {
        title: 'National Semiconductor Security',
        description: 'Sovereign protection of domestic chip-making capability.',
      },
    ],
    businessFunctions: [
      {
        name: 'Fab Security',
        description: 'Physical and cyber protection for fabrication facilities',
        icon: '🏭',
      },
      {
        name: 'Chip Tracing',
        description: 'End-to-end chip provenance and counterfeit detection',
        icon: '🔬',
      },
      {
        name: 'IP Protection',
        description: 'Design file security and exfiltration detection',
        icon: '🔍',
      },
    ],
    revenueStreams: [
      {
        name: 'SaaS Subscriptions',
        description: 'Fab and enterprise platform subscriptions',
        icon: '💳',
        annualPotential: '$10M-$30M',
      },
      {
        name: 'Government Contracts',
        description: 'National semiconductor security programmes',
        icon: '🏛️',
        annualPotential: '$25M-$80M',
      },
      {
        name: 'Chip Provenance API',
        description: 'Per-call API for chip tracing and counterfeit detection',
        icon: '🔌',
        annualPotential: '$8M-$20M',
      },
    ],
    technologyPartners: [],
    apiProducts: [
      {
        name: 'Chip Trace API',
        description: 'Chip provenance verification and counterfeit scoring',
        endpoint: '/api/v1/semiconductor-trace',
        pricePerCall: '$0.50',
      },
    ],
    companyStructure: {
      entity: 'Anchor AI Guard — Semiconductor Division',
      division: 'Chip Ecosystem Security Division',
      headcount: 'Founding team (scaling)',
      hq: 'Australia',
      registrations: [
        'Sovereign Architecture',
        'Designed for Compliance',
        'Australian-Built Platform',
      ],
    },
    disclaimer:
      'Anchor Semiconductor provides cybersecurity intelligence for semiconductor operations. It does not manufacture, sell, or export semiconductor products.',
    ctaText: 'Secure the Chip Ecosystem',
    ctaSubtext:
      'Semiconductor manufacturers & governments • Sovereign deployment • Export-control compliant',
  },
  {
    id: 'quantum',
    brandName: 'Anchor Quantum Cryptography',
    slug: '/quantum',
    icon: '⚛️',
    heroTitle: 'ANCHOR QUANTUM',
    heroSubtitle:
      "The world's first sovereign post-quantum cryptography intelligence platform. Quantum key distribution, PQC migration, algorithm security, and quantum threat forecasting — future-proofing the world's encryption.",
    primaryColor: 'violet',
    secondaryColor: 'purple',
    logo: resolveAnchorLogoByAsset(''),
    features: [
      {
        icon: '🔑',
        title: 'Quantum Key Distribution (QKD)',
        description:
          '2,847 quantum-safe key distribution channels with zero intercept events — sovereign-grade cryptographic protection.',
        worldFirst: true,
      },
      {
        icon: '🧮',
        title: 'PQC Migration Engine',
        description:
          'Autonomous migration of 12,471 systems from classical to quantum-safe cryptography — 42% faster than manual migration.',
        worldFirst: true,
      },
      {
        icon: '⚡',
        title: 'Quantum Threat Forecasting',
        description:
          'AI-powered prediction of quantum computing milestones and their impact on current encryption standards.',
      },
      {
        icon: '🔄',
        title: 'Algorithm Security Assessment',
        description:
          'Continuous evaluation of lattice-based, hash-based, and code-based cryptographic algorithms against quantum threats.',
        worldFirst: true,
      },
    ],
    dataSources: [
      { name: 'NIST PQC Standards', description: 'Post-quantum cryptography standardisation' },
      { name: 'Quantum Computing Labs', description: 'Qubit milestone tracking' },
      { name: 'Cryptographic Research', description: 'Algorithm vulnerability research' },
    ],
    pricingTiers: [
      {
        name: 'Enterprise',
        price: '$19,990',
        period: '/mo',
        features: ['PQC migration', 'Algorithm assessment', 'Threat forecasting', 'API access'],
        cta: 'Request demo',
      },
      {
        name: 'Government',
        price: 'Custom',
        period: '',
        features: ['National PQC strategy', 'QKD channels', 'Sovereign hosting', 'Dedicated team'],
        cta: 'Contact Government Sales',
      },
    ],
    testimonials: [],
    useCases: [
      {
        title: 'National PQC Migration',
        description: 'Sovereign-scale migration to post-quantum cryptographic standards.',
      },
      {
        title: 'Financial Sector Q-Day Preparedness',
        description:
          'Protecting financial infrastructure against future quantum computing threats.',
      },
    ],
    businessFunctions: [
      {
        name: 'QKD Management',
        description: 'Quantum key distribution channel management',
        icon: '🔑',
      },
      {
        name: 'PQC Migration',
        description: 'Autonomous migration to quantum-safe algorithms',
        icon: '🔄',
      },
      {
        name: 'Threat Forecasting',
        description: 'Quantum computing timeline prediction',
        icon: '⚡',
      },
    ],
    revenueStreams: [
      {
        name: 'SaaS Subscriptions',
        description: 'Enterprise quantum security platform',
        icon: '💳',
        annualPotential: '$10M-$35M',
      },
      {
        name: 'Government Contracts',
        description: 'National PQC migration programmes',
        icon: '🏛️',
        annualPotential: '$30M-$100M',
      },
      {
        name: 'Quantum Consulting',
        description: 'PQC readiness assessment and advisory',
        icon: '🎯',
        annualPotential: '$5M-$15M',
      },
    ],
    technologyPartners: [],
    apiProducts: [
      {
        name: 'PQC Assessment API',
        description: 'Quantum vulnerability scoring for encryption implementations',
        endpoint: '/api/v1/quantum-assess',
        pricePerCall: '$0.50',
      },
    ],
    companyStructure: {
      entity: 'Anchor AI Guard — Quantum Division',
      division: 'Post-Quantum Cryptography Division',
      headcount: 'Founding team (scaling)',
      hq: 'Australia',
      registrations: [
        'Sovereign Architecture',
        'Designed for Compliance',
        'Australian-Built Platform',
      ],
    },
    disclaimer:
      'Anchor Quantum provides cryptographic intelligence advisory. It does not guarantee protection against all quantum computing threats.',
    ctaText: 'Prepare for Q-Day',
    ctaSubtext: 'Enterprises & governments • Sovereign deployment • NIST-aligned',
  },
  // ——— ANCHOR FUEL ——————————————————————————————————————
  {
    id: 'fuel',
    brandName: 'Anchor Fuel Intelligence',
    slug: '/fuel',
    icon: '⛽',
    heroTitle: 'ANCHOR FUEL',
    heroSubtitle:
      "The world's first sovereign fuel-sector cybersecurity intelligence platform. Pipeline SCADA protection, refinery OT security, fuel-supply chain integrity, price-manipulation detection, and tanker fleet cyber-hardening — protecting the arteries of the global economy.",
    primaryColor: 'orange',
    secondaryColor: 'amber',
    logoUrl: resolveAnchorLogoByAsset('anchor-fuel.png'),
    features: [
      {
        icon: '🛢️',
        title: 'Pipeline SCADA Defence',
        description:
          'Real-time anomaly detection across 47,000+ pipeline SCADA endpoints — zero Colonial Pipeline-class incidents.',
        worldFirst: true,
      },
      {
        icon: '🏭',
        title: 'Refinery OT Security',
        description:
          'Autonomous monitoring of refinery control systems, PLCs, and safety instrumented systems with <2s threat response.',
      },
      {
        icon: '📊',
        title: 'Price Manipulation Detection',
        description:
          'AI-driven detection of wholesale fuel-price manipulation, wash trading, and market spoofing across global benchmarks.',
        worldFirst: true,
      },
      {
        icon: '🚢',
        title: 'Tanker Fleet Cyber-Hardening',
        description:
          'Satellite-linked protection for fuel tanker navigation, cargo management, and communications systems.',
      },
      {
        icon: '⛓️',
        title: 'Supply Chain Integrity',
        description:
          'End-to-end traceability from extraction to retail pump — fuel-quality tamper detection and provenance verification.',
        worldFirst: true,
      },
      {
        icon: '📡',
        title: 'Critical Infrastructure Resilience',
        description:
          'National fuel-reserve monitoring, strategic petroleum reserve (SPR) cyber defence, and sovereign fuel-security dashboards.',
      },
    ],
    dataSources: [
      {
        name: 'ICS-CERT / CISA Advisories',
        description: 'Industrial control system threat intelligence for fuel infrastructure',
      },
      {
        name: 'Global Fuel Price Feeds',
        description: 'Real-time Brent, WTI, and regional wholesale fuel pricing data',
      },
      {
        name: 'AIS & Satellite Tracking',
        description: 'Fuel tanker fleet tracking and anomaly detection',
      },
      {
        name: 'SCADA Telemetry',
        description: 'Pipeline and refinery control system telemetry streams',
      },
    ],
    pricingTiers: [
      {
        name: 'Enterprise',
        price: 'Custom',
        period: '',
        features: [
          'Pipeline SCADA defence',
          'Refinery OT monitoring',
          'Price manipulation alerts',
          'API access',
          '24/7 support',
        ],
        cta: 'Contact Sales',
      },
      {
        name: 'Government',
        price: 'Custom',
        period: '',
        features: [
          'National fuel-reserve monitoring',
          'SPR cyber defence',
          'Sovereign hosting',
          'Dedicated team',
          'Air-gapped options',
        ],
        cta: 'Contact Government Sales',
      },
    ],
    testimonials: [],
    useCases: [
      {
        title: 'Pipeline Cyber Defence',
        description:
          'Preventing Colonial Pipeline-class attacks on critical fuel infrastructure through AI-powered SCADA anomaly detection.',
      },
      {
        title: 'Fuel Market Integrity',
        description:
          'Detecting wholesale price manipulation, wash trading, and market abuse across global fuel benchmarks.',
      },
      {
        title: 'National Fuel Security',
        description:
          'Sovereign monitoring of strategic petroleum reserves and national fuel-supply resilience.',
      },
    ],
    businessFunctions: [
      {
        name: 'Pipeline Security Operations',
        description: 'SCADA and ICS monitoring for pipeline networks',
        icon: '🛢️',
      },
      {
        name: 'Refinery OT Protection',
        description: 'Operational technology security for refinery control systems',
        icon: '🏭',
      },
      {
        name: 'Market Intelligence',
        description: 'Fuel price manipulation detection and market integrity',
        icon: '📊',
      },
      {
        name: 'Fleet Cyber Defence',
        description: 'Tanker fleet communications and navigation security',
        icon: '🚢',
      },
    ],
    revenueStreams: [
      {
        name: 'SaaS Subscriptions',
        description: 'Enterprise pipeline and refinery security platform',
        icon: '💳',
        annualPotential: '$15M-$40M',
      },
      {
        name: 'Government Contracts',
        description: 'National fuel-security and SPR monitoring programmes',
        icon: '🏛️',
        annualPotential: '$25M-$75M',
      },
      {
        name: 'Market Integrity API',
        description: 'Fuel price manipulation detection for regulators and traders',
        icon: '📡',
        annualPotential: '$8M-$20M',
      },
    ],
    technologyPartners: [],
    apiProducts: [
      {
        name: 'Pipeline Threat API',
        description: 'Real-time SCADA anomaly detection and threat scoring for pipeline networks',
        endpoint: '/api/v1/fuel-pipeline-threat',
        pricePerCall: '$0.35',
      },
      {
        name: 'Fuel Market Integrity API',
        description: 'Price manipulation and wash-trading detection across fuel benchmarks',
        endpoint: '/api/v1/fuel-market-integrity',
        pricePerCall: '$0.50',
      },
    ],
    companyStructure: {
      entity: 'Anchor AI Guard — Fuel Division',
      division: 'Energy Infrastructure Security Division',
      headcount: 'Founding team (scaling)',
      hq: 'Australia',
      registrations: [
        'Sovereign Architecture',
        'Designed for Compliance',
        'Australian-Built Platform',
      ],
    },
    disclaimer:
      'Anchor Fuel provides cybersecurity intelligence for fuel infrastructure. It does not operate, manage, or control physical fuel assets.',
    ctaText: 'Protect Fuel Infrastructure',
    ctaSubtext: 'Pipeline operators & governments • Sovereign deployment • ICS/SCADA certified',
  },
  {
    id: 'telecom',
    brandName: 'Anchor Telecom Intelligence',
    slug: '/telecom',
    icon: '📡',
    heroTitle: 'ANCHOR TELECOM',
    heroSubtitle:
      "The world's first sovereign telecommunications cybersecurity intelligence platform. 5G tower protection, SIM-swap fraud prevention, spectrum integrity monitoring, and mobile network threat detection — protecting the invisible fabric of global communications.",
    primaryColor: 'indigo',
    secondaryColor: 'violet',
    logo: resolveAnchorLogoByAsset(''),
    features: [
      {
        icon: '📡',
        title: '5G Tower Defence',
        description:
          'Real-time monitoring of 142,000+ cell towers for rogue base stations, IMSI catchers, and firmware tampering.',
        worldFirst: true,
      },
      {
        icon: '📱',
        title: 'SIM-Swap Fraud Prevention',
        description:
          'AI-driven detection of SIM-swap attacks, number-porting fraud, and identity theft via mobile carriers.',
      },
      {
        icon: '📻',
        title: 'Spectrum Integrity',
        description:
          'Monitoring 847 spectrum bands for illegal jamming, interference, and unauthorised transmissions.',
        worldFirst: true,
      },
      {
        icon: '🛡️',
        title: 'Network Threat Detection',
        description:
          'Deep packet analysis and behavioural AI for detecting SS7/Diameter exploits and network intrusions.',
      },
      {
        icon: '🌐',
        title: 'Roaming Fraud Intelligence',
        description:
          'Cross-carrier roaming fraud detection and revenue assurance across international networks.',
      },
      {
        icon: '🔒',
        title: 'Critical Comms Protection',
        description:
          'Securing emergency services communications, military radio networks, and government secure phones.',
      },
    ],
    dataSources: [
      {
        name: 'Cell Tower Telemetry',
        description: 'Real-time tower health, signal strength, and firmware status feeds',
      },
      {
        name: 'SS7/Diameter Monitoring',
        description: 'Signalling protocol analysis for telecom backbone security',
      },
      {
        name: 'Carrier Fraud Feeds',
        description: 'SIM-swap, porting fraud, and roaming abuse intelligence',
      },
      {
        name: 'Spectrum Monitoring',
        description: 'RF spectrum analysis for jamming and interference detection',
      },
    ],
    pricingTiers: [
      {
        name: 'Enterprise',
        price: '$12,990',
        period: '/mo',
        features: [
          'Tower monitoring',
          'SIM fraud prevention',
          'Spectrum alerts',
          'API access',
          '24/7 support',
        ],
        cta: 'Request demo',
      },
      {
        name: 'Government',
        price: 'Custom',
        period: '',
        features: [
          'National telecom defence',
          'Emergency comms protection',
          'Sovereign hosting',
          'Air-gapped options',
        ],
        cta: 'Contact Government Sales',
      },
    ],
    testimonials: [],
    useCases: [
      {
        title: '5G Security Operations',
        description:
          'Protecting next-generation mobile networks from rogue base stations, firmware attacks, and spectrum interference.',
      },
      {
        title: 'SIM Fraud Prevention',
        description:
          'Stopping SIM-swap attacks and number-porting fraud in real-time across carrier networks.',
      },
      {
        title: 'National Comms Resilience',
        description:
          'Sovereign monitoring of national telecommunications infrastructure for government and defence.',
      },
    ],
    businessFunctions: [
      {
        name: 'Tower Security Operations',
        description: '5G/4G tower monitoring and rogue base station detection',
        icon: '📡',
      },
      {
        name: 'Fraud Intelligence',
        description: 'SIM-swap, porting fraud, and roaming abuse prevention',
        icon: '📱',
      },
      {
        name: 'Spectrum Defence',
        description: 'RF spectrum monitoring and interference detection',
        icon: '📻',
      },
      {
        name: 'Network Threat Centre',
        description: 'SS7/Diameter exploit detection and network security',
        icon: '🛡️',
      },
    ],
    revenueStreams: [
      {
        name: 'SaaS Subscriptions',
        description: 'Enterprise tower security and fraud prevention platform',
        icon: '💳',
        annualPotential: '$12M-$35M',
      },
      {
        name: 'Government Contracts',
        description: 'National telecom security and emergency comms protection',
        icon: '🏛️',
        annualPotential: '$20M-$60M',
      },
      {
        name: 'Fraud Detection API',
        description: 'SIM-swap and porting fraud prevention API for carriers',
        icon: '📡',
        annualPotential: '$6M-$18M',
      },
    ],
    technologyPartners: [],
    apiProducts: [
      {
        name: 'Tower Threat API',
        description: 'Real-time cell tower security monitoring and anomaly detection',
        endpoint: '/api/v1/telecom-tower-threat',
        pricePerCall: '$0.25',
      },
      {
        name: 'SIM Fraud API',
        description: 'SIM-swap and number-porting fraud detection for carriers',
        endpoint: '/api/v1/telecom-sim-fraud',
        pricePerCall: '$0.40',
      },
    ],
    companyStructure: {
      entity: 'Anchor AI Guard — Telecom Division',
      division: 'Communications Security Division',
      headcount: 'Founding team (scaling)',
      hq: 'Australia',
      registrations: [
        'Sovereign Architecture',
        'Designed for Compliance',
        'Australian-Built Platform',
      ],
    },
    disclaimer:
      'Anchor Telecom provides cybersecurity intelligence for telecommunications infrastructure. It does not operate or manage telecom networks.',
    ctaText: 'Protect Telecom Infrastructure',
    ctaSubtext: 'Carriers & governments • Sovereign deployment • 5G/4G certified',
  },
  {
    id: 'kinetic',
    brandName: 'Anchor Kinetic-SI',
    slug: '/kinetic',
    icon: '🎯',
    heroTitle: 'ANCHOR KINETIC-SI',
    heroSubtitle:
      "The world's first sovereign Smart Effector Intelligence platform. AI-guided ethical firewall for kinetic systems, partner OEM integration, immutable audit logging, and per-effector royalty intelligence — ensuring lethal autonomy stays under human command.",
    primaryColor: 'amber',
    secondaryColor: 'orange',
    logo: resolveAnchorLogoByAsset(''),
    features: [
      {
        icon: '🎯',
        title: '3-Layer PID Ethical Firewall',
        description:
          'Proportional-Integral-Derivative firewall ensures every kinetic action passes humanitarian law, ROE, and commander intent.',
        worldFirst: true,
      },
      {
        icon: '🚀',
        title: 'Smart Effector Registry',
        description:
          'Manage MARK-I through MARK-V effector tiers with real-time status, location, and readiness tracking.',
      },
      {
        icon: '📋',
        title: 'Immutable Audit Ledger',
        description:
          'Every discharge, authorization, and firewall check recorded in a tamper-proof sovereign audit chain.',
        worldFirst: true,
      },
      {
        icon: '🤝',
        title: 'OEM Partner Integration',
        description:
          'Direct integration with NIOA, Thales, BAE Systems, and RTX for sovereign manufacturing intelligence.',
      },
      {
        icon: '💰',
        title: 'Per-Effector Royalty Engine',
        description:
          'Automated royalty calculation and revenue tracking per unit across all effector classes.',
      },
      {
        icon: '🛡️',
        title: 'Human-in-the-Loop Guarantee',
        description:
          'Constitutional-level safeguard ensuring no autonomous lethal decision without human authorization.',
      },
    ],
    dataSources: [
      {
        name: 'Effector Telemetry',
        description: 'Real-time status, readiness, and location data from smart effector systems',
      },
      {
        name: 'OEM Manufacturing Data',
        description: 'Partner production, quality, and delivery intelligence',
      },
      {
        name: 'Audit Chain',
        description: 'Immutable discharge, authorization, and firewall check records',
      },
      {
        name: 'Doctrine Engine',
        description: 'Rules of engagement, humanitarian law, and commander intent databases',
      },
    ],
    pricingTiers: [
      {
        name: 'Defence',
        price: 'Custom',
        period: '',
        features: [
          'Full effector management',
          'PID firewall',
          'Immutable audit',
          'OEM integration',
          'Dedicated support',
        ],
        cta: 'Contact Defence Sales',
      },
      {
        name: 'Government',
        price: 'Custom',
        period: '',
        features: [
          'National defence integration',
          'Sovereign hosting',
          'Air-gapped deployment',
          'ITAR/EAR compliant',
        ],
        cta: 'Contact Government Sales',
      },
    ],
    testimonials: [],
    useCases: [
      {
        title: 'Ethical Autonomy',
        description:
          'Ensuring AI-guided kinetic systems operate within international humanitarian law and rules of engagement.',
      },
      {
        title: 'Defence Manufacturing',
        description:
          'Sovereign smart effector production intelligence with per-unit royalty tracking and OEM coordination.',
      },
      {
        title: 'Audit & Compliance',
        description:
          'Immutable audit trail for every kinetic action, supporting post-incident review and legal compliance.',
      },
    ],
    businessFunctions: [
      {
        name: 'Effector Operations',
        description: 'Smart effector management, readiness, and deployment intelligence',
        icon: '🎯',
      },
      {
        name: 'Ethical Firewall',
        description: 'PID-based humanitarian law and ROE compliance engine',
        icon: '🛡️',
      },
      {
        name: 'Partner Management',
        description: 'OEM integration with defence manufacturing partners',
        icon: '🤝',
      },
      {
        name: 'Revenue Intelligence',
        description: 'Per-effector royalty calculation and contract management',
        icon: '💰',
      },
    ],
    revenueStreams: [
      {
        name: 'Per-Effector Royalty',
        description: 'AI firmware royalty per guided munition and smart effector',
        icon: '🎯',
        annualPotential: '$50M-$200M',
      },
      {
        name: 'Platform Licensing',
        description: 'Sovereign Kinetic-SI platform licensing to allied nations',
        icon: '🏛️',
        annualPotential: '$30M-$100M',
      },
      {
        name: 'Retainer Contracts',
        description: 'Ongoing defence contractor retainer for effector intelligence',
        icon: '💳',
        annualPotential: '$25M-$75M',
      },
    ],
    technologyPartners: [],
    apiProducts: [
      {
        name: 'Effector Status API',
        description: 'Real-time smart effector readiness, location, and status intelligence',
        endpoint: '/api/v1/kinetic-effector-status',
        pricePerCall: '$1.00',
      },
      {
        name: 'Firewall Audit API',
        description: 'PID ethical firewall check results and immutable audit records',
        endpoint: '/api/v1/kinetic-firewall-audit',
        pricePerCall: '$0.75',
      },
    ],
    companyStructure: {
      entity: 'Anchor AI Guard — Kinetic-SI Division',
      division: 'Defence Intelligence Division',
      headcount: 'Founding team (scaling)',
      hq: 'Australia',
      registrations: ['Sovereign Architecture', 'ITAR/EAR Framework', 'Australian-Built Platform'],
    },
    disclaimer:
      'Anchor Kinetic-SI provides AI intelligence software only. It does not manufacture, sell, or operate weapons systems.',
    ctaText: 'Sovereign Effector Intelligence',
    ctaSubtext: 'Defence ministries & OEMs • Sovereign deployment • ITAR-ready',
  },
  // ═══════════════════════════════════════════════════════════════════════════
  // GOD'S EYE — Sovereign Global Protection Intelligence
  // "Looking down to protect the world — not to control it"
  // ICC/ICJ War Crimes Evidence Chain • Civilian Casualty Monitoring
  // Surveillance State Accountability • Satellite-Verified Intelligence
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: 'godseye',
    slug: '/anchor-godseye',
    brandName: "Anchor God's Eye",
    tagline: 'Sovereign Global Protection Intelligence',
    heroSubtitle:
      "The eye that protects — not controls. When they destroy the cameras, God's Eye still sees. Satellite-verified, court-admissible evidence for international law. Countries have this power and use it wrong — Anchor uses it right.",
    icon: '👁️',
    colorFrom: '#7c3aed',
    colorTo: '#4f46e5',
    accentColor: 'violet',
    borderColor: 'border-violet-500/40',
    textColor: 'text-violet-400',
    bgGradient: 'from-violet-950/40 via-indigo-950/30 to-slate-950',
    domain: 'godseye.anchoraiguard.com',
    targetMarket: [
      'International Criminal Court (ICC)',
      'International Court of Justice (ICJ)',
      'UN Agencies (OHCHR, OCHA, UNICEF, UNIFIL)',
      'Human Rights Organisations (Amnesty, HRW, MSF, ICRC)',
      'War Crime Prosecutors & Legal Teams',
      'Investigative Journalists (Reuters, AP, BBC, Al Jazeera)',
      'Political Risk & Reinsurance Companies',
      'Allied Government Defence & Intelligence',
      'Academic Conflict Research Institutions',
    ],
    problemStatement:
      "Governments destroy CCTV cameras, bomb UN bases, and shut down the internet to hide war crimes. UNICEF civilian monitoring cameras in southern Lebanon were systematically destroyed so the world couldn't see. Civilian casualties in Gaza, Iran, Yemen, Sudan, and Ukraine mount with inadequate documentation. Surveillance technology sold to authoritarian regimes enables persecution. Evidence of atrocities is fragmented, unverified, and rarely reaches international courts in admissible form.",
    solutionStatement:
      "God's Eye provides sovereign, satellite-verified, court-admissible intelligence that cannot be bombed, censored, or deleted. It cross-references GDELT, ACLED, OHCHR, Airwars, Copernicus Sentinel-2 satellite imagery, and UNHCR displacement data to build timestamped evidence chains for ICC/ICJ prosecution. Every bombing, every civilian casualty, every destroyed camera, every internet shutdown — documented, corroborated, and preserved.",
    features: [
      {
        icon: '⚖️',
        title: 'ICC/ICJ War Crime Evidence Chain',
        description:
          'Court-admissible evidence with Rome Statute and Geneva Convention legal classifications. Timestamped, multi-source corroborated, evidence-graded (A=court-ready to D=unverified).',
        worldFirst: true,
      },
      {
        icon: '💣',
        title: 'Bombing & Airstrike Tracker',
        description:
          'Every reported bombing in active conflict zones documented — weapon type, target classification (hospital, school, residential), perpetrator, civilian casualties, legal status assessment.',
        worldFirst: true,
      },
      {
        icon: '📡',
        title: 'Satellite-Verified Intelligence',
        description:
          'Copernicus Sentinel-2 and emergency mapping data. Troop movements, mass graves, detention camps, infrastructure destruction — verified from 786km altitude. Cannot be bombed.',
        worldFirst: true,
      },
      {
        icon: '🏥',
        title: 'UN Infrastructure Attack Monitor',
        description:
          "Tracks attacks on UNICEF cameras, UNIFIL peacekeeping bases, UNRWA schools, WHO hospitals. When they destroy civilian cameras to hide atrocities, God's Eye documents it.",
        worldFirst: true,
      },
      {
        icon: '👤',
        title: 'Civilian Casualty Records',
        description:
          'Cross-referenced civilian and child casualty data from Airwars, OCHA, WHO, MSF, local health ministries. Every death documented for accountability.',
        worldFirst: true,
      },
      {
        icon: '🕵️',
        title: 'Surveillance State Accountability',
        description:
          'Tracks Pegasus, Predator, Sharp Eyes, SORM, and other commercial spyware deployments. Documents which governments purchased surveillance tech and which populations they targeted.',
        worldFirst: true,
      },
      {
        icon: '🌐',
        title: 'Internet Shutdown Detection',
        description:
          'Real-time detection of internet blackouts during protests and military operations. Correlates shutdowns with escalated violence patterns — evidence that states cut comms before atrocities.',
      },
      {
        icon: '🔗',
        title: 'Cross-Domain Correlation Engine',
        description:
          'Links surveillance tech purchases to human rights abuses, arms transfers to conflicts, bombing patterns to displacement routes. AI-powered pattern detection across all sources.',
      },
    ],
    dataSources: [
      {
        name: 'GDELT Project',
        description: 'Global real-time event monitoring — conflicts, protests, atrocities',
      },
      {
        name: 'ACLED',
        description: 'Armed Conflict Location & Event Data — political violence worldwide',
      },
      { name: 'UN OHCHR', description: 'Office of the High Commissioner for Human Rights reports' },
      {
        name: 'Copernicus Sentinel-2',
        description: 'EU satellite imagery — 786km altitude, cannot be destroyed',
      },
      {
        name: 'Airwars',
        description: 'Gold standard civilian casualty monitoring from airstrikes',
      },
      { name: 'UNHCR', description: 'Refugee and displacement tracking data' },
      {
        name: 'Citizen Lab',
        description: 'Surveillance technology and commercial spyware tracking',
      },
      { name: 'Access Now', description: 'Internet shutdown detection and documentation' },
      { name: 'ReliefWeb (OCHA)', description: 'UN humanitarian situation reports' },
      {
        name: 'Copernicus EMS',
        description: 'Emergency Management Service — conflict and disaster response',
      },
    ],
    pricing: [
      {
        name: 'Journalist',
        price: '$999',
        period: '/mo',
        features: [
          'Verified incident feeds (all conflict zones)',
          'Bombing & casualty record search',
          'Evidence export (PDF briefs)',
          'Email alerts (critical incidents)',
          '10 API queries/day',
        ],
        cta: 'Protect the Truth',
        tierId: 'STARTER',
      },
      {
        name: 'NGO / Prosecutor',
        price: '$9,990',
        period: '/mo',
        features: [
          'Full evidence chain access (A-D grade)',
          'ICC/ICJ legal classification engine',
          'Surveillance accountability scoreboard',
          'Satellite-verified intelligence',
          'Cross-domain correlation engine',
          'Bulk evidence export (court packages)',
          'API access (1,000 queries/day)',
          'Conflict zone heatmaps',
        ],
        cta: 'Build Your Case',
        highlighted: true,
        tierId: 'PRO',
      },
      {
        name: 'Institutional',
        price: '$29,990',
        period: '/mo',
        features: [
          'Everything in NGO tier',
          'Real-time satellite watch alerts',
          'Custom conflict zone monitoring',
          'AI-powered evidence compilation',
          'Dedicated evidence analyst support',
          'Unlimited API access',
          'White-label evidence reports',
          'Priority evidence grading (24hr)',
        ],
        cta: 'Sovereign Intelligence',
        tierId: 'BUSINESS',
      },
      {
        name: 'UN / Government',
        price: 'Contact',
        period: '',
        features: [
          'Everything in Institutional',
          'Custom satellite tasking integration',
          'Sovereign deployment (air-gapped option)',
          'Direct ICC/ICJ evidence pipeline',
          'Multi-agency collaboration workspace',
          'Classified evidence chain (5-Eyes compatible)',
          'Real-time SIGINT correlation (authorised only)',
          '24/7 sovereign operations centre',
        ],
        cta: 'Sovereign Deployment',
        tierId: 'GOVERNMENT',
      },
    ],
    useCases: [
      {
        title: 'ICC War Crime Prosecution',
        description:
          "Build court-admissible evidence packages for ICC prosecutors. God's Eye cross-references GDELT, ACLED, satellite imagery, and UN reports to create evidence chains graded A (court-ready) through D (unverified). Legal classifications map to Rome Statute articles and Geneva Convention breaches.",
      },
      {
        title: 'UN Camera Destruction Documentation',
        description:
          "When UNICEF civilian monitoring cameras in southern Lebanon are destroyed, God's Eye documents every attack on UN infrastructure — facility type, perpetrator, equipment destroyed, operational impact. Satellite verification provides evidence that cannot be bombed.",
      },
      {
        title: 'Civilian Casualty Accountability',
        description:
          'Cross-reference bombing records with civilian casualty data from Airwars, OCHA, WHO, and local health ministries. Every airstrike classified by target type (hospital, school, residential), weapon used, and legal status (proportionate/disproportionate/indiscriminate).',
      },
      {
        title: 'Surveillance Technology Accountability',
        description:
          'Track which governments purchased Pegasus, Predator, or Sharp Eyes surveillance tech and correlate with human rights abuses in those countries. Hold both buyer and vendor accountable. Evidence graded and timestamped for court use.',
      },
      {
        title: 'Conflict Zone Humanitarian Intelligence',
        description:
          'Real-time monitoring of active conflict zones — Lebanon, Gaza, Iran, Yemen, Sudan, Ukraine, Myanmar, Syria. Track displacement, casualties, infrastructure destruction, and aid access. Feed directly to UNHCR, ICRC, and MSF operations rooms.',
      },
    ],
    revenueStreams: [
      {
        name: 'SaaS Intelligence Subscriptions',
        description: 'Monthly subscriptions for journalists, NGOs, prosecutors, and institutions',
        icon: '💎',
        annualPotential: '$15M-$50M',
      },
      {
        name: 'ICC/ICJ Evidence Packages',
        description: 'Court-grade evidence compilation and expert analysis for active prosecutions',
        icon: '⚖️',
        annualPotential: '$10M-$30M',
      },
      {
        name: 'Government Intelligence Contracts',
        description:
          'Sovereign deployment for allied nations, UN peacekeeping, and defence agencies',
        icon: '🏛️',
        annualPotential: '$25M-$75M',
      },
      {
        name: 'API Intelligence Feeds',
        description:
          'Real-time conflict, casualty, and surveillance data feeds for media and research',
        icon: '📡',
        annualPotential: '$5M-$15M',
      },
      {
        name: 'Insurance & Political Risk Data',
        description: 'Conflict zone risk scoring for reinsurance, travel, and political risk firms',
        icon: '🛡️',
        annualPotential: '$8M-$20M',
      },
      {
        name: 'Academic Research Licensing',
        description: 'Conflict data licensing for universities, think tanks, and policy research',
        icon: '🎓',
        annualPotential: '$3M-$10M',
      },
    ],
    technologyPartners: [
      {
        name: 'Copernicus / ESA',
        description: 'EU satellite constellation',
        logo: '🛰️',
        integration:
          'Sentinel-2 satellite imagery for evidence verification — 786km altitude, 10m resolution. Cannot be destroyed by ground forces.',
      },
      {
        name: 'GDELT Project',
        description: 'Global event monitoring',
        logo: '🌐',
        integration:
          'Real-time monitoring of global news, conflict events, and atrocity reports across 100+ languages.',
      },
      {
        name: 'xAI (Grok)',
        description: 'Deep reasoning AI',
        logo: '🧠',
        integration:
          'Grok-powered evidence analysis — legal classification, pattern detection, cross-reference verification.',
      },
      {
        name: 'Starlink',
        description: 'Satellite internet',
        logo: '📡',
        integration:
          'Satellite connectivity for conflict zone data collection when terrestrial internet is shut down.',
      },
    ],
    apiProducts: [
      {
        name: 'War Crime Evidence API',
        description: 'Court-grade evidence records with legal classifications',
        endpoint: '/api/v1/godseye/war-crimes',
        pricePerCall: '$0.50',
      },
      {
        name: 'Civilian Casualty Tracker API',
        description: 'Cross-referenced civilian and child casualty records by conflict zone',
        endpoint: '/api/v1/godseye/civilian-casualties',
        pricePerCall: '$0.25',
      },
      {
        name: 'Bombing Record API',
        description: 'Airstrike and bombing data with weapon and target classifications',
        endpoint: '/api/v1/godseye/bombings',
        pricePerCall: '$0.25',
      },
      {
        name: 'Surveillance Accountability API',
        description: 'Commercial spyware deployments and internet shutdown tracking',
        endpoint: '/api/v1/godseye/surveillance',
        pricePerCall: '$0.15',
      },
      {
        name: 'Conflict Heatmap API',
        description: 'Global threat heatmap with country-level incident aggregation',
        endpoint: '/api/v1/godseye/heatmap',
        pricePerCall: '$0.10',
      },
    ],
    companyStructure: {
      entity: "Anchor AI Guard — God's Eye Division",
      division: 'Global Protection Intelligence Division',
      headcount: 'Founding team (scaling)',
      hq: 'Australia',
      registrations: [
        'Sovereign Architecture',
        'ICC Evidence Standards',
        'Geneva Convention Compliance',
        'Australian-Built Platform',
      ],
    },
    disclaimer:
      "Anchor God's Eye is an impartial intelligence platform. It documents all actors in all conflicts without political bias. Evidence is sourced from publicly available data, UN agencies, and satellite imagery. God's Eye does not conduct surveillance — it holds surveillance states accountable.",
    ctaText: 'Sovereign Protection Intelligence',
    ctaSubtext: 'ICC/ICJ prosecutors • UN agencies • Human rights NGOs • Investigative journalists',
  },
  // ═══════════════════════════════════════════════════════════════════════════
  // ANCHOR TRADE RISK — Sovereign Trade Intelligence
  // Tariffs • Supply-chain integrity • Trade-flow anomalies • Entity risk
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: 'traderisk',
    slug: '/anchor-traderisk',
    brandName: 'Anchor Trade Risk',
    tagline: 'Sovereign Trade Intelligence',
    heroSubtitle:
      'Real-time tariff tracking, sanctions screening, supply-chain risk mapping, and trade-flow anomaly detection. From customs compliance to geopolitical disruption — Anchor sees the risks before they hit your bottom line.',
    icon: '📊',
    colorFrom: 'from-amber-500',
    colorTo: 'to-slate-500',
    accentColor: 'amber',
    borderColor: 'border-amber-500',
    textColor: 'text-amber-400',
    bgGradient: 'from-amber-500/10 via-transparent to-slate-900/5',
    domain: 'traderisk.anchoraiguard.com',
    targetMarket: [
      'Import/Export Companies',
      'Trade Finance Banks',
      'Customs Brokers',
      'Supply Chain Managers',
      'Government Trade Agencies',
      'Logistics Providers',
    ],
    problemStatement:
      'Global trade faces 14,000+ active tariff measures, 6,300+ sanctioned entities, and growing supply-chain fragility from geopolitical tensions. Manual compliance checking is slow, expensive, and error-prone.',
    solutionStatement:
      'Anchor Trade Risk provides AI-powered tariff intelligence, real-time sanctions screening, supply-chain disruption prediction, and trade-flow anomaly detection — all sovereign, all automated.',
    features: [
      {
        icon: '📊',
        title: 'Tariff Intelligence Engine',
        description:
          'Real-time tracking of 14,000+ global tariff measures, duty rates, and trade agreements. HS code classification and rate comparison.',
      },
      {
        icon: '🚫',
        title: 'Sanctions Screening',
        description:
          'Entity screening against OFAC, EU, UK, UN, and Australian sanctions lists. Real-time updates, fuzzy matching, and vessel tracking.',
      },
      {
        icon: '🗺️',
        title: 'Supply Chain Risk Map',
        description:
          'Map critical supply-chain dependencies, single-source risks, and geopolitical disruption scenarios. Predict bottlenecks before they happen.',
      },
      {
        icon: '🔍',
        title: 'Trade Flow Anomaly Detection',
        description:
          'AI detects unusual trade patterns: mis-invoicing, transhipment fraud, sanctions circumvention, and customs duty avoidance.',
      },
    ],
    dataSources: [
      {
        name: 'WTO Tariff Database',
        description: 'Global tariff rate data from 164 member nations',
      },
      { name: 'OFAC / SDN List', description: 'US Treasury sanctions and blocked persons' },
      { name: 'UN Comtrade', description: 'International merchandise trade statistics' },
      {
        name: 'Australian Border Force',
        description: 'Customs duty rates, tariff concessions, FTAs',
      },
    ],
    useCases: [
      {
        title: 'Tariff Impact Analysis',
        description:
          'Model the financial impact of new tariffs and trade measures on your supply chain within hours.',
      },
      {
        title: 'Sanctions Compliance',
        description:
          'Screen customers, suppliers, and vessels against global sanctions lists in real time.',
      },
      {
        title: 'Supply Chain Resilience',
        description:
          'Identify single-source dependencies and develop alternative sourcing strategies.',
      },
      {
        title: 'Trade Finance Risk',
        description:
          'Detect trade-based money laundering, over/under-invoicing, and circular trading patterns.',
      },
    ],
    technologyPartners: [
      {
        name: 'WTO',
        description: 'Tariff and trade agreement data',
        logo: '🌐',
        integration: 'Data Feed',
      },
      {
        name: 'ITC Trade Map',
        description: 'Trade flow statistics and market analysis',
        logo: '📈',
        integration: 'Data Feed',
      },
    ],
    apiProducts: [
      {
        name: 'Tariff Intelligence API',
        description: 'HS code lookup, duty rate calculation, FTA eligibility across 164 countries',
        endpoint: '/api/v1/traderisk/tariffs',
        pricePerCall: '$0.05',
      },
      {
        name: 'Sanctions Screening API',
        description: 'Entity and vessel screening against all major sanctions lists',
        endpoint: '/api/v1/traderisk/sanctions-screen',
        pricePerCall: '$0.08',
      },
    ],
    companyStructure: {
      entity: 'Anchor AI Guard — Trade Risk Division',
      division: 'Trade Intelligence Division',
      headcount: 'Founding team (scaling)',
      hq: 'Australia',
      registrations: [
        'Sovereign Architecture',
        'AUSTRAC Reporting',
        'AEO Compliance Ready',
        'Australian-Built Platform',
      ],
    },
    disclaimer:
      'Anchor Trade Risk provides trade intelligence and screening tools. It does not provide customs brokerage, legal, or financial advice. All outputs should be validated by qualified trade compliance professionals.',
    ctaText: 'Sovereign Trade Intelligence',
    ctaSubtext:
      'Import/Export companies • Trade finance • Customs brokers • Government trade agencies',
  },

  // ─── ANCHOR AGRI (Precision Intelligence) ─────────────────────────
  {
    id: 'agri',
    slug: '/anchor-agri',
    brandName: 'Anchor Agri',
    tagline: 'Precision Agriculture Intelligence',
    heroSubtitle:
      "The world's first sovereign precision agriculture platform. Monitors every field, tracks every supply chain, and predicts every threat — from soil sensors to global food security.",
    icon: '🌿',
    logoUrl: resolveAnchorLogoByAsset(''),
    colorFrom: 'from-green-400',
    colorTo: 'to-emerald-600',
    accentColor: 'green',
    borderColor: 'border-green-400',
    textColor: 'text-green-400',
    bgGradient: 'from-green-400/10 via-transparent to-emerald-900/5',
    domain: 'agri.anchoraiguard.com',
    targetMarket: [
      'Precision Agriculture Companies',
      'AgriTech Startups',
      'Food Safety Regulators',
      'Supply Chain Operators',
      'Government Agriculture Departments',
      'Commodity Traders',
      'Farming Cooperatives',
    ],
    problemStatement:
      'Crop threats spread undetected. Supply chains are fragile. Food safety violations go unnoticed until outbreaks occur. Farms lack real-time intelligence to respond to weather, pests, and market shifts.',
    solutionStatement:
      'Anchor Agri delivers sovereign-grade precision agriculture intelligence — detecting crop threats before they spread, securing food supply chains end-to-end, and predicting market disruptions before they hit.',
    features: [
      {
        icon: '🛰️',
        title: 'Satellite Field Monitoring',
        description:
          'Real-time satellite imagery analysis for crop health, irrigation status, and pest detection across thousands of hectares.',
        worldFirst: true,
      },
      {
        icon: '🌡️',
        title: 'IoT Sensor Intelligence',
        description:
          'Soil moisture, temperature, pH, and nutrient sensors feeding AI models for precision irrigation and fertilisation recommendations.',
      },
      {
        icon: '🐛',
        title: 'Pest & Disease Early Warning',
        description:
          'Machine learning models combining weather, satellite, and trap data to predict pest outbreaks 72 hours ahead of infestation.',
        worldFirst: true,
      },
      {
        icon: '📦',
        title: 'Supply Chain Integrity',
        description:
          'End-to-end food supply chain tracking from paddock to plate with contamination detection, cold chain monitoring, and recall intelligence.',
      },
      {
        icon: '📊',
        title: 'Commodity Price Intelligence',
        description:
          'Real-time agricultural commodity pricing with AI-powered demand forecasting and weather impact modelling.',
      },
      {
        icon: '🔗',
        title: 'Cross-Sector Integration',
        description:
          'Connects with Climate (weather impact), Water (irrigation), Logistics (transport), and Finance (crop insurance) sectors.',
      },
    ],
    dataSources: [
      { name: 'USDA / ABARES', description: 'Government crop statistics & forecasts' },
      { name: 'FAO / UN', description: 'Global food & agriculture data' },
      { name: 'Sentinel-2', description: 'European satellite crop imagery' },
      { name: 'CBOT / ICE', description: 'Agricultural commodity exchanges' },
    ],
    pricing: [
      {
        name: 'Farm',
        price: '$1,990',
        period: '/mo',
        features: [
          '1 operation',
          'Field monitoring',
          'Pest alerts',
          'Basic supply chain',
          'Email support',
        ],
        cta: 'Request demo',
      },
      {
        name: 'Cooperative',
        price: '$7,990',
        period: '/mo',
        features: [
          '10 operations',
          'Everything in Farm',
          'Commodity intelligence',
          'Cross-sector integration',
          'API access',
          'Priority support',
        ],
        cta: 'Request demo',
        highlighted: true,
      },
      {
        name: 'Enterprise',
        price: '$29,990',
        period: '/mo',
        features: [
          'Unlimited operations',
          'Everything in Cooperative',
          'Custom AI models',
          'Government integration',
          'On-premise deployment',
          'Dedicated support',
        ],
        cta: 'Contact sales',
      },
    ],
    companyStructure: {
      entity: 'Anchor AI Guard — Agri Division',
      division: 'Precision Agriculture Division',
      headcount: 'Founding team (scaling)',
      hq: 'Australia',
      registrations: ['Sovereign Architecture', 'Australian-Built Platform'],
    },
    useCases: [
      {
        title: 'Precision Irrigation',
        description:
          'AI-optimised irrigation schedules based on soil moisture, weather forecasts, and crop growth stage.',
      },
      {
        title: 'Pest Outbreak Response',
        description:
          'Early warning and containment recommendations for pest and disease outbreaks across regions.',
      },
      {
        title: 'Supply Chain Traceability',
        description:
          'End-to-end tracking from farm to consumer with contamination detection and recall intelligence.',
      },
      {
        title: 'Commodity Trading',
        description:
          'Price forecasting and market timing intelligence for agricultural commodities.',
      },
    ],
    technologyPartners: [
      {
        name: 'Sentinel-2 ESA',
        description: 'European satellite imagery for crop monitoring',
        logo: '🛰️',
        integration: 'Satellite Data Feed',
      },
      {
        name: 'USDA NASS',
        description: 'US agricultural statistics and crop reports',
        logo: '🇺🇸',
        integration: 'Data API',
      },
      {
        name: 'FAO',
        description: 'UN food and agriculture organisation data',
        logo: '🌍',
        integration: 'Statistical Feed',
      },
    ],
    apiProducts: [
      {
        name: 'Field Monitoring API',
        description: 'Satellite-based crop health and anomaly detection',
        endpoint: '/api/agri/field-monitor',
        pricePerCall: '$0.05',
      },
      {
        name: 'Pest Prediction API',
        description: 'ML-powered pest and disease outbreak predictions',
        endpoint: '/api/agri/pest-predict',
        pricePerCall: '$0.08',
      },
      {
        name: 'Supply Chain API',
        description: 'End-to-end food supply chain tracking',
        endpoint: '/api/agri/supply-chain',
        pricePerCall: '$0.04',
      },
    ],
    disclaimer:
      'Anchor Agri provides agricultural intelligence tools. It does not provide agronomic advice or guarantee crop outcomes. All outputs should be validated by qualified agricultural professionals.',
    ctaText: 'Sovereign Agri Intelligence',
    ctaSubtext: 'Farms • Cooperatives • AgriTech • Food safety • Government agriculture agencies',
  },
  {
    id: 'water',
    slug: '/anchor-water',
    brandName: 'Anchor Water',
    tagline: 'Sovereign Water Infrastructure Intelligence',
    heroSubtitle:
      'Protect water treatment, distribution, quality monitoring, and flood-response systems with a business that signs up, demos, and operates as its own entity.',
    icon: '💧',
    logoUrl: resolveAnchorLogoByAsset(''),
    colorFrom: 'from-cyan-500',
    colorTo: 'to-blue-500',
    accentColor: 'cyan',
    borderColor: 'border-cyan-500',
    textColor: 'text-cyan-300',
    bgGradient: 'from-cyan-500/10 via-transparent to-blue-900/10',
    domain: 'water.anchoraiguard.com',
    targetMarket: [
      'Water utilities',
      'Treatment operators',
      'Flood response teams',
      'Critical infrastructure agencies',
      'Smart city operators',
    ],
    problemStatement:
      'Water networks carry both cyber and public-safety risk. Treatment, pumping, quality telemetry, and flood systems cannot rely on generic dashboards or shared access.',
    solutionStatement:
      'Anchor Water delivers sovereign monitoring, contamination intelligence, SCADA defence, and response workflows designed specifically for water operators.',
    features: [
      {
        icon: '🧪',
        title: 'Quality Intelligence',
        description:
          'Correlate sensor drift, contamination anomalies, and treatment variance before they turn into public incidents.',
      },
      {
        icon: '🛡️',
        title: 'Water SCADA Defence',
        description:
          'Detect malicious command sequences, unsafe dosing changes, and remote access abuse across water control systems.',
      },
      {
        icon: '🌊',
        title: 'Flood & Supply Resilience',
        description:
          'Model flood impact, supply continuity, and infrastructure stress with response-ready operational views.',
      },
    ],
    dataSources: [
      { name: 'Water telemetry', description: 'Quality, dosing, pressure, and flow sensors' },
      { name: 'Hydrology feeds', description: 'River, dam, rainfall, and flood indicators' },
      { name: 'SCADA events', description: 'Operational command and device alert streams' },
    ],
    pricing: [
      {
        name: 'Utility',
        price: '$3,990',
        period: '/mo',
        features: ['Water quality dashboards', 'Alerting', 'Secure access', 'Email support'],
        cta: 'Request demo',
      },
      {
        name: 'Regional Authority',
        price: '$12,990',
        period: '/mo',
        features: [
          'Multi-site intelligence',
          'SCADA defence',
          'Flood resilience views',
          'Priority support',
        ],
        cta: 'Request demo',
        highlighted: true,
      },
    ],
    testimonials: [],
    stats: [
      { value: '2,847', label: 'Treatment Assets' },
      { value: '99.7%', label: 'Online Telemetry' },
      { value: '24/7', label: 'Incident Monitoring' },
    ],
    useCases: [
      {
        title: 'Treatment plant security',
        description:
          'Monitor dosing, filtration, and control-system anomalies in a single sovereign command surface.',
      },
      {
        title: 'Regional supply defence',
        description: 'Track pressure, flow, and outage propagation across distributed networks.',
      },
      {
        title: 'Flood response coordination',
        description:
          'Give response teams live intelligence across catchments, assets, and population impact.',
      },
    ],
    revenueStreams: [
      {
        name: 'Utility subscriptions',
        description: 'Recurring sovereign intelligence subscriptions for operators',
        icon: '💧',
        annualPotential: '$9M+',
      },
    ],
    businessFunctions: [
      { name: 'Operations', description: 'Water ops monitoring and triage', icon: '⚙️' },
      { name: 'Security', description: 'SCADA and cyber incident defence', icon: '🛡️' },
      { name: 'Response', description: 'Flood and contamination response workflows', icon: '🚨' },
    ],
    technologyPartners: [
      {
        name: 'SCADA partners',
        description: 'Industrial telemetry and control integrations',
        logo: '🔧',
        integration: 'Operational monitoring',
      },
      {
        name: 'Hydrology feeds',
        description: 'Weather and basin intelligence',
        logo: '🌧️',
        integration: 'Flood forecasting',
      },
    ],
    apiProducts: [
      {
        name: 'Water Risk API',
        description: 'Telemetry anomaly scoring and contamination detection',
        endpoint: '/api/water/quality',
        pricePerCall: '$0.05',
      },
    ],
    companyStructure: {
      entity: 'Anchor Water Pty Ltd',
      division: 'Water Intelligence Division',
      headcount: 'Founding team (scaling)',
      hq: 'Australia',
      registrations: ['Sovereign Architecture', 'Australian-Built Platform'],
    },
    disclaimer:
      'Anchor Water supports water operators with intelligence and workflow tooling. Operational decisions must still be authorised by qualified personnel.',
    ctaText: 'Open Anchor Water',
    ctaSubtext: 'Water utilities • Treatment • Flood response • Critical infrastructure',
  },
  {
    id: 'biotech',
    slug: '/anchor-biotech',
    brandName: 'Anchor Biotech',
    tagline: 'Sovereign Biotechnology Security',
    heroSubtitle:
      'Protect biotech IP, genomics platforms, labs, and clinical programs with a dedicated business flow that does not bleed into other sectors.',
    icon: '🧬',
    logoUrl: resolveAnchorLogoByAsset(''),
    colorFrom: 'from-emerald-500',
    colorTo: 'to-lime-500',
    accentColor: 'emerald',
    borderColor: 'border-emerald-500',
    textColor: 'text-emerald-300',
    bgGradient: 'from-emerald-500/10 via-transparent to-lime-900/10',
    domain: 'biotech.anchoraiguard.com',
    targetMarket: [
      'Biotech startups',
      'Pharma research teams',
      'Clinical trial operators',
      'Genomics labs',
      'Biosecurity agencies',
    ],
    problemStatement:
      'Biotech organisations carry high-value IP, regulated data, and lab operational risk that generic enterprise tooling does not model well.',
    solutionStatement:
      'Anchor Biotech combines lab security, genomic data protection, clinical-trial integrity, and biotech-specific threat intelligence in one sovereign environment.',
    features: [
      {
        icon: '🔬',
        title: 'Lab Security Command',
        description:
          'Protect lab access, experiment data, instrument networks, and BSL workflows with biotech-specific controls.',
      },
      {
        icon: '💊',
        title: 'Clinical & Pharma Intelligence',
        description:
          'Track trial integrity, pharma IP exposure, and supply-chain risk across drug programs.',
      },
      {
        icon: '🧠',
        title: 'Genomics Data Defence',
        description:
          'Secure sensitive sequence data, model pipelines, and research collaboration surfaces.',
      },
    ],
    dataSources: [
      { name: 'LIMS feeds', description: 'Laboratory instruments and experiment workflows' },
      { name: 'Clinical systems', description: 'Trial operations and sponsor datasets' },
      { name: 'Threat intel', description: 'Biotech-targeting campaign and IP theft signals' },
    ],
    pricing: [
      {
        name: 'Lab',
        price: '$4,990',
        period: '/mo',
        features: ['Lab monitoring', 'Data defence', 'Secure auth', 'Email support'],
        cta: 'Request demo',
      },
      {
        name: 'Biotech Enterprise',
        price: '$15,990',
        period: '/mo',
        features: [
          'Clinical program coverage',
          'Biotech threat intel',
          'Priority support',
          'API access',
        ],
        cta: 'Request demo',
        highlighted: true,
      },
    ],
    testimonials: [],
    stats: [
      { value: '847M', label: 'Genome Records Modeled' },
      { value: '1,247', label: 'Clinical Programs' },
      { value: '24/7', label: 'Lab Monitoring' },
    ],
    useCases: [
      {
        title: 'Genomics platform protection',
        description: 'Defend sequence data, model outputs, and instrument networks.',
      },
      {
        title: 'Clinical trial integrity',
        description: 'Monitor data quality, tampering risk, and sponsor exposure.',
      },
      {
        title: 'Research IP defence',
        description: 'Protect assays, compounds, and biotech trade secrets from theft.',
      },
    ],
    revenueStreams: [
      {
        name: 'Biotech subscriptions',
        description: 'Recurring platform access for labs and biotech operators',
        icon: '🧬',
        annualPotential: '$12M+',
      },
    ],
    businessFunctions: [
      {
        name: 'Research Security',
        description: 'Protect labs and experimental systems',
        icon: '🔬',
      },
      { name: 'Clinical Integrity', description: 'Monitor trials and regulated data', icon: '💊' },
      {
        name: 'IP Defence',
        description: 'Safeguard biotech IP and collaboration flows',
        icon: '🛡️',
      },
    ],
    technologyPartners: [
      {
        name: 'LIMS integrations',
        description: 'Lab information management systems',
        logo: '🧪',
        integration: 'Experiment and sample visibility',
      },
      {
        name: 'Clinical partners',
        description: 'Trial operations and sponsor platforms',
        logo: '📋',
        integration: 'Integrity and compliance workflows',
      },
    ],
    apiProducts: [
      {
        name: 'Biotech Risk API',
        description: 'Program risk, lab anomaly, and data exposure scoring',
        endpoint: '/api/biotech/risks',
        pricePerCall: '$0.07',
      },
    ],
    companyStructure: {
      entity: 'Anchor Biotech Pty Ltd',
      division: 'Biotechnology Security Division',
      headcount: 'Founding team (scaling)',
      hq: 'Australia',
      registrations: ['Sovereign Architecture', 'Australian-Built Platform'],
    },
    disclaimer:
      'Anchor Biotech supports biotech operators with intelligence and cyber tooling. It does not replace regulated clinical, scientific, or biosafety decision-making.',
    ctaText: 'Open Anchor Biotech',
    ctaSubtext: 'Labs • Clinical trials • Genomics • Biosecurity • Research IP',
  },
  {
    id: 'infrastructure',
    slug: '/anchor-infrastructure',
    brandName: 'Anchor Infrastructure',
    tagline: 'Critical Infrastructure Protection',
    heroSubtitle:
      'Operate a dedicated infrastructure business identity for grids, transport, utilities, and smart systems without sharing auth or dashboard context with other sectors.',
    icon: '🏗️',
    logoUrl: resolveAnchorLogoByAsset(''),
    colorFrom: 'from-amber-500',
    colorTo: 'to-orange-500',
    accentColor: 'amber',
    borderColor: 'border-amber-500',
    textColor: 'text-amber-300',
    bgGradient: 'from-amber-500/10 via-transparent to-orange-900/10',
    domain: 'infrastructure.anchoraiguard.com',
    targetMarket: [
      'Infrastructure operators',
      'Transport authorities',
      'Utilities',
      'Smart city programs',
      'Government asset owners',
    ],
    problemStatement:
      'Critical infrastructure cannot tolerate shared access, weak routing, or delayed threat visibility across OT, transport, and public systems.',
    solutionStatement:
      'Anchor Infrastructure gives operators a dedicated business surface for OT telemetry, asset risk, resilience planning, and cyber-physical threat response.',
    features: [
      {
        icon: '⚡',
        title: 'Grid & Utility Monitoring',
        description:
          'Track anomalies, outages, and cyber-physical stress across energy and utility systems.',
      },
      {
        icon: '🚆',
        title: 'Transport Network Defence',
        description:
          'Protect rail, road, port, and city transport infrastructure with live operational intelligence.',
      },
      {
        icon: '🏙️',
        title: 'Smart Infrastructure Resilience',
        description:
          'Correlate sensors, control systems, and public-service dependencies across connected infrastructure.',
      },
    ],
    dataSources: [
      { name: 'OT telemetry', description: 'ICS, SCADA, and asset event streams' },
      {
        name: 'Infrastructure sensors',
        description: 'Condition, displacement, and load indicators',
      },
      { name: 'Threat intelligence', description: 'Critical infrastructure targeting signals' },
    ],
    pricing: [
      {
        name: 'Operator',
        price: '$5,990',
        period: '/mo',
        features: ['Asset monitoring', 'Operational views', 'Secure access', 'Email support'],
        cta: 'Request demo',
      },
      {
        name: 'National Infrastructure',
        price: '$19,990',
        period: '/mo',
        features: [
          'Multi-network resilience',
          'Cyber-physical threat intel',
          'Priority support',
          'API access',
        ],
        cta: 'Request demo',
        highlighted: true,
      },
    ],
    testimonials: [],
    stats: [
      { value: '847,000', label: 'Assets Modeled' },
      { value: '99.97%', label: 'Service Uptime Lens' },
      { value: '24/7', label: 'Operational Monitoring' },
    ],
    useCases: [
      {
        title: 'Critical asset protection',
        description: 'Secure bridges, utilities, transport nodes, and urban systems.',
      },
      {
        title: 'OT risk triage',
        description:
          'Monitor industrial and municipal control systems with sector-specific context.',
      },
      {
        title: 'Resilience planning',
        description: 'Model cascading failures and response dependencies across networks.',
      },
    ],
    revenueStreams: [
      {
        name: 'Infrastructure subscriptions',
        description: 'Recurring protection subscriptions for critical asset operators',
        icon: '🏗️',
        annualPotential: '$15M+',
      },
    ],
    businessFunctions: [
      {
        name: 'Operations',
        description: 'Monitor asset networks and service continuity',
        icon: '⚙️',
      },
      { name: 'Security', description: 'Detect cyber-physical risk and attack paths', icon: '🛡️' },
      { name: 'Resilience', description: 'Plan recovery and dependency response', icon: '📡' },
    ],
    technologyPartners: [
      {
        name: 'OT vendors',
        description: 'Industrial control and telemetry integrations',
        logo: '🔌',
        integration: 'Operational visibility',
      },
      {
        name: 'Infrastructure data feeds',
        description: 'Asset condition and incident telemetry',
        logo: '📡',
        integration: 'Resilience intelligence',
      },
    ],
    apiProducts: [
      {
        name: 'Infrastructure Risk API',
        description: 'Asset risk, outage, and dependency scoring',
        endpoint: '/api/infrastructure/risks',
        pricePerCall: '$0.08',
      },
    ],
    companyStructure: {
      entity: 'Anchor Infrastructure Pty Ltd',
      division: 'Critical Infrastructure Division',
      headcount: 'Founding team (scaling)',
      hq: 'Australia',
      registrations: ['Sovereign Architecture', 'Australian-Built Platform'],
    },
    disclaimer:
      'Anchor Infrastructure supports operators with monitoring and response intelligence. Safety-critical operational actions must still be authorised by qualified personnel.',
    ctaText: 'Open Anchor Infrastructure',
    ctaSubtext: 'Utilities • Transport • OT/ICS • Smart infrastructure • Government assets',
  },
];

// Helper to find a sector config by ID or slug
export const getSectorConfig = (idOrSlug: string): SectorMarketingConfig | undefined => {
  const normalized = idOrSlug.trim().replace(/^\/+/, '');
  const inferredSectorId = normalized
    .replace(/^ecosystem\/anchor\//, '')
    .replace(/^anchor-/, '')
    .replace(/^ecosystem\/sector\//, '')
    .replace(/^ecosystem\/product\//, '');
  const canonicalId = resolveBusinessIdentityId(inferredSectorId);
  if (canonicalId) {
    const canonicalConfig = SECTOR_MARKETING_DATA.find((sector) => sector.id === canonicalId);
    if (canonicalConfig) return canonicalConfig;
  }
  return SECTOR_MARKETING_DATA.find(
    (s) => s.id === idOrSlug || s.slug === idOrSlug || s.slug === `/${idOrSlug}`
  );
};
